import { getApiUrl } from "@/utils/api";
import { invalidateAuthMe } from "@/utils/chromeCache";
import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import TradingSummaryTile from "@/components/admin/TradingSummaryTile";
import { MasterPropertyEditDialog } from "@/components/admin/MasterPropertyEditDialog";
import VerifyIdSection from "@/components/property-admin/VerifyIdSection";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { 
  LogOut, 
  Plus, 
  Pencil, 
  Trash2, 
  Home, 
  Mail, 
  Loader2,
  Building2,
  MessageSquare,
  Upload,
  Link as LinkIcon,
  FileText,
  Save,
  Palette,
  BarChart3,
  TrendingUp,
  Users,
  Calendar,
  ArrowUpRight,
  ArrowDownRight,
  Search,
  ArrowUpDown,
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  X,
  GripVertical,
  Globe,
  UserCircle,
  Phone,
  ExternalLink,
  Unlink,
  Cloud,
  CloudOff,
  Star,
  Crown,
  Sparkles,
  Link2,
  Import,
  Check,
  AlertCircle,
  Eye,
  EyeOff,
  Key,
  Settings,
  CheckCircle2,
  Clock,
  Grid3x3,
  Plug,
  Rocket,
  Zap,
  Info,
  Package,
  HandCoins,
  Megaphone,
  Tag,
  User,
  FolderOpen,
  Gavel,
  FileCode2,
  History,
} from "lucide-react";
import AdminTradingOffers from "@/components/admin/AdminTradingOffers";
import SupplierApplicationsTab from "@/components/admin/SupplierApplicationsTab";
import DocumentsSection from "@/components/property-admin/DocumentsSection";
import { ImageUpload } from "@/components/ImageUpload";
import { ColorPicker } from "@/components/ColorPicker";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { ContentTab } from "@/components/admin/ContentTab";
import { SeoTab } from "@/components/admin/SeoTab";
import AdminIntegrationsTab from "@/components/admin/AdminIntegrationsTab";
import { RendyVideoStatusBadge } from "@/components/admin/RendyVideoStatusBadge";
import ErrorLogTab from "@/components/admin/ErrorLogTab";
import AssetsAdmin from "@/components/admin/AssetsAdmin";
import IconGeneratorAdmin from "@/components/admin/IconGeneratorAdmin";
import IntegrationsTab from "@/components/admin/IntegrationsTab";
import AdminAnalyticsTab from "@/components/admin/AdminAnalyticsTab";
import AdminAccountTab from "@/components/admin/AdminAccountTab";
import NavigationManager from "@/components/admin/NavigationManager";
import { SiteStylesAdminTab } from "@/components/admin/SiteStylesAdminTab";import { CustomStaticPages } from "@/components/admin/CustomStaticPages";
import GitHubExplorerTab from "@/components/admin/GitHubExplorerTab";
import EmailTemplatesTab from "@/components/admin/EmailTemplatesTab";
import CampaignsAdmin from "@/components/admin/CampaignsAdmin";
import CMSTab from "@/components/cms/CMSTab";
import ProductsAdmin from "@/components/admin/ProductsAdmin";
import SuppliersAdmin from "@/components/admin/SuppliersAdmin";
import ReportsAdmin from "@/components/admin/ReportsAdmin";
import UpdatesAdmin from "@/components/admin/UpdatesAdmin";
import ValuationsAdmin from "@/components/admin/ValuationsAdmin";
import PlanningAppealsAdmin from "@/components/admin/PlanningAppealsAdmin";
import PlanningApplicationsAdmin from "@/components/admin/PlanningApplicationsAdmin";
import CategoriesAdmin from "@/components/admin/CategoriesAdmin";
import ReplicationPanel from "@/components/admin/ReplicationPanel";
import { resolveImageUrl } from "@/utils/imageUrl";
import { useShowDbFieldNames } from "@/hooks/useShowDbFieldNames";
import { useSiteStyles } from "@/hooks/useSiteStyles";
import axios from "axios";

// Debounce hook for auto-save
function useDebounce(callback, delay) {
  const timeoutRef = useRef(null);
  
  const debouncedCallback = useCallback((...args) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      callback(...args);
    }, delay);
  }, [callback, delay]);
  
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);
  
  return debouncedCallback;
}


const API = getApiUrl();
const LOGO_URL = "https://customer-assets.emergentagent.com/job_realty-gallery/artifacts/zzc7uqc2_Screenshot_20260119_035617_Chrome.ico";

/**
 * Wrap a property-form field to respect the CMS Fields settings.
 * Module-level so it doesn't remount children on parent re-render.
 *  - Renders nothing when `visible === false` in the CMS Fields table.
 *  - Renders an info popover next to the label when `info` is non-empty.
 * Usage:
 *   <CmsFieldWrap fieldKey="price" label="Price" required settings={cmsFieldSettings}>{input}</CmsFieldWrap>
 */
function CmsFieldWrap_UNUSED_LEGACY({ fieldKey, label, required, children, className = "", settings, style }) {
  const meta = (settings && settings[fieldKey]) || { info: "", visible: true };
  if (meta.visible === false) return null;
  return (
    <div className={`space-y-2 ${className}`} style={style}>
      {label && (
        <div className="flex items-center gap-1.5">
          <Label>
            {label}
            {required ? <span className="text-red-600 ml-0.5">*</span> : null}
          </Label>
          {meta.info && (
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="inline-flex items-center justify-center h-4 w-4 rounded-full text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                  title="Field guidance — click to view"
                  data-testid={`cms-field-info-btn-${fieldKey}`}
                >
                  <Info className="h-3.5 w-3.5" />
                </button>
              </PopoverTrigger>
              <PopoverContent
                side="top"
                align="start"
                className="max-w-xs text-xs leading-relaxed"
                data-testid={`cms-field-info-content-${fieldKey}`}
              >
                {meta.info}
              </PopoverContent>
            </Popover>
          )}
        </div>
      )}
      {children}
    </div>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  // Shared Context also read by property edit dialogs / agent dashboard —
  // refetch() after any site-styles save so feature-flag gated CTAs
  // (Estimate Price, Planning Applications, etc.) update without a reload.
  const { refetch: refetchSharedSiteStyles } = useSiteStyles();
  const [user, setUser] = useState(location.state?.user || null);
  const [properties, setProperties] = useState([]);
  // Per-property offer summary — {property_id: {total, sent, accepted, ...}}.
  // Fetched once alongside the properties list. Powers the offer status pill
  // and the offer filter on the Properties tab.
  const [offerSummary, setOfferSummary] = useState({});
  // Properties tab filters + search.
  const [propertySearch, setPropertySearch] = useState("");
  const [propertyOfferFilter, setPropertyOfferFilter] = useState("all");
  const [propertyLocationFilter, setPropertyLocationFilter] = useState("");
  const [propertyAgentFilter, setPropertyAgentFilter] = useState("");
  const [enquiries, setEnquiries] = useState([]);
  const [pageContent, setPageContent] = useState({});
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(() => {
    try { return localStorage.getItem("admin.activeTab") || "properties"; } catch { return "properties"; }
  });
  const [adminSubTab, setAdminSubTab] = useState(() => {
    try {
      const saved = localStorage.getItem("admin.adminSubTab") || "seo";
      // "agents" (Account Management) moved under Trading — a stale saved
      // value would otherwise render a blank System tab forever.
      return saved === "agents" ? "seo" : saved;
    } catch { return "seo"; }
  });
  // Sub-tabs under Trading (offers / enquiries / agents / applications) and
  // Products (products / suppliers). Kept as separate state so we don't
  // clobber the primary activeTab when a user clicks between siblings.
  const [tradingSubTab, setTradingSubTab] = useState(() => {
    try { return localStorage.getItem("admin.tradingSubTab") || "offers"; } catch { return "offers"; }
  });
  const [productsSubTab, setProductsSubTab] = useState(() => {
    try { return localStorage.getItem("admin.productsSubTab") || "products"; } catch { return "products"; }
  });
  const [reportsSubTab, setReportsSubTab] = useState(() => {
    try {
      const v = localStorage.getItem("admin.reportsSubTab");
      // Legacy value "reports" migrates to "sales" (renamed 2026-02).
      return v === "reports" ? "sales" : (v || "sales");
    } catch { return "sales"; }
  });

  // Persist every admin nav choice so admins land back where they left off.
  useEffect(() => { try { localStorage.setItem("admin.activeTab", activeTab); } catch { /* ignore */ } }, [activeTab]);
  useEffect(() => { try { localStorage.setItem("admin.adminSubTab", adminSubTab); } catch { /* ignore */ } }, [adminSubTab]);
  useEffect(() => { try { localStorage.setItem("admin.tradingSubTab", tradingSubTab); } catch { /* ignore */ } }, [tradingSubTab]);
  useEffect(() => { try { localStorage.setItem("admin.productsSubTab", productsSubTab); } catch { /* ignore */ } }, [productsSubTab]);
  useEffect(() => { try { localStorage.setItem("admin.reportsSubTab", reportsSubTab); } catch { /* ignore */ } }, [reportsSubTab]);

  // Settings tab shares a few flags with Admin -> Integrations (e.g. the
  // Planning Applications switches) — re-pull site-styles on every visit so
  // a change made from Integrations shows up here without a page reload.
  useEffect(() => {
    if (activeTab !== "admin" || adminSubTab !== "settings") return;
    axios.get(`${API}/admin/site-styles`, { withCredentials: true })
      .then(({ data }) => { if (data.styles) setSiteStyles(prev => ({ ...prev, ...data.styles })); })
      .catch(() => {});
  }, [activeTab, adminSubTab]);
  
  // Agent Applications state
  const [applications, setApplications] = useState([]);
  const [applicationsLoading, setApplicationsLoading] = useState(false);
  const [applicationStats, setApplicationStats] = useState({ pending: 0, approved: 0, rejected: 0 });
  const [processingApplication, setProcessingApplication] = useState(null);
  const [editingApplication, setEditingApplication] = useState(null);
  const [showEditAppDialog, setShowEditAppDialog] = useState(false);
  const [editAppForm, setEditAppForm] = useState({ agency_name: "", contact_name: "", email: "", phone: "", website: "", message: "" });
  const [supplierAppPendingCount, setSupplierAppPendingCount] = useState(0);
  
  // Auto-save state for styles
  const [stylesAutoSaving, setStylesAutoSaving] = useState(false);
  const [stylesLastSaved, setStylesLastSaved] = useState(null);
  const [stylesHasChanges, setStylesHasChanges] = useState(false);
  const stylesInitialLoad = useRef(true);
  
  // Auto-save state for agents
  const [agentAutoSaving, setAgentAutoSaving] = useState(false);
  const [agentLastSaved, setAgentLastSaved] = useState(null);
  const [agentHasChanges, setAgentHasChanges] = useState(false);
  const agentInitialLoad = useRef(true);
  
  // Analytics state
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  
  // Site Styles state
  const [siteStyles, setSiteStyles] = useState({
    cardBackground: "#e8f0e8",
    cardBorder: "#c5d9c5",
    primaryHex: "#2f4f4f",
    backgroundHex: "#f5f2ed",
    accentHex: "#e07b5d",
    textHex: "#1a1a1a",
    mutedTextHex: "#666666",
    borderHex: "#e5e0d8",
    headerBgHex: "#ffffff",
    footerBgHex: "#2f4f4f",
    buttonBgHex: "#2f4f4f",
    buttonTextHex: "#ffffff",
    heroFontHex: "#ffffff",
    heroImageOpacity: 100,
    heroTextOverlayOpacity: 0,
    heroTextOverlayColor: "#000000",
    // Hero Text Settings
    heroTitle: "Find Your Dream Home",
    heroSubtitle: "Welcome to SellLocalHomes",
    heroDescription: "Discover exceptional properties in prime locations. Your perfect home is waiting.",
    heroTitleSize: 48,
    heroSubtitleSize: 14,
    heroDescriptionSize: 18,
    heroTextAlign: "left",
    heroHeight: 80, // Hero height in vh (viewport height percentage)
    // Navigation settings
    navBgHex: "#ffffff",
    navTextHex: "#1a1a1a",
    navHoverHex: "#2f4f4f",
    navFontSize: 14,
    navLogoFontSize: 20,
    navButtonBgHex: "#2f4f4f",
    navButtonTextHex: "#ffffff",
    navLogoText: "SellLocalHomes",
    navLinks: [
      { name: "Home", path: "/", enabled: true },
      { name: "Properties", path: "/properties", enabled: true },
      { name: "Join Us", path: "/join-us", enabled: true },
      { name: "Contact", path: "/contact", enabled: true }
    ],
    navCtaText: "Request Viewing",
    navCtaEnabled: true
  });
  const [stylesLoading, setStylesLoading] = useState(false);
  
  // Setup event listeners on mount
  useEffect(() => {
    console.log('AdminDashboard: Setting up event listener for switch-to-page-content-tab');
    
    // Listen for event to switch to Page Content tab
    const handleSwitchToPageContent = (event) => {
      console.log('AdminDashboard received switch-to-page-content-tab event', event.detail);
      setActiveTab("admin");
      setAdminSubTab("page-content");
    };
    
    window.addEventListener('switch-to-page-content-tab', handleSwitchToPageContent);
    
    return () => {
      window.removeEventListener('switch-to-page-content-tab', handleSwitchToPageContent);
    };
  }, [setActiveTab, setAdminSubTab]);

  // Read `?tab=admin&sub=github` from the URL (e.g. GitHub OAuth callback bounce)
  // and switch to the requested sub-tab. Leaves query params intact so the
  // GitHubExplorerTab can still show its own toast for gh_connected/gh_error.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get("tab");
    const sub = params.get("sub");
    if (t) setActiveTab(t);
    if (sub) setAdminSubTab(sub === "agents" ? "seo" : sub);
  }, [setActiveTab, setAdminSubTab]);
  
  // Enquiries state
  const [enquirySearch, setEnquirySearch] = useState("");
  const [enquirySort, setEnquirySort] = useState({ field: "timestamp", direction: "desc" });
  const [deletingEnquiry, setDeletingEnquiry] = useState(null);
  
  // Agents state
  const [agents, setAgents] = useState([]);
  const [agentsLoading, setAgentsLoading] = useState(false);
  const [showAgentDialog, setShowAgentDialog] = useState(false);
  const [editingAgent, setEditingAgent] = useState(null);
  const [agentFormLoading, setAgentFormLoading] = useState(false);
  const [expandedAgentWorkers, setExpandedAgentWorkers] = useState(new Set());

  // Valuation (tenant) accounts — created from Capture Property Form submissions
  const [tenants, setTenants] = useState([]);
  const [tenantsLoading, setTenantsLoading] = useState(false);
  const [tenantSearch, setTenantSearch] = useState("");
  const [tenantDocsDialog, setTenantDocsDialog] = useState(null);
  const [deletingAgent, setDeletingAgent] = useState(null);
  const [showAgentPropertiesDialog, setShowAgentPropertiesDialog] = useState(null);
  // Agent selected for the Documents deep-link modal — master admin uses
  // this to inspect / manage any agent's document library in-place.
  const [documentsAgent, setDocumentsAgent] = useState(null);
  const [agentForm, setAgentForm] = useState({
    name: "",
    logo: "",
    contact_name: "",
    email: "",
    phone: "",
    website: "",
    address: "",
    commission: "",
    notes: "",
    email_notifications: true,
    marketing_emails: true,
    supplier_emails: true,
    // Live status fields
    is_live: true,
    live_from: new Date().toISOString().split('T')[0],
    live_to: (() => { const d = new Date(); d.setMonth(d.getMonth() + 12); return d.toISOString().split('T')[0]; })(),
    // Assets fields
    cloud_storage_url: "",
    cloud_storage_credentials: "",
    // Account disabled -> blocks login entirely (shown a "contact us" message)
    active: true
  });
  
  // Property form state
  const [showPropertyDialog, setShowPropertyDialog] = useState(false);
  const [editingProperty, setEditingProperty] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [imageInputMode, setImageInputMode] = useState("upload");
  const [emailingCredentials, setEmailingCredentials] = useState(null);
  // Rightmove per-property action modal state.
  // Shape: null | { property, action: 'send'|'remove', loading, result }
  const [rightmoveAction, setRightmoveAction] = useState(null);
  // Postcode → lat/lng auto-geocode status for the Address 1 input.
  const [geocodeStatus, setGeocodeStatus] = useState(null);

  // Debug: `?show=yes` in the URL prints each DB field name next to its label
  // across the property edit form (both CMS and Rightmove sections).
  const showDbNames = useShowDbFieldNames();

  // DOM effect: whenever the property dialog is open with `?show=yes`, walk
  // every named form control inside it and inject a small DB-name badge into
  // the associated <label>. Idempotent (marks labels with dataset flag).
  useEffect(() => {
    if (!showDbNames) return;
    if (!showPropertyDialog) return;
    const applyBadges = () => {
      const modal = document.querySelector('[data-testid="property-dialog"]')
        || document.querySelector('[role="dialog"]');
      if (!modal) return;
      const inputs = modal.querySelectorAll(
        "input[name], textarea[name], select[name]"
      );
      inputs.forEach((el) => {
        const nm = el.getAttribute("name");
        if (!nm) return;
        // Find the closest preceding <label> in the same field wrapper.
        let label = null;
        let node = el.parentElement;
        for (let hop = 0; hop < 3 && node && !label; hop += 1) {
          label = node.querySelector("label");
          node = node.parentElement;
        }
        if (!label || label.dataset.dbBadgeShown) return;
        const badge = document.createElement("span");
        badge.className =
          "ml-2 inline-block text-[10px] font-mono text-blue-700 bg-blue-50 border border-blue-200 rounded px-1 align-middle";
        badge.textContent = nm;
        badge.setAttribute("data-testid", `db-field-badge-${nm}`);
        label.appendChild(badge);
        label.dataset.dbBadgeShown = "1";
      });
    };
    // Run once now and again after a tick — React portals / Radix dialogs
    // sometimes mount their content asynchronously.
    applyBadges();
    const t = setTimeout(applyBadges, 250);
    return () => clearTimeout(t);
  }, [showDbNames, showPropertyDialog, editingProperty]);

  const handleAddressGeocode = async (e) => {
    // Called from either onBlur (native input event → e.target.value) or
    // programmatically (no event → read current propertyForm.cms_House_Name_Number).
    const raw = ((e?.target?.value ?? propertyForm.cms_House_Name_Number) || "").trim();
    if (!raw) return;
    // Try UK postcode format first (postcodes.io — free, no key, UK-only).
    const cleaned = raw.toUpperCase().replace(/\s+/g, "");
    const ukPostcodeRx = /^[A-Z]{1,2}\d[A-Z\d]?\d[A-Z]{2}$/i;
    if (!ukPostcodeRx.test(cleaned)) {
      return; // Not a UK postcode — leave as-is (US-style / free-text address).
    }
    // Skip if we already have coordinates.
    if (propertyForm.latitude && propertyForm.longitude) return;

    try {
      setGeocodeStatus({ type: "info", message: "Looking up postcode…" });
      const resp = await fetch(
        `https://api.postcodes.io/postcodes/${encodeURIComponent(cleaned)}`
      );
      const data = await resp.json();
      if (data.status !== 200 || !data.result) {
        setGeocodeStatus({ type: "error", message: "Postcode not found." });
        return;
      }
      const r = data.result;
      setPropertyForm((prev) => ({
        ...prev,
        latitude: prev.latitude || r.latitude,
        longitude: prev.longitude || r.longitude,
        display_address:
          prev.display_address ||
          [r.admin_ward, r.admin_district, r.region].filter(Boolean).join(", "),
      }));
      if (editingProperty) setPropertyHasChanges(true);
      setGeocodeStatus({
        type: "success",
        message: `Auto-filled coordinates and location from ${r.postcode}.`,
      });
    } catch (err) {
      setGeocodeStatus({ type: "error", message: `Lookup failed: ${err.message}` });
    }
  };

  // Force-refresh lat/lng from the postcode field. Fires on focus of the
  // postcode input so admins can re-run the lookup at any time (unlike
  // handleAddressGeocode, this does NOT skip when coords are already set).
  const handlePostcodeGeocode = async () => {
    const raw = (propertyForm.postcode || "").trim();
    if (!raw) return;
    const cleaned = raw.toUpperCase().replace(/\s+/g, "");
    const ukPostcodeRx = /^[A-Z]{1,2}\d[A-Z\d]?\d[A-Z]{2}$/i;
    if (!ukPostcodeRx.test(cleaned)) {
      setGeocodeStatus({ type: "error", message: "Not a valid UK postcode." });
      return;
    }
    try {
      setGeocodeStatus({ type: "info", message: "Recalculating coordinates…" });
      const resp = await fetch(
        `https://api.postcodes.io/postcodes/${encodeURIComponent(cleaned)}`
      );
      const data = await resp.json();
      if (data.status !== 200 || !data.result) {
        setGeocodeStatus({ type: "error", message: "Postcode not found." });
        return;
      }
      const r = data.result;
      setPropertyForm((prev) => ({
        ...prev,
        latitude: r.latitude,
        longitude: r.longitude,
      }));
      if (editingProperty) setPropertyHasChanges(true);
      setGeocodeStatus({
        type: "success",
        message: `Recalculated lat/lng from ${r.postcode}.`,
      });
    } catch (err) {
      setGeocodeStatus({ type: "error", message: `Lookup failed: ${err.message}` });
    }
  };
  
  // Property auto-save state
  const [propertyAutoSaving, setPropertyAutoSaving] = useState(false);
  const [propertyLastSaved, setPropertyLastSaved] = useState(null);
  const [propertyHasChanges, setPropertyHasChanges] = useState(false);
  const [aiRewriting, setAiRewriting] = useState(false);
  const propertyInitialLoad = useRef(true);
  
  // Scrape feature state
  const [scrapeUrl, setScrapeUrl] = useState("");
  const [scraping, setScraping] = useState(false);
  const [scrapeResult, setScrapeResult] = useState(null);
  // Auto-apply preference is remembered across sessions in localStorage so
  // power users don't have to tick it every time.
  const [scrapeAutoApply, setScrapeAutoApply] = useState(() => {
    try {
      return localStorage.getItem("slh:scrape:auto-apply") === "1";
    } catch (_) {
      return false;
    }
  });
  const [generatingSeo, setGeneratingSeo] = useState(false);
  
  const [propertyForm, setPropertyForm] = useState({
    title: "",
    price: "",
    price_label: "Default",
    currency: "£",
    display_address: "",
    cms_House_Name_Number: "",
    street_name: "",
    latitude: "",
    longitude: "",
    beds: "",
    baths: "",
    description: "",
    main_image: "",
    gallery: [],
    video_url: "",
    video_type: "youtube",
    show_video_thumbnail: true,
    featured: false,
    show_header: true,
    show_footer: true,
    show_back_link: true,
    size: "",
    property_type: "",
    tenure: "",
    floorplan_image: "",
    floorplan_size: "",
    features: "",
    nearest_stations: "",
    council_tax_band: "",
    parking_spaces: "",
    garden_facing: "",
    date_added: "",
    // Live status fields
    is_live: true,
    status: "For Sale",
    live_from: new Date().toISOString().split('T')[0],
    live_to: (() => { const d = new Date(); d.setMonth(d.getMonth() + 12); return d.toISOString().split('T')[0]; })(),
    // SEO fields
    seo_title: "",
    seo_description: "",
    seo_slug: "",
    seo_keywords: "",
    seo_focus_keyword: "",
    seo_robots: "index, follow",
    // Agent fields
    agent_id: "",
    agent_name: "",
    agent_logo: "",
    agent_contact_name: "",
    agent_tel: "",
    agent_whatsapp_tel: "",
    agent_whatsapp_enabled: true,
    agent_email: "",
    agent_website: "",
    agent_email_notifications: true,
    agent_commission: "",
    agent_notes: "",
    admin_username: "",
    admin_password: "",
    // Vendor (property owner) contact details
    vendor_name: "",
    vendor_email: "",
    vendor_phone: "",
    // Per-property Rightmove field overrides (see PropertyRightmoveSection)
    cms_overrides: {}
  });

  // Content editing state
  const [editingContent, setEditingContent] = useState(null);
  const [contentSaving, setContentSaving] = useState(false);

  useEffect(() => {
    fetchData();
    if (!user) {
      fetchUser();
    }
  }, []);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
    } catch (error) {
      console.error("Failed to fetch user:", error);
      navigate("/admin/login", { replace: true });
    }
  };

  const fetchData = async () => {
    try {
      const [propertiesRes, enquiriesRes, contentRes, usersRes, stylesRes, analyticsRes, agentsRes, applicationsRes, appStatsRes, offerSummaryRes] = await Promise.all([
        axios.get(`${API}/admin/properties`, { withCredentials: true }),
        axios.get(`${API}/admin/enquiries`, { withCredentials: true }),
        axios.get(`${API}/admin/content`, { withCredentials: true }),
        axios.get(`${API}/admin/users`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/site-styles`, { withCredentials: true }).catch(() => ({ data: { styles: {} } })),
        axios.get(`${API}/admin/analytics`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${API}/admin/agents`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/agent-applications`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/agent-applications/stats`, { withCredentials: true }).catch(() => ({ data: { pending: 0, approved: 0, rejected: 0 } })),
        axios.get(`${API}/admin/properties/offer-summary`, { withCredentials: true }).catch(() => ({ data: { summary: {} } })),
      ]);
      setProperties(propertiesRes.data);
      setEnquiries(enquiriesRes.data);
      setPageContent(contentRes.data.pages || {});
      setUsers(usersRes.data || []);
      if (stylesRes.data.styles) {
        setSiteStyles(prev => ({ ...prev, ...stylesRes.data.styles }));
      }
      if (analyticsRes.data) {
        setAnalytics(analyticsRes.data);
      }
      setAgents(agentsRes.data || []);
      setApplications(applicationsRes.data || []);
      setApplicationStats(appStatsRes.data || { pending: 0, approved: 0, rejected: 0 });
      setOfferSummary(offerSummaryRes.data?.summary || {});
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
    fetchTenants();
  };

  // Valuation (tenant) accounts
  const fetchTenants = async () => {
    setTenantsLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/tenants`, { withCredentials: true });
      setTenants(data.tenants || []);
    } catch (error) {
      console.error("Failed to fetch valuation accounts:", error);
    } finally {
      setTenantsLoading(false);
    }
  };

  const tenantMatchesSearch = (t, search) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    if ((t.name || "").toLowerCase().includes(q)) return true;
    if ((t.email || "").toLowerCase().includes(q)) return true;
    if ((t.postcodes || []).some((pc) => pc.toLowerCase().includes(q))) return true;
    return false;
  };

  // Agent Applications functions
  const fetchApplications = async () => {
    setApplicationsLoading(true);
    try {
      const [appsResponse, statsResponse] = await Promise.all([
        axios.get(`${API}/admin/agent-applications`, { withCredentials: true }),
        axios.get(`${API}/admin/agent-applications/stats`, { withCredentials: true })
      ]);
      setApplications(appsResponse.data);
      setApplicationStats(statsResponse.data);
    } catch (error) {
      console.error("Failed to fetch applications:", error);
      toast.error("Failed to load applications");
    } finally {
      setApplicationsLoading(false);
    }
  };

  const handleApproveApplication = async (applicationId) => {
    setProcessingApplication(applicationId);
    try {
      await axios.put(`${API}/admin/agent-applications/${applicationId}/approve`, {}, { withCredentials: true });
      toast.success("Application approved! Agent account created.");
      fetchApplications();
      fetchAgents();
    } catch (error) {
      console.error("Failed to approve application:", error);
      toast.error(error.response?.data?.detail || "Failed to approve application");
    } finally {
      setProcessingApplication(null);
    }
  };

  const handleRejectApplication = async (applicationId) => {
    const reason = window.prompt("Enter rejection reason (optional):");
    setProcessingApplication(applicationId);
    try {
      await axios.put(`${API}/admin/agent-applications/${applicationId}/reject?reason=${encodeURIComponent(reason || '')}`, {}, { withCredentials: true });
      toast.success("Application rejected");
      fetchApplications();
    } catch (error) {
      console.error("Failed to reject application:", error);
      toast.error(error.response?.data?.detail || "Failed to reject application");
    } finally {
      setProcessingApplication(null);
    }
  };

  const handleEditApplication = (app) => {
    setEditingApplication(app);
    setEditAppForm({
      agency_name: app.agency_name || "",
      contact_name: app.contact_name || "",
      email: app.email || "",
      phone: app.phone || "",
      website: app.website || "",
      message: app.message || ""
    });
    setShowEditAppDialog(true);
  };

  const handleSaveApplication = async () => {
    if (!editingApplication) return;
    try {
      await axios.put(`${API}/admin/agent-applications/${editingApplication.application_id}`, editAppForm, { withCredentials: true });
      toast.success("Application updated");
      setShowEditAppDialog(false);
      setEditingApplication(null);
      fetchApplications();
    } catch (error) {
      console.error("Failed to update application:", error);
      toast.error(error.response?.data?.detail || "Failed to update");
    }
  };

  const handleDeleteApplication = async (applicationId) => {
    if (!window.confirm("Delete this application permanently?")) return;
    try {
      await axios.delete(`${API}/admin/agent-applications/${applicationId}`, { withCredentials: true });
      toast.success("Application deleted");
      fetchApplications();
    } catch (error) {
      console.error("Failed to delete application:", error);
      toast.error(error.response?.data?.detail || "Failed to delete");
    }
  };

  // Agent CRUD functions
  const fetchAgents = async () => {
    setAgentsLoading(true);
    try {
      const response = await axios.get(`${API}/admin/agents`, { withCredentials: true });
      setAgents(response.data);
    } catch (error) {
      console.error("Failed to fetch agents:", error);
      toast.error("Failed to load agents");
    } finally {
      setAgentsLoading(false);
    }
  };

  const handleOpenAgentDialog = (agent = null) => {
    agentInitialLoad.current = true;  // Reset initial load flag
    setAgentHasChanges(false);
    setAgentLastSaved(null);
    
    if (agent) {
      setEditingAgent(agent);
      setAgentForm({
        name: agent.name || "",
        logo: agent.logo || "",
        contact_name: agent.contact_name || "",
        email: agent.email || "",
        phone: agent.phone || "",
        website: agent.website || "",
        address: agent.address || "",
        commission: agent.commission || "",
        notes: agent.notes || "",
        email_notifications: agent.email_notifications !== false,
        marketing_emails: agent.marketing_emails !== false,
        supplier_emails: agent.supplier_emails !== false,
        is_agent: agent.is_agent !== false,
        is_master_agent: agent.is_master_agent === true,
        parent_agent_id: agent.parent_agent_id || null,
        position: agent.position || "",
        // Live status fields
        is_live: agent.is_live !== false,
        live_from: agent.live_from || new Date().toISOString().split('T')[0],
        live_to: agent.live_to || (() => { const d = new Date(); d.setMonth(d.getMonth() + 12); return d.toISOString().split('T')[0]; })(),
        // Assets fields
        cloud_storage_url: agent.cloud_storage_url || "",
        cloud_storage_credentials: agent.cloud_storage_credentials || "",
        active: agent.active !== false
      });
    } else {
      setEditingAgent(null);
      setAgentForm({
        name: "",
        logo: "",
        contact_name: "",
        email: "",
        phone: "",
        website: "",
        address: "",
        commission: "",
        notes: "",
        email_notifications: true,
        marketing_emails: true,
        supplier_emails: true,
        is_agent: true,
        is_master_agent: false,
        parent_agent_id: null,
        position: "",
        // Live status fields
        is_live: true,
        live_from: new Date().toISOString().split('T')[0],
        live_to: (() => { const d = new Date(); d.setMonth(d.getMonth() + 12); return d.toISOString().split('T')[0]; })(),
        // Assets fields
        cloud_storage_url: "",
        cloud_storage_credentials: "",
        active: true
      });
    }
    setShowAgentDialog(true);
  };

  const handleAgentLogoUpload = (url) => {
    setAgentForm(prev => ({ ...prev, logo: url }));
    if (editingAgent) setAgentHasChanges(true);
  };

  const handleSaveAgent = async () => {
    if (!agentForm.name) {
      toast.error("Agent name is required");
      return;
    }

    setAgentFormLoading(true);
    try {
      const dataToSend = { ...agentForm };
      
      if (editingAgent) {
        await axios.put(`${API}/admin/agents/${editingAgent.agent_id}`, dataToSend, { withCredentials: true });
        toast.success("Agent updated successfully");
      } else {
        await axios.post(`${API}/admin/agents`, dataToSend, { withCredentials: true });
        toast.success("Agent created successfully");
      }
      setShowAgentDialog(false);
      fetchAgents();
      fetchData();
    } catch (error) {
      console.error("Failed to save agent:", error);
      toast.error("Failed to save agent");
    } finally {
      setAgentFormLoading(false);
    }
  };

  const handleDeleteAgent = async (agentId) => {
    try {
      await axios.delete(`${API}/admin/agents/${agentId}`, { withCredentials: true });
      toast.success("Agent deleted successfully");
      setDeletingAgent(null);
      fetchAgents();
      fetchData();
    } catch (error) {
      console.error("Failed to delete agent:", error);
      toast.error("Failed to delete agent");
    }
  };

  // Auto-save function for agents
  const performAgentAutoSave = useCallback(async (form, agentId) => {
    if (!form || !form.name || agentAutoSaving || !agentId) return;
    
    setAgentAutoSaving(true);
    try {
      const response = await axios.put(`${API}/admin/agents/${agentId}`, form, { withCredentials: true });
      setAgentLastSaved(new Date());
      setAgentHasChanges(false);
      toast.success("Saved", { duration: 1500 });
      
      // Refresh agents list in background to update linked properties
      fetchAgents();
      
      // Update editingAgent with fresh data including linked_properties
      if (response.data) {
        // Fetch the updated agent with linked properties
        const agentsRes = await axios.get(`${API}/admin/agents`, { withCredentials: true });
        const updatedAgent = agentsRes.data?.find(a => a.agent_id === agentId);
        if (updatedAgent) {
          setEditingAgent(prev => ({ ...prev, linked_properties: updatedAgent.linked_properties }));
        }
      }
    } catch (error) {
      console.error("Agent auto-save error:", error);
      toast.error(error.response?.data?.detail || "Failed to auto-save");
    } finally {
      setAgentAutoSaving(false);
    }
  }, [agentAutoSaving]);

  // Debounced auto-save for agents (1.5 second delay)
  const debouncedAgentSave = useDebounce(performAgentAutoSave, 1500);

  // Trigger agent auto-save when agentForm changes (only when editing)
  useEffect(() => {
    if (agentInitialLoad.current) {
      agentInitialLoad.current = false;
      return;
    }
    if (editingAgent && agentHasChanges && agentForm.name) {
      debouncedAgentSave(agentForm, editingAgent.agent_id);
    }
  }, [agentForm, editingAgent, agentHasChanges, debouncedAgentSave]);

  // Handle agent form field changes with auto-save trigger
  const handleAgentFormChange = (field, value) => {
    setAgentForm(prev => ({ ...prev, [field]: value }));
    if (editingAgent) setAgentHasChanges(true);
  };

  const handleUnlinkAgentFromProperty = async (agentId, propertyId) => {
    try {
      await axios.post(`${API}/admin/agents/${agentId}/unlink-property/${propertyId}`, {}, { withCredentials: true });
      toast.success("Property unlinked from agent");
      fetchAgents();
      fetchData();
    } catch (error) {
      console.error("Failed to unlink property:", error);
      toast.error("Failed to unlink property");
    }
  };

  // Workers — self-registered (SSO) agent accounts reassigned under a
  // parent agent. Edits save immediately (small, infrequent list).
  const [savingWorkerId, setSavingWorkerId] = useState(null);
  const handleWorkerUpdate = async (workerId, patch) => {
    setSavingWorkerId(workerId);
    try {
      await axios.put(`${API}/admin/agents/${workerId}`, patch, { withCredentials: true });
      setAgents((prev) => prev.map((a) => (a.agent_id === workerId ? { ...a, ...patch } : a)));
      toast.success("Worker updated");
    } catch (error) {
      console.error("Failed to update worker:", error);
      toast.error("Failed to update worker");
    } finally {
      setSavingWorkerId(null);
    }
  };

  // SSO connections — admin can disconnect Google/Facebook from the agent's
  // record (e.g. after it was compromised, or they're moving to password
  // login). Backend refuses to strip the agent's ONLY sign-in method.
  const [disconnectingSso, setDisconnectingSso] = useState(null); // "google" | "facebook" | null
  const handleDisconnectAgentSso = async (agentId, provider) => {
    setDisconnectingSso(provider);
    const clearedKeys = provider === "google"
      ? ["google_user_id", "google_email", "google_name", "google_picture", "google_linked_at"]
      : ["facebook_id", "facebook_name", "facebook_picture", "facebook_linked_at"];
    try {
      const res = await axios.post(
        `${API}/admin/agents/${agentId}/disconnect-sso`,
        { provider },
        { withCredentials: true }
      );
      // $unset drops the keys server-side, but axios/JSON responses never
      // carry an "absent" signal — a shallow {...prev, ...res.data} merge
      // would leave the old provider fields from `prev` untouched. Clear
      // them explicitly before merging in the fresh response.
      const strip = (obj) => {
        const next = { ...obj };
        clearedKeys.forEach((k) => delete next[k]);
        return { ...next, ...res.data };
      };
      setEditingAgent((prev) => (prev ? strip(prev) : prev));
      setAgents((prev) => prev.map((a) => (a.agent_id === agentId ? strip(a) : a)));
      toast.success(`${provider === "google" ? "Google" : "Facebook"} disconnected`);
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to disconnect ${provider}`);
    } finally {
      setDisconnectingSso(null);
    }
  };

  const toggleAgentWorkersPanel = (agentId) => {
    setExpandedAgentWorkers((prev) => {
      const next = new Set(prev);
      if (next.has(agentId)) next.delete(agentId);
      else next.add(agentId);
      return next;
    });
  };

  // Removing a worker returns them to the Master Agent rather than orphaning them
  const handleRemoveWorker = (workerId) => {
    const master = agents.find((a) => a.is_master_agent);
    handleWorkerUpdate(workerId, { parent_agent_id: master ? master.agent_id : null });
  };

  // Auto-save function for styles
  const performStylesAutoSave = useCallback(async (styles) => {
    if (!styles) return;
    
    setStylesAutoSaving(true);
    try {
      await axios.put(`${API}/admin/site-styles`, { styles }, { withCredentials: true });
      setStylesLastSaved(new Date());
      setStylesHasChanges(false);
      // Revalidate the shared Context so property edit dialogs / agent
      // dashboard (which read flags via useSiteStyles, not this page's
      // local state) pick up the change immediately — no page reload.
      refetchSharedSiteStyles();
      console.log("Styles saved successfully");
    } catch (error) {
      console.error("Auto-save styles error:", error);
      toast.error("Failed to save styles");
    } finally {
      setStylesAutoSaving(false);
    }
  }, [refetchSharedSiteStyles]);

  // Auto-save when siteStyles changes (with debounce)
  const stylesTimeoutRef = useRef(null);
  
  useEffect(() => {
    // Skip initial load
    if (stylesInitialLoad.current) {
      stylesInitialLoad.current = false;
      return;
    }
    
    // Clear any existing timeout
    if (stylesTimeoutRef.current) {
      clearTimeout(stylesTimeoutRef.current);
    }
    
    // Set new timeout for auto-save
    if (siteStyles && stylesHasChanges) {
      stylesTimeoutRef.current = setTimeout(() => {
        performStylesAutoSave(siteStyles);
      }, 1500);
    }
    
    // Cleanup on unmount
    return () => {
      if (stylesTimeoutRef.current) {
        clearTimeout(stylesTimeoutRef.current);
      }
    };
  }, [siteStyles, stylesHasChanges, performStylesAutoSave]);

  // Update style with auto-save flag
  const updateStyle = (key, value) => {
    setSiteStyles(prev => ({ ...prev, [key]: value }));
    setStylesHasChanges(true);
  };

  const handleSaveStyles = async () => {
    await performStylesAutoSave(siteStyles);
    toast.success("Styles saved!");
  };

  // Delete enquiry
  const handleDeleteEnquiry = async (enquiryId) => {
    try {
      await axios.delete(`${API}/admin/enquiries/${enquiryId}`, { withCredentials: true });
      setEnquiries(prev => prev.filter(e => e.enquiry_id !== enquiryId));
      toast.success("Enquiry deleted successfully");
      setDeletingEnquiry(null);
    } catch (error) {
      console.error("Failed to delete enquiry:", error);
      toast.error("Failed to delete enquiry");
    }
  };

  // Filter and sort enquiries
  const getFilteredAndSortedEnquiries = () => {
    let filtered = [...enquiries];
    
    // Search filter
    if (enquirySearch.trim()) {
      const search = enquirySearch.toLowerCase();
      filtered = filtered.filter(e => 
        e.name?.toLowerCase().includes(search) ||
        e.email?.toLowerCase().includes(search) ||
        e.phone?.toLowerCase().includes(search) ||
        e.property_title?.toLowerCase().includes(search)
      );
    }
    
    // Sort
    filtered.sort((a, b) => {
      let aVal, bVal;
      
      switch (enquirySort.field) {
        case "name":
          aVal = a.name?.toLowerCase() || "";
          bVal = b.name?.toLowerCase() || "";
          break;
        case "email":
          aVal = a.email?.toLowerCase() || "";
          bVal = b.email?.toLowerCase() || "";
          break;
        case "property_title":
          aVal = a.property_title?.toLowerCase() || "";
          bVal = b.property_title?.toLowerCase() || "";
          break;
        case "timestamp":
        default:
          aVal = new Date(a.timestamp);
          bVal = new Date(b.timestamp);
          break;
      }
      
      if (enquirySort.direction === "asc") {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });
    
    return filtered;
  };

  const toggleEnquirySort = (field) => {
    setEnquirySort(prev => ({
      field,
      direction: prev.field === field && prev.direction === "desc" ? "asc" : "desc"
    }));
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      toast.success("Logged out successfully");
      navigate("/", { replace: true });
    } catch (error) {
      console.error("Logout error:", error);
      navigate("/", { replace: true });
    } finally {
      // Lets any public page with Edit Mode on (e.g. open in another tab,
      // or this one navigating back to "/") deactivate it immediately
      // instead of waiting on a route-change-triggered session recheck.
      // Must drop the cached /auth/me result FIRST — otherwise the route
      // change to "/" re-fetches auth state via the stale (still "master")
      // cached value before this event's listener even runs, flipping
      // "Viewing as Master Admin" back on right after logout.
      invalidateAuthMe();
      window.dispatchEvent(new CustomEvent("slh:admin-signed-out"));
    }
  };

  const openCreateDialog = () => {
    setEditingProperty(null);
    setPropertyForm({
      title: "",
      price: "",
      price_label: "Default",
      currency: "£",
      location: "",
      address: "",
      latitude: "",
      longitude: "",
      beds: "",
      baths: "",
      description: "",
      main_image: "",
      gallery: [],
      video_url: "",
      video_type: "youtube",
      show_video_thumbnail: true,
      featured: false,
      show_header: true,
      show_footer: true,
      show_back_link: true,
      size: "",
      property_type: "",
      tenure: "",
      floorplan_image: "",
      floorplan_size: "",
      features: "",
      nearest_stations: "",
      council_tax_band: "",
      parking_spaces: "",
      garden_facing: "",
      date_added: new Date().toISOString().split('T')[0],
      // Live status fields
      is_live: true,
      status: "For Sale",
      live_from: new Date().toISOString().split('T')[0],
      live_to: (() => { const d = new Date(); d.setMonth(d.getMonth() + 12); return d.toISOString().split('T')[0]; })(),
      seo_title: "",
      seo_description: "",
      seo_slug: "",
      seo_keywords: "",
      seo_focus_keyword: "",
      seo_robots: "index, follow",
      agent_id: "",
      agent_name: "",
      agent_logo: "",
      agent_contact_name: "",
      agent_tel: "",
      agent_email: "",
      agent_website: "",
      agent_email_notifications: true,
      agent_commission: "",
      agent_notes: "",
      admin_username: "",
      admin_password: "",
      vendor_name: "",
      vendor_email: "",
      vendor_phone: ""
    });
    setImageInputMode("upload");
    setShowPropertyDialog(true);
  };

  const openEditDialog = (property) => {
    setEditingProperty(property);
    setPropertyForm({
      title: property.title,
      price: property.price?.toString() || "",
      price_label: property.price_label || "Default",
      currency: property.currency || "£",
      display_address: property.display_address,
      cms_House_Name_Number: property.cms_House_Name_Number || "",
      street_name: property.street_name || "",
      postcode: property.postcode || "",
      transaction_status: property.transaction_status || "",
      latitude: property.latitude?.toString() || "",
      longitude: property.longitude?.toString() || "",
      beds: property.beds.toString(),
      baths: property.baths.toString(),
      description: property.description,
      main_image: property.main_image,
      gallery: property.gallery || [],
      video_url: property.video_url || "",
      video_type: property.video_type || "youtube",
      show_video_thumbnail: property.show_video_thumbnail !== false,
      featured: property.featured,
      show_header: property.show_header !== false,
      show_footer: property.show_footer !== false,
      show_back_link: property.show_back_link !== false,
      size: property.size || "",
      property_type: property.property_type || "",
      tenure: property.tenure || "",
      floorplan_image: property.floorplan_image || "",
      floorplan_size: property.floorplan_size || "",
      features: property.features || "",
      nearest_stations: property.nearest_stations || "",
      council_tax_band: property.council_tax_band || "",
      parking_spaces: property.parking_spaces?.toString() || "",
      garden_facing: property.garden_facing || "",
      date_added: property.date_added || "",
      // Live status fields
      is_live: property.is_live !== false,
      status: property.status || (property.is_live !== false ? "For Sale" : "Off"),
      live_from: property.live_from || new Date().toISOString().split('T')[0],
      live_to: property.live_to || (() => { const d = new Date(); d.setMonth(d.getMonth() + 12); return d.toISOString().split('T')[0]; })(),
      seo_title: property.seo_title || "",
      seo_description: property.seo_description || "",
      seo_slug: property.seo_slug || "",
      seo_keywords: property.seo_keywords || "",
      seo_focus_keyword: property.seo_focus_keyword || "",
      seo_robots: property.seo_robots || "index, follow",
      agent_id: property.agent_id || "",
      agent_name: property.agent_name || "",
      agent_logo: property.agent_logo || "",
      agent_contact_name: property.agent_contact_name || "",
      agent_tel: property.agent_tel || "",
      agent_whatsapp_tel: property.agent_whatsapp_tel || "",
      agent_whatsapp_enabled: property.agent_whatsapp_enabled !== false,
      agent_email: property.agent_email || "",
      agent_website: property.agent_website || "",
      agent_email_notifications: property.agent_email_notifications !== false,
      agent_commission: property.agent_commission || "",
      agent_notes: property.agent_notes || "",
      admin_username: property.admin_username || "",
      admin_password: "",  // Never pre-fill password
      vendor_name: property.vendor_name || "",
      vendor_email: property.vendor_email || "",
      vendor_phone: property.vendor_phone || "",
      cms_overrides: property.cms_overrides || {}
    });
    setImageInputMode("url");
    setShowPropertyDialog(true);
  };

  // Handle selecting an existing agent for a property
  const handleSelectAgent = (agentId) => {
    if (!agentId) {
      // Clear agent fields
      setPropertyForm(prev => ({
        ...prev,
        agent_id: "",
        agent_name: "",
        agent_logo: "",
        agent_contact_name: "",
        agent_tel: "",
        agent_whatsapp_tel: "",
        agent_whatsapp_enabled: true,
        agent_email: "",
        agent_website: "",
        agent_email_notifications: true
      }));
      if (editingProperty) setPropertyHasChanges(true);
      return;
    }

    const agent = agents.find(a => a.agent_id === agentId);
    if (agent) {
      setPropertyForm(prev => ({
        ...prev,
        agent_id: agent.agent_id,
        agent_name: agent.name,
        agent_logo: agent.logo || "",
        agent_contact_name: agent.contact_name || "",
        agent_tel: agent.phone || "",
        agent_whatsapp_tel: agent.whatsapp_phone || agent.phone || "",
        agent_whatsapp_enabled: agent.whatsapp_enabled !== false,
        agent_email: agent.email || "",
        agent_website: agent.website || "",
        agent_email_notifications: agent.email_notifications !== false
      }));
      if (editingProperty) setPropertyHasChanges(true);
    }
  };

  const handleGalleryUpload = (urls) => {
    setPropertyForm(prev => {
      const existing = prev.gallery || [];
      const startIdx = existing.length;
      const isVideo = (u) => /\.(mp4|webm|mov|m4v)(\?|#|$)/i.test(u || "");
      const additions = urls.map((u, i) => ({
        media_url: u,
        media_type: isVideo(u) ? 5 : 1,
        caption: "",
        sort_order: startIdx + i,
      }));
      return { ...prev, gallery: [...existing, ...additions] };
    });
    if (editingProperty) setPropertyHasChanges(true);
  };

  const handleRemoveGalleryImage = (index) => {
    setPropertyForm(prev => ({
      ...prev,
      gallery: prev.gallery.filter((_, i) => i !== index)
    }));
    if (editingProperty) setPropertyHasChanges(true);
  };

  // Move gallery image up
  const handleMoveImageUp = (index) => {
    if (index === 0) return;
    setPropertyForm(prev => {
      const newGallery = [...prev.gallery];
      [newGallery[index - 1], newGallery[index]] = [newGallery[index], newGallery[index - 1]];
      return { ...prev, gallery: newGallery };
    });
    if (editingProperty) setPropertyHasChanges(true);
  };

  // Move gallery image down
  const handleMoveImageDown = (index) => {
    setPropertyForm(prev => {
      if (index >= prev.gallery.length - 1) return prev;
      const newGallery = [...prev.gallery];
      [newGallery[index], newGallery[index + 1]] = [newGallery[index + 1], newGallery[index]];
      return { ...prev, gallery: newGallery };
    });
    if (editingProperty) setPropertyHasChanges(true);
  };

  // Auto-save function for property
  const performPropertyAutoSave = useCallback(async (form, propertyId) => {
    if (!form || !propertyId || propertyAutoSaving) return;
    
    // Validate required fields
    if (!form.title || !form.price || !form.display_address) return;
    
    setPropertyAutoSaving(true);
    try {
      // Gallery/main_image are intentionally NOT included in autosave — they
      // have their own dedicated persistence path (Rendy add/remove-from-gallery
      // endpoints + the manual "Save" button) so a debounced autosave can never
      // overwrite an out-of-band gallery change with a stale local array.
      const data = {
        title: form.title,
        price: parseInt(form.price) || 0,
        price_label: form.price_label || "Default",
        currency: form.currency || "£",
        display_address: form.display_address,
        cms_House_Name_Number: form.cms_House_Name_Number || null,
        street_name: form.street_name || null,
        postcode: form.postcode || null,
        transaction_status: form.transaction_status || null,
        latitude: form.latitude ? parseFloat(form.latitude) : null,
        longitude: form.longitude ? parseFloat(form.longitude) : null,
        beds: parseInt(form.beds) || 0,
        baths: parseInt(form.baths) || 0,
        description: form.description,
        video_url: form.video_url || null,
        video_type: form.video_type || "youtube",
        show_video_thumbnail: form.show_video_thumbnail,
        featured: form.featured,
        show_header: form.show_header,
        show_footer: form.show_footer,
        show_back_link: form.show_back_link,
        size: form.size || null,
        property_type: form.property_type || null,
        tenure: form.tenure || null,
        floorplan_image: form.floorplan_image || null,
        floorplan_size: form.floorplan_size || null,
        features: form.features || null,
        nearest_stations: form.nearest_stations || null,
        council_tax_band: form.council_tax_band || null,
        parking_spaces: form.parking_spaces ? parseInt(form.parking_spaces) : null,
        garden_facing: form.garden_facing || null,
        date_added: form.date_added || null,
        // Live status fields
        is_live: form.is_live,
        status: form.status || null,
        live_from: form.live_from || null,
        live_to: form.live_to || null,
        // SEO fields
        seo_title: form.seo_title || null,
        seo_description: form.seo_description || null,
        seo_slug: form.seo_slug || null,
        seo_keywords: form.seo_keywords || null,
        seo_focus_keyword: form.seo_focus_keyword || null,
        seo_robots: form.seo_robots || "index, follow",
        // Agent fields
        agent_id: form.agent_id || null,
        agent_name: form.agent_name || null,
        agent_logo: form.agent_logo || null,
        agent_contact_name: form.agent_contact_name || null,
        agent_tel: form.agent_tel || null,
        agent_whatsapp_tel: form.agent_whatsapp_tel || null,
        agent_whatsapp_enabled: form.agent_whatsapp_enabled !== false,
        agent_email: form.agent_email || null,
        agent_website: form.agent_website || null,
        agent_email_notifications: form.agent_email_notifications,
        agent_commission: form.agent_commission || null,
        agent_notes: form.agent_notes || null,
        // Vendor (property owner) contact details
        vendor_name: form.vendor_name || null,
        vendor_email: form.vendor_email || null,
        vendor_phone: form.vendor_phone || null,
        // Per-property Rightmove overrides (see PropertyRightmoveSection)
        cms_overrides: form.cms_overrides || {}
      };

      await axios.put(`${API}/admin/properties/${propertyId}`, data, { withCredentials: true });
      setPropertyLastSaved(new Date());
      setPropertyHasChanges(false);
      // Refresh property list in background
      fetchData();
    } catch (error) {
      console.error("Property auto-save error:", error);
      toast.error("Failed to auto-save property");
    } finally {
      setPropertyAutoSaving(false);
    }
  }, [propertyAutoSaving]);

  // Debounced auto-save for property (1.5 second delay)
  const debouncedPropertySave = useDebounce(performPropertyAutoSave, 1500);

  // Trigger auto-save when propertyForm changes (only when editing existing property)
  useEffect(() => {
    if (propertyInitialLoad.current) {
      propertyInitialLoad.current = false;
      return;
    }
    if (editingProperty && propertyHasChanges) {
      debouncedPropertySave(propertyForm, editingProperty.property_id);
    }
  }, [propertyForm, editingProperty, propertyHasChanges, debouncedPropertySave]);

  // Handle form field changes with auto-save trigger
  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setPropertyForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (editingProperty) setPropertyHasChanges(true);
  };

  // AI Rewrite Description handler
  const handleAIRewrite = async () => {
    if (!propertyForm.description) {
      toast.error("Please enter a description first");
      return;
    }
    
    setAiRewriting(true);
    try {
      const response = await axios.post(
        `${API}/property-admin/ai-rewrite-description`,
        {
          description: propertyForm.description,
          mode: "description",
          property_context: {
            title: propertyForm.title,
            display_address: propertyForm.display_address,
            price: propertyForm.price,
            bedrooms: propertyForm.beds,
            bathrooms: propertyForm.baths,
            sqft: propertyForm.size
          }
        },
        { withCredentials: true }
      );
      
      if (response.data.rewritten_description) {
        setPropertyForm(prev => ({
          ...prev,
          description: response.data.rewritten_description
        }));
        toast.success("Description rewritten by AI!");
        if (editingProperty) setPropertyHasChanges(true);
      }
    } catch (error) {
      console.error("AI rewrite error:", error);
      toast.error(error.response?.data?.detail || "Failed to rewrite description");
    } finally {
      setAiRewriting(false);
    }
  };

  // Ask the AI to condense the full description into a single-paragraph
  // Rightmove Summary (max ~500 chars). Result is written to the per-property
  // `cms_overrides.Summary` slot so it flows through on the next push.
  const [aiSummarising, setAiSummarising] = useState(false);
  const handleAISummary = async () => {
    if (!propertyForm.description) {
      toast.error("Add a description first so the AI has something to summarise");
      return;
    }
    setAiSummarising(true);
    try {
      const response = await axios.post(
        `${API}/property-admin/ai-rewrite-description`,
        {
          description: propertyForm.description,
          mode: "summary",
          property_context: {
            title: propertyForm.title,
            display_address: propertyForm.display_address,
            price: propertyForm.price,
            bedrooms: propertyForm.beds,
            bathrooms: propertyForm.baths,
          }
        },
        { withCredentials: true }
      );
      const summary = response.data?.rewritten_description;
      if (summary) {
        setPropertyForm(prev => ({
          ...prev,
          cms_overrides: {
            ...(prev.cms_overrides || {}),
            Summary: summary,
          },
        }));
        toast.success("Summary generated by AI!");
        if (editingProperty) setPropertyHasChanges(true);
      }
    } catch (error) {
      console.error("AI summary error:", error);
      toast.error(error.response?.data?.detail || "Failed to generate summary");
    } finally {
      setAiSummarising(false);
    }
  };

  // Scrape property data from URL
  const handleScrape = async () => {
    if (!scrapeUrl) {
      toast.error("Please enter a URL to scrape");
      return;
    }
    
    setScraping(true);
    setScrapeResult(null);
    try {
      const response = await axios.post(`${API}/property-admin/scrape`, {
        url: scrapeUrl
      }, { withCredentials: true });
      const data = response.data.data;
      if (scrapeAutoApply) {
        // Skip the intermediate preview UI when the admin has ticked
        // "Apply immediately" — pass the freshly scraped data directly so we
        // don't race React's state update for `scrapeResult`.
        applyScrapedData(data);
      } else {
        setScrapeResult(data);
        toast.success("Property data scraped successfully");
      }
    } catch (error) {
      console.error("Failed to scrape:", error);
      toast.error(error.response?.data?.detail || "Failed to scrape property data");
    } finally {
      setScraping(false);
    }
  };

  const applyScrapedData = (dataOverride) => {
    const data = dataOverride || scrapeResult;
    if (!data) return;

    const updates = { ...propertyForm };
    if (data.title) updates.title = data.title;
    if (data.price) updates.price = String(data.price);
    if (data.display_address) updates.display_address = data.display_address;
    if (data.beds) updates.beds = parseInt(data.beds);
    if (data.baths) updates.baths = parseInt(data.baths);
    if (data.description) updates.description = data.description;
    if (data.features) updates.features = data.features;
    if (data.size) updates.size = data.size;
    if (data.property_type) updates.property_type = data.property_type;

    // Additional fields
    if (data.council_tax_band) updates.council_tax_band = data.council_tax_band;
    if (data.parking_spaces) updates.parking_spaces = parseInt(data.parking_spaces);
    if (data.garden_facing) updates.garden_facing = data.garden_facing;
    if (data.floorplan_image) updates.floorplan_image = data.floorplan_image;

    // Import all images to gallery (first image becomes featured automatically).
    // Convert scraped URL strings into GalleryItem objects (media_type=1 photograph, blank caption).
    if (data.images && data.images.length > 0) {
      updates.gallery = data.images.map((u, i) => ({
        media_url: u,
        media_type: /\.(mp4|webm|mov|m4v)(\?|#|$)/i.test(u) ? 5 : 1,
        caption: "",
        sort_order: i,
      }));
    }

    setPropertyForm(updates);
    if (editingProperty) setPropertyHasChanges(true);

    const imageCount = data.images?.length || 0;
    const imageMsg = imageCount > 0 ? ` (${imageCount} images imported)` : '';
    const floorplanMsg = data.floorplan_image ? ', floorplan' : '';
    toast.success(`Scraped data applied${imageMsg}${floorplanMsg}`);
    // Nudge users to the Rightmove panel — description, features, beds and
    // baths now live inside `Rightmove → Description / Rooms & Size` instead
    // of the primary CMS section.
    if (data.description || data.features || data.beds || data.baths) {
      toast.info(
        "Description, Features, Bedrooms & Bathrooms have been placed under the Rightmove panel below.",
        { duration: 6000 }
      );
    }
    setScrapeResult(null);
    setScrapeUrl("");

    // Auto-scroll to the first Rightmove group that just received data so
    // the admin can see the imported values without hunting for them.
    // Rightmove group DOM ids follow `rightmove-group-<name>` slug format.
    const targetGroup = data.description || data.features
      ? "description"
      : (data.beds || data.baths)
      ? "rooms-&-size"
      : (data.cms_House_Name_Number || data.postcode)
      ? "address"
      : null;
    if (targetGroup) {
      // Wait one frame so the `<details>` `open` attribute has re-applied.
      setTimeout(() => {
        const el = document.querySelector(
          `[data-testid="rightmove-group-${targetGroup}"]`
        );
        el?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 300);
    }
  };

  const handleGenerateSeoKeywords = async () => {
    setGeneratingSeo(true);
    try {
      const response = await axios.post(`${API}/property-admin/ai-generate-seo`, {
        title: propertyForm.title,
        cms_House_Name_Number: propertyForm.cms_House_Name_Number,
        display_address: propertyForm.display_address,
        features: propertyForm.features,
        description: propertyForm.description,
        property_type: propertyForm.property_type,
        beds: parseInt(propertyForm.beds) || null,
        baths: parseInt(propertyForm.baths) || null
      }, { withCredentials: true });

      const d = response.data;
      if (d) {
        setPropertyForm(prev => ({
          ...prev,
          seo_keywords: d.keywords || prev.seo_keywords,
          seo_focus_keyword: d.focus_keyword || prev.seo_focus_keyword,
          seo_title: d.seo_title || prev.seo_title,
          seo_description: d.seo_description || prev.seo_description
        }));
        if (editingProperty) setPropertyHasChanges(true);
        toast.success("SEO content generated!");
      }
    } catch (error) {
      console.error("SEO generation error:", error);
      toast.error(error.response?.data?.detail || "Failed to generate SEO content");
    } finally {
      setGeneratingSeo(false);
    }
  };

  const handleDeleteProperty = async (propertyId) => {
    const pid = propertyId || editingProperty?.property_id;
    if (!pid) return;
    const title = editingProperty?.title || "this property";
    if (!window.confirm(`Delete "${title}"? This will also remove all enquiries and analytics.`)) return;
    try {
      await axios.delete(`${API}/admin/properties/${pid}`, { withCredentials: true });
      toast.success("Property deleted");
      setShowPropertyDialog(false);
      setEditingProperty(null);
      fetchData();
      setActiveTab("properties");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete property");
    }
  };

  // Push this property's full document (all fields) to the production
  // environment via the public `/api/public/properties/push` receiver.
  // Requires the backend `PRODUCTION_URL` env var (or default) to point at
  // the live deployment. Upserts on the target side by property_id.
  const [pushingToProd, setPushingToProd] = useState({});
  const handlePushToProd = async (property) => {
    const pid = property?.property_id;
    if (!pid) return;
    if (!window.confirm(
      `Push "${property.title || pid}" to LIVE production?\n\n` +
      `All fields (content, gallery, price, status, etc.) will overwrite the ` +
      `same property on production. This cannot be undone.`
    )) return;
    try {
      setPushingToProd((prev) => ({ ...prev, [pid]: true }));
      const r = await axios.post(
        `${API}/admin/properties/${pid}/push-to-prod`,
        {},
        { withCredentials: true, timeout: 90000 }
      );
      // Backend returns HTTP 200 for both success and soft errors (see
      // rationale in server.py — Cloudflare rewrites 5xx bodies), so we
      // must inspect the JSON status field ourselves.
      if (r.data?.status === "success") {
        const action = r.data?.action || "pushed";
        const target = (r.data?.target || "").replace(/\/$/, "");
        const liveUrl = target ? `${target}/property/${pid}` : null;
        toast.success(`Property ${action} on production`, {
          duration: 10000,
          action: liveUrl
            ? {
                label: "View on Live",
                onClick: () => window.open(liveUrl, "_blank", "noopener,noreferrer"),
              }
            : undefined,
        });
      } else {
        toast.error(r.data?.message || "Failed to push to production");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || e.response?.data?.message || "Failed to push to production");
    } finally {
      setPushingToProd((prev) => {
        const next = { ...prev };
        delete next[pid];
        return next;
      });
    }
  };

  // Rich error state surfaced when a Zoopla or Rightmove push fails.
  // Rather than a fleeting toast, we render a full dialog explaining what
  // went wrong and which CMS fields the admin should fix.
  const [pushError, setPushError] = useState(null);

  // Bulk-status change state — which rows are ticked + whether an apply is in
  // flight. Backend endpoint updates many properties in one atomic call.
  const [selectedPropertyIds, setSelectedPropertyIds] = useState(new Set());
  const [bulkStatusBusy, setBulkStatusBusy] = useState(false);

  // Property-edit-modal section order — the admin arranges sections in
  // "CMS → Property Form", stored via /admin/property-form-layout.
  const DEFAULT_SECTION_ORDER = [
    "title", "listing_status", "page_display", "import_url", "price", "address",
    "general_details", "video", "gallery", "floorplan", "agent", "vendor", "seo",
    "rm_type_status", "rm_address", "rm_dates", "rm_price_tenure",
    "rm_description", "rm_rooms_size", "rm_amenities", "rm_regulatory", "rm_media",
  ];
  const [sectionOrder, setSectionOrder] = useState(DEFAULT_SECTION_ORDER);
  useEffect(() => {
    axios
      .get(`${API}/admin/property-form-layout`, { withCredentials: true })
      .then(({ data }) => {
        if (!Array.isArray(data?.order)) return;
        // Merge saved order with the default list so any sections we ship
        // AFTER the admin saved their layout (e.g. "agent") still appear
        // rather than getting order:999 and floating to the bottom.
        const saved = data.order.filter((id) => DEFAULT_SECTION_ORDER.includes(id));
        const missing = DEFAULT_SECTION_ORDER.filter((id) => !saved.includes(id));
        setSectionOrder([...saved, ...missing]);
      })
      .catch(() => {});
  }, [showPropertyDialog]);
  // CSS-order lookup — the grid respects `style.order` on each child, so we
  // can rearrange sections without touching the JSX structure.
  const sectionOrderIdx = (id) => {
    const i = sectionOrder.indexOf(id);
    return i === -1 ? 999 : i;
  };

  // Per-CMS-field settings driven by the CMS Fields table (info + visible).
  // Fetched once when the admin opens the dashboard; consumed by the property
  // edit form to conditionally hide fields and show info popovers.
  const [cmsFieldSettings, setCmsFieldSettings] = useState({});
  useEffect(() => {
    axios
      .get(`${API}/admin/cms-field-settings`, { withCredentials: true })
      .then(({ data }) => setCmsFieldSettings(data?.settings || {}))
      .catch(() => {});
  }, []);
  // Look up an info/visible pair for a CMS field key.
  const cmsMeta = (key) => cmsFieldSettings[key] || { info: "", visible: true };

  const applyBulkStatus = async ({ status, transaction_status }) => {
    const ids = Array.from(selectedPropertyIds);
    if (!ids.length) return;
    try {
      setBulkStatusBusy(true);
      const body = { property_ids: ids };
      if (status) body.status = status;
      if (transaction_status) body.transaction_status = transaction_status;
      const { data } = await axios.post(
        `${API}/admin/properties/bulk-status`,
        body,
        { withCredentials: true }
      );
      toast.success(
        `${data.modified} of ${data.matched} propert${data.matched === 1 ? "y" : "ies"} updated` +
        (status ? ` · status → ${status}` : "") +
        (transaction_status ? ` · txn → ${transaction_status}` : "")
      );
      // Refresh the properties list to reflect new statuses.
      const refreshed = await axios.get(`${API}/properties`);
      setProperties(refreshed.data || []);
      // Keep selection so the admin can chain another change on the same set.
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk update failed");
    } finally {
      setBulkStatusBusy(false);
    }
  };

  // Parse a backend push response into the shape the dialog expects.
  const buildErrorDialog = (provider, environment, property, resp) => {
    const httpStatus = resp?.http_status || resp?.status_code || null;
    // Zoopla returns {status, http_status, message, missing_fields?, url, request_body, response_text}
    // Rightmove returns {status_code, response_text, response_json, detail?}
    const missing = Array.isArray(resp?.missing_fields) ? resp.missing_fields : [];
    let title = `${provider} push failed`;
    let summary = resp?.message || resp?.detail || "";
    let responseText = resp?.response_text || "";
    // Try to pull the primary error line out of a Zoopla JSON response.
    if (!summary && responseText) {
      try {
        const parsed = JSON.parse(responseText);
        if (parsed.error_advice) summary = parsed.error_advice;
        else if (Array.isArray(parsed.errors) && parsed.errors[0]?.message) {
          summary = parsed.errors[0].message.slice(0, 300);
        }
      } catch (_) { /* not JSON */ }
    }
    if (missing.length) title = `${provider} push blocked — fix required field${missing.length > 1 ? "s" : ""}`;
    return {
      provider,
      environment,
      property,
      httpStatus,
      title,
      summary: summary || "Push failed — see response details below.",
      missing,
      url: resp?.url || "",
      request_body: resp?.request_body || null,
      response_text: responseText || "",
    };
  };
  const [zooplaForcePushing, setZooplaForcePushing] = useState({});
  const handleZooplaAction = async (property, env, mode = "push") => {
    const pid = property?.property_id;
    if (!pid) return;
    const envLabel = env === "live" ? "LIVE" : "Sandbox";
    const isDelete = mode === "delete";
    const verb = isDelete ? "Remove" : "Force-push";
    // Confirmation is only shown for destructive actions (delete). Push is
    // treated as safe/repeatable so we skip the popup.
    if (isDelete) {
      if (!window.confirm(
        `Remove "${property.title || pid}" from Zoopla ${envLabel}?\n\n` +
        `Dispatches listing/delete — the listing will be withdrawn from Zoopla ${envLabel}.` +
        (env === "live" ? "\n\nThis WILL remove your public Zoopla listing." : "")
      )) return;
    }
    const key = `${pid}:${env}:${mode}`;
    try {
      setZooplaForcePushing((prev) => ({ ...prev, [key]: true }));
      const { data } = await axios.post(
        `${API}/admin/integrations/zoopla-test-call`,
        {
          environment: env,
          action: isDelete ? "listing_delete" : "listing_update",
          property_id: pid,
        },
        { withCredentials: true, timeout: 90000 }
      );
      if (data.status === "success") {
        toast.success(`Zoopla ${envLabel}: ${isDelete ? "listing/delete" : "listing/update"} → HTTP ${data.http_status}`);
      } else {
        toast.error(`Zoopla ${envLabel} ${verb.toLowerCase()} failed — see details`);
        setPushError(buildErrorDialog(`Zoopla ${envLabel}`, env, property, data));
      }
    } catch (e) {
      const errData = e.response?.data || {};
      toast.error(`Zoopla ${envLabel} ${verb.toLowerCase()} failed — see details`);
      setPushError(buildErrorDialog(`Zoopla ${envLabel}`, env, property, {
        message: errData.detail || errData.message || e.message,
        http_status: e.response?.status || null,
      }));
    } finally {
      setZooplaForcePushing((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };
  const handleZooplaForcePush = (property, env) => handleZooplaAction(property, env, "push");
  const handleZooplaRemove   = (property, env) => handleZooplaAction(property, env, "delete");

  const handleSubmitProperty = async () => {
    if (!propertyForm.title || !propertyForm.price || !propertyForm.display_address) {
      toast.error("Please fill in required fields");
      return;
    }
    // Use first (by sort_order) gallery item as main_image (featured image).
    const gallery = [...propertyForm.gallery].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
    const main_image = gallery.length > 0 ? (gallery[0].media_url || null) : null;

    setFormLoading(true);
    try {
      const data = {
        title: propertyForm.title,
        price: parseInt(propertyForm.price) || 0,
        price_label: propertyForm.price_label || "Default",
        currency: propertyForm.currency || "£",
        display_address: propertyForm.display_address,
        cms_House_Name_Number: propertyForm.cms_House_Name_Number || null,
        street_name: propertyForm.street_name || null,
        postcode: propertyForm.postcode || null,
        transaction_status: propertyForm.transaction_status || null,
        latitude: propertyForm.latitude ? parseFloat(propertyForm.latitude) : null,
        longitude: propertyForm.longitude ? parseFloat(propertyForm.longitude) : null,
        beds: parseInt(propertyForm.beds) || 0,
        baths: parseInt(propertyForm.baths) || 0,
        description: propertyForm.description,
        main_image: main_image,
        gallery: gallery,
        video_url: propertyForm.video_url || null,
        video_type: propertyForm.video_type || "youtube",
        show_video_thumbnail: propertyForm.show_video_thumbnail,
        featured: propertyForm.featured,
        show_header: propertyForm.show_header,
        show_footer: propertyForm.show_footer,
        show_back_link: propertyForm.show_back_link,
        size: propertyForm.size || null,
        property_type: propertyForm.property_type || null,
        tenure: propertyForm.tenure || null,
        floorplan_image: propertyForm.floorplan_image || null,
        floorplan_size: propertyForm.floorplan_size || null,
        features: propertyForm.features || null,
        nearest_stations: propertyForm.nearest_stations || null,
        council_tax_band: propertyForm.council_tax_band || null,
        parking_spaces: propertyForm.parking_spaces ? parseInt(propertyForm.parking_spaces) : null,
        garden_facing: propertyForm.garden_facing || null,
        date_added: propertyForm.date_added || null,
        // SEO fields
        seo_title: propertyForm.seo_title || null,
        seo_description: propertyForm.seo_description || null,
        seo_slug: propertyForm.seo_slug || null,
        seo_keywords: propertyForm.seo_keywords || null,
        seo_focus_keyword: propertyForm.seo_focus_keyword || null,
        seo_robots: propertyForm.seo_robots || "index, follow",
        // Agent fields
        agent_id: propertyForm.agent_id || null,
        agent_name: propertyForm.agent_name || null,
        agent_logo: propertyForm.agent_logo || null,
        agent_contact_name: propertyForm.agent_contact_name || null,
        agent_tel: propertyForm.agent_tel || null,
        agent_whatsapp_tel: propertyForm.agent_whatsapp_tel || null,
        agent_whatsapp_enabled: propertyForm.agent_whatsapp_enabled !== false,
        agent_email: propertyForm.agent_email || null,
        agent_website: propertyForm.agent_website || null,
        agent_email_notifications: propertyForm.agent_email_notifications,
        agent_commission: propertyForm.agent_commission || null,
        agent_notes: propertyForm.agent_notes || null,
        admin_username: propertyForm.admin_username || null,
        admin_password: propertyForm.admin_password || null,
        vendor_name: propertyForm.vendor_name || null,
        vendor_email: propertyForm.vendor_email || null,
        vendor_phone: propertyForm.vendor_phone || null
      };

      if (editingProperty) {
        await axios.put(
          `${API}/admin/properties/${editingProperty.property_id}`,
          data,
          { withCredentials: true }
        );
        toast.success("Property updated successfully");
      } else {
        await axios.post(`${API}/admin/properties`, data, { withCredentials: true });
        toast.success("Property created successfully");
      }

      setShowPropertyDialog(false);
      fetchData();
    } catch (error) {
      console.error("Save property error:", error);
      toast.error("Failed to save property");
    } finally {
      setFormLoading(false);
    }
  };

  const handleEmailCredentials = async (property) => {
    const password = prompt(`Enter a password for ${property.admin_username}:`);
    if (!password) return;

    setEmailingCredentials(property.property_id);
    try {
      const response = await axios.post(
        `${API}/admin/email-credentials`,
        { property_id: property.property_id, password },
        { withCredentials: true }
      );
      
      if (response.data.status === "success") {
        toast.success(response.data.message);
      } else if (response.data.status === "partial") {
        // Email failed but credentials were saved - show them for manual sharing
        const creds = response.data.credentials;
        toast.warning(
          <div>
            <p className="font-medium">Email failed - share manually:</p>
            <p className="text-xs mt-1">Username: {creds.username}</p>
            <p className="text-xs">Password: {creds.password}</p>
            <p className="text-xs mt-1">Login: /property-admin/login</p>
          </div>,
          { duration: 15000 }
        );
      } else {
        toast.warning(response.data.message);
      }
    } catch (error) {
      console.error("Email credentials error:", error);
      toast.error(error.response?.data?.detail || "Failed to email credentials");
    } finally {
      setEmailingCredentials(null);
    }
  };

  const handleRightmoveAction = async (property, action, options = {}) => {
    setRightmoveAction({ property, action, options, loading: true, result: null });
    try {
      const { data } = await axios.post(
        `${API}/admin/integrations/rightmove-test-call`,
        {
          environment: "test",
          action,
          property_id: property.property_id,
          dry_run: false,
          ...options,
        },
        { withCredentials: true }
      );
      setRightmoveAction({ property, action, options, loading: false, result: data });
    } catch (error) {
      console.error("Rightmove action error:", error);
      setRightmoveAction({
        property,
        action,
        options,
        loading: false,
        result: { status: "error", detail: error.response?.data?.detail || error.message || "Request failed" },
      });
    }
  };

  // Kicks off the AddPremiumListing / AddFeaturedProperty flow by opening
  // the modal in an "options" state — the actual API call fires when the
  // admin clicks Confirm inside the modal.
  const openRightmoveOptions = (property, action) => {
    const baseDefaults = { auto_send_first: true };
    const defaults = action === "add_premium"
      ? { ...baseDefaults, premium_web: true, premium_mobile: true, premium_display_type: 1, premium_resale_stamp_text: "1" }
      : { ...baseDefaults, featured_type: 1 };
    setRightmoveAction({
      property,
      action,
      options: defaults,
      loading: false,
      result: null,
      needsConfirm: true,
    });
  };

  const handleContentChange = (pageId, sectionId, value) => {
    setPageContent(prev => ({
      ...prev,
      [pageId]: prev[pageId].map(item => 
        item.section_id === sectionId ? { ...item, content: value } : item
      )
    }));
  };

  const handleSaveContent = async (pageId, sectionId, content) => {
    setContentSaving(true);
    try {
      await axios.put(
        `${API}/admin/content/${pageId}/${sectionId}`,
        { content },
        { withCredentials: true }
      );
      toast.success("Content saved");
    } catch (error) {
      console.error("Save content error:", error);
      toast.error("Failed to save content");
    } finally {
      setContentSaving(false);
    }
  };

  const formatPrice = (price, currency = "£") => {
    if (!price && price !== 0) return "Price on Application";
    try {
      return `${currency}${Number(price).toLocaleString()}`;
    } catch {
      return `${currency}${price}`;
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const PAGE_LABELS = {
    home: "Home Page",
    navigation: "Navigation Menu",
    properties: "Properties Page",
    property: "Property Detail Page",
    contact: "Contact Page",
    footer: "Footer",
    common: "Common Labels"
  };

  const SECTION_LABELS = {
    // Home
    hero_subtitle: "Hero Subtitle",
    hero_title: "Hero Title",
    hero_description: "Hero Description",
    hero_cta: "Hero Button Text",
    featured_subtitle: "Featured Section Subtitle",
    featured_title: "Featured Section Title",
    cta_title: "CTA Section Title",
    cta_description: "CTA Section Description",
    cta_button: "CTA Button Text",
    stat_1_value: "Stat 1 Value",
    stat_1_label: "Stat 1 Label",
    stat_2_value: "Stat 2 Value",
    stat_2_label: "Stat 2 Label",
    stat_3_value: "Stat 3 Value",
    stat_3_label: "Stat 3 Label",
    // Navigation
    nav_home: "Home Link",
    nav_properties: "Properties Link",
    nav_contact: "Contact Link",
    nav_cta: "Nav CTA Button",
    // Properties Page
    page_subtitle: "Page Subtitle",
    page_title: "Page Title",
    page_description: "Page Description",
    view_details_btn: "View Details Button",
    no_results: "No Results Message",
    filter_location: "Location Filter Label",
    filter_beds: "Beds Filter Label",
    filter_price_min: "Min Price Label",
    filter_price_max: "Max Price Label",
    // Property Detail
    back_link: "Back Link Text",
    price_label: "Price Label",
    enquire_btn: "Enquire Button",
    call_text: "Call Text",
    about_title: "About Section Title",
    features_title: "Features Section Title",
    gallery_title: "Gallery Section Title",
    floorplan_title: "Floorplan Section Title",
    location_title: "Location Section Title",
    brochure_title: "Brochure Section Title",
    brochure_desc: "Brochure Description",
    download_btn: "Download Button Text",
    featured_by: "Featured By Label",
    share_label: "Share Label",
    // Contact Page
    form_title: "Form Title",
    form_description: "Form Description",
    form_name_label: "Name Field Label",
    form_email_label: "Email Field Label",
    form_phone_label: "Phone Field Label",
    form_message_label: "Message Field Label",
    form_submit_btn: "Submit Button Text",
    // Footer
    tagline: "Company Tagline",
    quick_links_title: "Quick Links Title",
    contact_title: "Contact Section Title",
    email: "Contact Email",
    phone: "Contact Phone",
    address: "Address",
    copyright: "Copyright Text",
    // Common Labels
    bedrooms: "Bedrooms Label",
    bathrooms: "Bathrooms Label",
    type: "Type Label",
    tenure: "Tenure Label",
    size: "Size Label",
    parking: "Parking Label",
    garden_faces: "Garden Faces Label",
    stations: "Stations Label",
    featured_badge: "Featured Badge",
    video_badge: "Video Badge"
  };


  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-white border-b border-border sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={LOGO_URL} alt="SellLocalHomes" className="h-10 w-10" />
            <div>
              <h1 className="font-serif text-xl font-medium">Admin Dashboard</h1>
              <p className="text-sm text-muted-foreground">Manage your properties & content</p>
            </div>
          </div>

          <div className="flex items-center gap-4">
            {user && (
              <div className="flex items-center gap-3" data-testid="user-info">
                {user.picture ? (
                  <img 
                    src={user.picture} 
                    alt={user.name || "User"} 
                    className="h-8 w-8 rounded-full"
                  />
                ) : (
                  <div className="h-8 w-8 rounded-full bg-primary text-white flex items-center justify-center text-sm font-medium">
                    {(user.name || user.email || "U").charAt(0).toUpperCase()}
                  </div>
                )}
                <span className="text-sm font-medium hidden sm:block" data-testid="user-name">
                  {user.name || user.email}
                </span>
              </div>
            )}
            <a href="/" className="text-muted-foreground hover:text-foreground" data-testid="view-site-link">
              <Home size={20} />
            </a>
            <Button
              asChild
              variant="ghost"
              size="sm"
              className="text-muted-foreground hover:text-foreground gap-1.5"
              title="Open the website in a new tab, in Edit Mode"
            >
              <a
                href={`${window.location.origin}/?edit=yes`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="admin-edit-site-btn"
              >
                <Pencil size={18} />
                <span className="hidden sm:inline">Edit</span>
              </a>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="text-muted-foreground hover:text-foreground"
              data-testid="logout-btn"
            >
              <LogOut size={20} />
            </Button>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="border-b border-border bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex gap-8 overflow-x-auto">
            <button
              onClick={() => setActiveTab("properties")}
              className={`py-4 border-b-2 flex items-center gap-2 text-sm font-medium transition-colors whitespace-nowrap ${
                activeTab === "properties"
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
              data-testid="tab-properties"
            >
              <Building2 size={18} />
              Properties ({properties.length})
            </button>
            <button
              onClick={() => setActiveTab("products")}
              className={`py-4 border-b-2 flex items-center gap-2 text-sm font-medium transition-colors whitespace-nowrap ${
                activeTab === "products"
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
              data-testid="tab-products"
            >
              <Package size={18} />
              Products
            </button>
            <button
              onClick={() => setActiveTab("reports")}
              className={`py-4 border-b-2 flex items-center gap-2 text-sm font-medium transition-colors whitespace-nowrap ${
                activeTab === "reports"
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
              data-testid="tab-reports"
            >
              <BarChart3 size={18} />
              Reports
            </button>
            <button
              onClick={() => setActiveTab("trading")}
              className={`py-4 border-b-2 flex items-center gap-2 text-sm font-medium transition-colors whitespace-nowrap ${
                activeTab === "trading"
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
              data-testid="tab-trading"
            >
              <HandCoins size={18} />
              Trading
              {applicationStats.pending > 0 && (
                <span
                  className="bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full"
                  title="Pending agent applications"
                >
                  {applicationStats.pending}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab("cms")}
              className={`py-4 border-b-2 flex items-center gap-2 text-sm font-medium transition-colors whitespace-nowrap ${
                activeTab === "cms"
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
              data-testid="tab-cms"
            >
              <Grid3x3 size={18} />
              CMS
            </button>
            <button
              onClick={() => setActiveTab("integrations")}
              className={`py-4 border-b-2 flex items-center gap-2 text-sm font-medium transition-colors whitespace-nowrap ${
                activeTab === "integrations"
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
              data-testid="tab-integrations"
            >
              <Plug size={18} />
              Integrations
            </button>
            <button
              onClick={() => setActiveTab("updates")}
              className={`py-4 border-b-2 flex items-center gap-2 text-sm font-medium transition-colors whitespace-nowrap ${
                activeTab === "updates"
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
              data-testid="tab-updates"
            >
              <History size={18} />
              Updates
            </button>
            <button
              onClick={() => setActiveTab("admin")}
              className={`py-4 border-b-2 flex items-center gap-2 text-sm font-medium transition-colors whitespace-nowrap ${
                activeTab === "admin"
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
              data-testid="tab-admin"
            >
              <Settings size={18} />
              System
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {activeTab === "updates" && (
          <UpdatesAdmin />
        )}

        {activeTab === "cms" && (
          <CMSTab />
        )}

        {activeTab === "products" && (
          <div className="space-y-4">
            {/* Products Sub-tabs */}
            <div className="border-b border-border">
              <div className="flex gap-4 overflow-x-auto">
                {[
                  { id: "products", label: "Products", icon: Package },
                  { id: "categories", label: "Categories", icon: Tag },
                  { id: "suppliers", label: "Suppliers", icon: Building2 },
                ].map((t) => {
                  const Icon = t.icon;
                  return (
                    <button
                      key={t.id}
                      onClick={() => setProductsSubTab(t.id)}
                      className={`py-3 px-2 border-b-2 flex items-center gap-2 text-sm font-medium transition-colors whitespace-nowrap ${
                        productsSubTab === t.id
                          ? "border-primary text-primary"
                          : "border-transparent text-muted-foreground hover:text-foreground"
                      }`}
                      data-testid={`products-subtab-${t.id}`}
                    >
                      <Icon size={16} />
                      {t.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {productsSubTab === "products" && <ProductsAdmin />}
            {productsSubTab === "categories" && <CategoriesAdmin />}
            {productsSubTab === "suppliers" && <SuppliersAdmin />}
          </div>
        )}

        {activeTab === "reports" && (
          <div className="space-y-4">
            {/* Reports Sub-tabs */}
            <div className="border-b border-border">
              <div className="flex gap-4 overflow-x-auto">
                {[
                  { id: "sales", label: "Sales", icon: BarChart3 },
                  { id: "analytics", label: "Analytics", icon: BarChart3 },
                  { id: "valuations", label: "Valuations", icon: Home },
                  { id: "planning-applications", label: "Planning Applications", icon: FileCode2 },
                  ...(siteStyles.planningAppealsSectionEnabled !== false ? [{ id: "planning-appeals", label: "Planning Appeals", icon: Gavel }] : []),
                ].map((t) => {
                  const Icon = t.icon;
                  return (
                    <button
                      key={t.id}
                      onClick={() => setReportsSubTab(t.id)}
                      className={`py-3 px-2 border-b-2 flex items-center gap-2 text-sm font-medium transition-colors whitespace-nowrap ${
                        reportsSubTab === t.id
                          ? "border-primary text-primary"
                          : "border-transparent text-muted-foreground hover:text-foreground"
                      }`}
                      data-testid={`reports-subtab-${t.id}`}
                    >
                      <Icon size={16} />
                      {t.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {reportsSubTab === "sales" && <ReportsAdmin />}
            {reportsSubTab === "analytics" && <AdminAnalyticsTab />}
            {reportsSubTab === "valuations" && <ValuationsAdmin />}
            {reportsSubTab === "planning-applications" && <PlanningApplicationsAdmin />}
            {reportsSubTab === "planning-appeals" && siteStyles.planningAppealsSectionEnabled !== false && <PlanningAppealsAdmin />}
          </div>
        )}

        {activeTab === "trading" && (
          <div className="space-y-4">
            <TradingSummaryTile
              enquiries={enquiries}
              agents={agents}
              applicationStats={applicationStats}
            />
            {/* Trading Sub-tabs */}
            <div className="border-b border-border">
              <div className="flex gap-4 overflow-x-auto">
                {[
                  { id: "offers", label: "Offers", icon: HandCoins },
                  { id: "enquiries", label: `Enquiries (${enquiries.length})`, icon: MessageSquare },
                  { id: "campaigns", label: "Campaigns", icon: Megaphone },
                  { id: "agents", label: `Account Management (${agents.length})`, icon: Users },
                  { id: "applications", label: applicationStats.pending > 0 ? `Applications (${applicationStats.pending})` : "Applications", icon: User },
                  { id: "supplier_applications", label: "Supplier Applications", icon: Building2 },
                ].map((t) => {
                  const Icon = t.icon;
                  return (
                    <button
                      key={t.id}
                      onClick={() => setTradingSubTab(t.id)}
                      className={`py-3 px-2 border-b-2 flex items-center gap-2 text-sm font-medium transition-colors whitespace-nowrap ${
                        tradingSubTab === t.id
                          ? "border-primary text-primary"
                          : "border-transparent text-muted-foreground hover:text-foreground"
                      }`}
                      data-testid={`trading-subtab-${t.id}`}
                    >
                      <Icon size={16} />
                      {t.label}
                      {t.badge > 0 && (
                        <span className="bg-red-500 text-white text-[10px] px-1.5 py-0.5 rounded-full">
                          {t.badge}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {tradingSubTab === "offers" && <AdminTradingOffers />}
            {tradingSubTab === "campaigns" && <CampaignsAdmin />}
            {tradingSubTab === "supplier_applications" && <SupplierApplicationsTab />}
          </div>
        )}

        {activeTab === "integrations" && (
          <IntegrationsTab />
        )}

        {activeTab === "admin" && (() => {
          const SYSTEM_NAV_GROUPS = [
            {
              key: "content",
              label: "Content",
              icon: FileText,
              items: [
                { id: "seo", label: "SEO", testid: "admin-subtab-seo" },
                { id: "styles", label: "Site Styles", testid: "admin-subtab-styles" },
                { id: "integrations", label: "Links", testid: "admin-subtab-integrations" },
                { id: "emails", label: "Emails", testid: "admin-subtab-emails" },
              ],
            },
            {
              key: "tools",
              label: "Tools",
              icon: Settings,
              items: [
                { id: "settings", label: "Settings", testid: "admin-subtab-settings" },
                { id: "assets", label: "Assets", testid: "admin-subtab-assets" },
                { id: "icon-generator", label: "Icon Generator", testid: "admin-subtab-icon-generator" },
                { id: "github", label: "GitHub Explorer", testid: "admin-subtab-github" },
                { id: "error-log", label: "Log", testid: "admin-subtab-error-log" },
              ],
            },
          ];
          return (
          <div className="space-y-6">
            {/* System Sub-nav — grouped into dropdown categories to avoid an
                11-wide horizontally-scrolling tab strip. */}
            <div className="border-b">
              <div className="flex gap-2 overflow-x-auto py-1">
                {SYSTEM_NAV_GROUPS.map((group) => {
                  const activeItem = group.items.find((it) => it.id === adminSubTab);
                  const groupPending = group.items.reduce((sum, it) => sum + (it.pending || 0), 0);
                  const GroupIcon = group.icon;
                  return (
                    <DropdownMenu key={group.key}>
                      <DropdownMenuTrigger asChild>
                        <button
                          type="button"
                          data-testid={`system-nav-group-${group.key}`}
                          className={`py-2 px-3 rounded-md border-b-2 flex items-center gap-2 text-sm font-medium transition-colors whitespace-nowrap ${
                            activeItem
                              ? "border-primary text-primary bg-primary/5"
                              : "border-transparent text-muted-foreground hover:text-foreground hover:bg-muted"
                          }`}
                        >
                          <GroupIcon size={16} />
                          {activeItem ? activeItem.label : group.label}
                          {groupPending > 0 && (
                            <span className="bg-red-500 text-white text-[10px] px-1.5 py-0.5 rounded-full">
                              {groupPending}
                            </span>
                          )}
                          <ChevronDown size={14} />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start">
                        {group.items.map((it) => (
                          <DropdownMenuItem
                            key={it.id}
                            data-testid={it.testid}
                            onClick={() => setAdminSubTab(it.id)}
                            className={`flex items-center justify-between gap-2 ${adminSubTab === it.id ? "bg-accent font-medium" : ""}`}
                          >
                            {it.label}
                            {it.pending > 0 && (
                              <span className="bg-red-500 text-white text-[10px] px-1.5 py-0.5 rounded-full">
                                {it.pending}
                              </span>
                            )}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  );
                })}
              </div>
            </div>

            {/* Admin Sub-tab Content */}
            {adminSubTab === "seo" && <SeoTab />}
            {adminSubTab === "styles" && <SiteStylesAdminTab />}
            {adminSubTab === "integrations" && <AdminIntegrationsTab />}
            {adminSubTab === "github" && <GitHubExplorerTab />}
            {adminSubTab === "emails" && <EmailTemplatesTab />}
            {adminSubTab === "error-log" && <ErrorLogTab />}
            {adminSubTab === "assets" && <AssetsAdmin />}
            {adminSubTab === "icon-generator" && <IconGeneratorAdmin />}
            {adminSubTab === "settings" && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-semibold mb-4">My Properties Settings</h3>
                  <p className="text-sm text-muted-foreground">Manage company details and account settings</p>
                </div>

                {/* Features */}
                <div className="bg-white border border-border p-6 space-y-4" data-testid="admin-features-section">
                  <div className="border-b border-border pb-3">
                    <h3 className="font-serif text-lg font-medium">Features</h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      Sitewide kill switches. Turning one off hides it everywhere, even if an agent has
                      their per-property switch turned on.
                    </p>
                  </div>

                  <div className="border-t border-border pt-3">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-widest mb-2">
                      Individual CTA Tiles
                    </p>
                    {[
                      { key: "dashboardEnabled", label: "Dashboard", testid: "feature-dashboard-switch" },
                      { key: "ctaEditEnabled", label: "Edit", testid: "feature-cta-edit-switch" },
                      { key: "ctaViewEnabled", label: "View", testid: "feature-cta-view-switch" },
                      { key: "ctaOffersEnabled", label: "Offers", testid: "feature-cta-offers-switch" },
                      { key: "ctaEnquiriesEnabled", label: "Enquiries", testid: "feature-cta-enquiries-switch" },
                      { key: "ctaOrdersEnabled", label: "Orders", testid: "feature-cta-orders-switch" },
                      { key: "ctaDocumentsEnabled", label: "Documents", testid: "feature-cta-documents-switch" },
                      { key: "ctaVerifyIdEnabled", label: "Verify ID", testid: "feature-cta-verify-id-switch" },
                      { key: "ctaInfoEnabled", label: "Info", testid: "feature-cta-info-switch" },
                      { key: "ctaBuyingEnabled", label: "Buy Products", testid: "feature-cta-buying-switch" },
                      { key: "ctaSocialAdEnabled", label: "Create Social Media Ad", testid: "feature-cta-social-ad-switch" },
                      { key: "ctaImportUrlEnabled", label: "Import URL", testid: "feature-cta-import-url-switch" },
                      { key: "ctaRendyEnabled", label: "Rendy (video generation)", testid: "feature-cta-rendy-switch" },
                    ].map(({ key, label, testid }) => (
                      <div key={key} className="flex items-center justify-between gap-4 py-2">
                        <Label className="font-normal text-sm">{label}</Label>
                        <Switch
                          checked={siteStyles[key] !== false}
                          onCheckedChange={(checked) => updateStyle(key, checked)}
                          data-testid={testid}
                        />
                      </div>
                    ))}
                  </div>

                  <div className="border-t border-border pt-3">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-widest mb-2">
                      Agent Dashboard Navigation
                    </p>
                    <div className="flex items-center justify-between gap-4 py-2">
                      <div>
                        <Label className="font-normal text-sm">Buying Section</Label>
                        <p className="text-xs text-muted-foreground">
                          The "Buying" pill next to "Selling" at the top of the agent dashboard
                          (Favourites, Offers made, Enquiry History)
                        </p>
                      </div>
                      <Switch
                        checked={siteStyles.buyingSectionEnabled !== false}
                        onCheckedChange={(checked) => updateStyle('buyingSectionEnabled', checked)}
                        data-testid="feature-buying-section-switch"
                      />
                    </div>
                    <div className="flex items-center justify-between gap-4 py-2 border-t border-border pt-3">
                      <div>
                        <Label className="font-normal text-sm">House Prices Section</Label>
                        <p className="text-xs text-muted-foreground">
                          The "House Prices" pill next to "Selling"/"Buying" — lets agents type any
                          postcode and get an AI price estimate from real sold comparables
                        </p>
                      </div>
                      <Switch
                        checked={siteStyles.housePricesSectionEnabled !== false}
                        onCheckedChange={(checked) => updateStyle('housePricesSectionEnabled', checked)}
                        data-testid="feature-house-prices-section-switch"
                      />
                    </div>
                    <div className="flex items-center justify-between gap-4 py-2 border-t border-border pt-3">
                      <div>
                        <Label className="font-normal text-sm">Planning Applications Section</Label>
                        <p className="text-xs text-muted-foreground">
                          The "Planning Applications" pill next to "House Prices" — auto-shows planning
                          activity near the agent's own properties, plus a postcode search for anywhere else
                        </p>
                      </div>
                      <Switch
                        checked={siteStyles.planningAppsSectionEnabled !== false}
                        onCheckedChange={(checked) => updateStyle('planningAppsSectionEnabled', checked)}
                        data-testid="feature-planning-apps-section-switch"
                      />
                    </div>
                    <div className="flex items-center justify-between gap-4 py-2 border-t border-border pt-3">
                      <div>
                        <Label className="font-normal text-sm">Planning Appeals Section</Label>
                        <p className="text-xs text-muted-foreground">
                          The "Planning Appeals" pill on the agent dashboard AND the "Planning Appeals"
                          sub-tab under Admin → Reports — searches the Planning Inspectorate appeals database
                        </p>
                      </div>
                      <Switch
                        checked={siteStyles.planningAppealsSectionEnabled !== false}
                        onCheckedChange={(checked) => updateStyle('planningAppealsSectionEnabled', checked)}
                        data-testid="feature-planning-appeals-section-switch"
                      />
                    </div>
                    <div className="flex items-center justify-between gap-4 py-2 border-t border-border pt-3">
                      <div>
                        <Label className="font-normal text-sm">Enforce ID Verification</Label>
                        <p className="text-xs text-muted-foreground">
                          When ON, agents who haven't completed "Verify ID" see a reminder banner on
                          their My Properties page. When OFF (bypass), My Properties always shows
                          normally with no reminder, regardless of verification status.
                        </p>
                      </div>
                      <Switch
                        checked={siteStyles.enforceIdVerification === true}
                        onCheckedChange={(checked) => updateStyle('enforceIdVerification', checked)}
                        data-testid="feature-enforce-id-verification-switch"
                      />
                    </div>
                    <div className="flex items-center justify-between gap-4 py-2 border-t border-border pt-3">
                      <div>
                        <Label className="font-normal text-sm">Add Property</Label>
                        <p className="text-xs text-muted-foreground">
                          The "New Property" / "Create First Property" buttons in the agent's My Properties
                        </p>
                      </div>
                      <Switch
                        checked={siteStyles.addPropertyEnabled !== false}
                        onCheckedChange={(checked) => updateStyle('addPropertyEnabled', checked)}
                        data-testid="feature-add-property-switch"
                      />
                    </div>
                  </div>

                  <div className="border-t border-border pt-3">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-widest mb-2">
                      Property Edit Dialog — AI Tools
                    </p>
                    <div className="flex items-center justify-between gap-4 py-2">
                      <div>
                        <Label className="font-normal text-sm">Estimate Price CTA</Label>
                        <p className="text-xs text-muted-foreground">
                          The "Estimate Price" button next to the Price field on a property's edit screen
                        </p>
                      </div>
                      <Switch
                        checked={siteStyles.estimatePriceCtaEnabled !== false}
                        onCheckedChange={(checked) => updateStyle('estimatePriceCtaEnabled', checked)}
                        data-testid="feature-estimate-price-cta-switch"
                      />
                    </div>
                    <div className="flex items-center justify-between gap-4 py-2 border-t border-border pt-3">
                      <div>
                        <Label className="font-normal text-sm">Planning Applications CTA</Label>
                        <p className="text-xs text-muted-foreground">
                          The separate "Planning Applications" button next to "Estimate Price" on a property's edit screen
                        </p>
                      </div>
                      <Switch
                        checked={siteStyles.planningAppsCtaEnabled !== false}
                        onCheckedChange={(checked) => updateStyle('planningAppsCtaEnabled', checked)}
                        data-testid="feature-planning-apps-cta-switch"
                      />
                    </div>
                    <div className="flex items-center justify-between gap-4 py-2 border-t border-border pt-3">
                      <div>
                        <Label className="font-normal text-sm">Show on House Prices tool</Label>
                        <p className="text-xs text-muted-foreground">
                          Nearby planning applications inside the agent dashboard's House Prices postcode tool
                          (same switch as Admin → Integrations → Planning Applications)
                        </p>
                      </div>
                      <Switch
                        checked={siteStyles.planningAppsHousePricesEnabled !== false}
                        onCheckedChange={(checked) => updateStyle('planningAppsHousePricesEnabled', checked)}
                        data-testid="feature-planning-apps-house-prices-switch-settings"
                      />
                    </div>
                    <div className="flex items-center justify-between gap-4 py-2 border-t border-border pt-3">
                      <div>
                        <Label className="font-normal text-sm">Show on Estimate Price modal</Label>
                        <p className="text-xs text-muted-foreground">
                          Nearby planning applications inside the "Estimate Price" result (agent + master admin)
                          (same switch as Admin → Integrations → Planning Applications)
                        </p>
                      </div>
                      <Switch
                        checked={siteStyles.planningAppsEstimateModalEnabled !== false}
                        onCheckedChange={(checked) => updateStyle('planningAppsEstimateModalEnabled', checked)}
                        data-testid="feature-planning-apps-estimate-modal-switch-settings"
                      />
                    </div>
                  </div>
                </div>

                <ReplicationPanel />

                {/* Company Settings */}
                <div className="bg-white border border-border p-6 space-y-4">
                  <div className="flex items-center justify-between border-b border-border pb-3">
                    <h3 className="font-serif text-lg font-medium">Master Administrator</h3>
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      {stylesAutoSaving ? (
                        <><Loader2 size={12} className="animate-spin" /> Saving...</>
                      ) : stylesHasChanges ? (
                        <><Clock size={12} /> Unsaved changes</>
                      ) : (
                        <><CheckCircle2 size={12} className="text-green-500" /> Auto-saved</>
                      )}
                    </span>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex-1 grid grid-cols-[80px_1fr] gap-y-2 gap-x-3 items-center max-w-md">
                      <Label className="text-xs text-muted-foreground">Name</Label>
                      <Input value={siteStyles.adminName || user?.name || ""} onChange={(e) => updateStyle("adminName", e.target.value)} className="rounded-none h-8 text-sm" />
                      <Label className="text-xs text-muted-foreground">Email</Label>
                      <Input value={siteStyles.adminEmail || user?.email || ""} onChange={(e) => updateStyle("adminEmail", e.target.value)} className="rounded-none h-8 text-sm" />
                    </div>
                  </div>
                </div>

                {/* Company Details */}
                <div className="bg-white border border-border p-6 space-y-4">
                  <div className="flex items-center justify-between border-b border-border pb-3">
                    <h3 className="font-serif text-lg font-medium">Company Details</h3>
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      {stylesAutoSaving ? (
                        <><Loader2 size={12} className="animate-spin" /> Saving...</>
                      ) : stylesHasChanges ? (
                        <><Clock size={12} /> Unsaved changes</>
                      ) : (
                        <><CheckCircle2 size={12} className="text-green-500" /> Auto-saved</>
                      )}
                    </span>
                  </div>
                  <div className="grid grid-cols-[120px_1fr] gap-y-3 gap-x-4 items-center max-w-2xl">
                    <Label className="text-sm text-right">Company Name</Label>
                    <Input value={siteStyles.navLogoText || ""} onChange={(e) => updateStyle("navLogoText", e.target.value)} placeholder="SellLocalHomes" className="rounded-none" />
                    <Label className="text-sm text-right">Domain</Label>
                    <Input value={siteStyles.companyDomain || ""} onChange={(e) => updateStyle("companyDomain", e.target.value)} placeholder="selllocalhomes.co.uk" className="rounded-none" />
                    <Label className="text-sm text-right">Contact Name</Label>
                    <Input value={siteStyles.contactName || ""} onChange={(e) => updateStyle("contactName", e.target.value)} placeholder="Tony Minides" className="rounded-none" />
                    <Label className="text-sm text-right">Contact Email</Label>
                    <Input type="email" value={siteStyles.contactEmail || ""} onChange={(e) => updateStyle("contactEmail", e.target.value)} placeholder="info@company.com" className="rounded-none" />
                    <Label className="text-sm text-right">Contact Phone</Label>
                    <Input value={siteStyles.contactPhone || ""} onChange={(e) => updateStyle("contactPhone", e.target.value)} placeholder="07850 007 215" className="rounded-none" />
                    <Label className="text-sm text-right">Address</Label>
                    <Input value={siteStyles.companyAddress || ""} onChange={(e) => updateStyle("companyAddress", e.target.value)} placeholder="123 Main Street, City, Country" className="rounded-none" />
                    <Label className="text-sm text-right">Company Reg.</Label>
                    <Input value={siteStyles.companyRegistration || ""} onChange={(e) => updateStyle("companyRegistration", e.target.value)} placeholder="12345678" className="rounded-none" data-testid="company-reg-input" />
                    <Label className="text-sm text-right">VAT Number</Label>
                    <Input value={siteStyles.companyVat || ""} onChange={(e) => updateStyle("companyVat", e.target.value)} placeholder="GB123456789" className="rounded-none" data-testid="company-vat-input" />
                  </div>
                  <div className="border-t border-border pt-4">
                    <div className="flex items-center gap-4">
                      <Label className="text-sm shrink-0 w-[120px] text-right">Company Logo</Label>
                      {siteStyles.companyLogo && (
                        <div className="relative h-10 w-24 rounded border border-border overflow-hidden shrink-0">
                          <img src={resolveImageUrl(siteStyles.companyLogo)} alt="Logo" className="w-full h-full object-contain p-1" />
                          <button onClick={() => updateStyle("companyLogo", "")} className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-[10px]">×</button>
                        </div>
                      )}
                      <ImageUpload onUpload={(url) => updateStyle("companyLogo", url)} multiple={false} label="Upload Logo" skipWatermark={true} />
                    </div>
                  </div>
                </div>

                {/* Account Settings */}
                <AdminAccountTab />
              </div>
            )}
          </div>
          );
        })()}

        {activeTab === "properties" && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="font-serif text-2xl font-medium">Properties</h2>
              <Button
                onClick={openCreateDialog}
                className="bg-primary text-white hover:bg-primary/90 rounded-none"
                data-testid="add-property-btn"
              >
                <Plus size={18} className="mr-2" />
                Add Property
              </Button>
            </div>

            {/* Filters & search */}
            <div className="bg-white border border-border p-3 flex flex-wrap items-end gap-3" data-testid="properties-filter-bar">
              <div className="relative flex-1 min-w-[240px]">
                <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  Search
                </Label>
                <Input
                  value={propertySearch}
                  onChange={(e) => setPropertySearch(e.target.value)}
                  placeholder="Postcode, agent name, or email…"
                  className="rounded-none mt-1"
                  data-testid="properties-search"
                />
              </div>
              <div className="min-w-[180px]">
                <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  Location
                </Label>
                <Input
                  value={propertyLocationFilter}
                  onChange={(e) => setPropertyLocationFilter(e.target.value)}
                  placeholder="Town, city, area…"
                  className="rounded-none mt-1"
                  data-testid="properties-filter-location"
                />
              </div>
              <div className="min-w-[180px]">
                <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  Agent name
                </Label>
                <Input
                  value={propertyAgentFilter}
                  onChange={(e) => setPropertyAgentFilter(e.target.value)}
                  placeholder="Agent name"
                  className="rounded-none mt-1"
                  data-testid="properties-filter-agent"
                />
              </div>
              <div className="min-w-[160px]">
                <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  Offer status
                </Label>
                <Select value={propertyOfferFilter} onValueChange={setPropertyOfferFilter}>
                  <SelectTrigger className="rounded-none mt-1" data-testid="properties-filter-offer">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="with">Has any offer</SelectItem>
                    <SelectItem value="none">No offers</SelectItem>
                    <SelectItem value="sent">Open offers</SelectItem>
                    <SelectItem value="accepted">Accepted</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                    <SelectItem value="withdrawn">Withdrawn</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {(propertySearch || propertyLocationFilter || propertyAgentFilter || propertyOfferFilter !== "all") && (
                <Button
                  variant="ghost"
                  className="rounded-none"
                  onClick={() => {
                    setPropertySearch("");
                    setPropertyLocationFilter("");
                    setPropertyAgentFilter("");
                    setPropertyOfferFilter("all");
                  }}
                  data-testid="properties-filters-clear"
                >
                  Clear
                </Button>
              )}
            </div>

            {/* Bulk status change bar — visible when one or more rows are ticked. */}
            {selectedPropertyIds.size > 0 && (
              <div
                className="bg-primary/5 border border-primary/30 rounded flex flex-wrap items-center gap-3 px-4 py-3"
                data-testid="bulk-status-bar"
              >
                <span className="text-sm font-medium" data-testid="bulk-status-count">
                  {selectedPropertyIds.size} selected
                </span>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs text-muted-foreground">Set status:</span>
                  {["For Sale", "Test", "Off"].map((s) => (
                    <Button
                      key={s}
                      size="sm"
                      variant="outline"
                      onClick={() => applyBulkStatus({ status: s })}
                      disabled={bulkStatusBusy}
                      data-testid={`bulk-status-${s.replace(/\s+/g, '-').toLowerCase()}`}
                    >
                      {s}
                    </Button>
                  ))}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs text-muted-foreground">Transaction:</span>
                  <Select
                    onValueChange={(v) => applyBulkStatus({ transaction_status: v })}
                    disabled={bulkStatusBusy}
                  >
                    <SelectTrigger className="w-44 h-8" data-testid="bulk-txn-status-trigger">
                      <SelectValue placeholder="Change to…" />
                    </SelectTrigger>
                    <SelectContent>
                      {["Available", "Under Offer", "Reserved", "SSTC", "SSTCM", "Let Agreed", "Let"].map((s) => (
                        <SelectItem key={s} value={s} data-testid={`bulk-txn-status-item-${s.replace(/\s+/g,'-').toLowerCase()}`}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="ml-auto">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setSelectedPropertyIds(new Set())}
                    disabled={bulkStatusBusy}
                    data-testid="bulk-clear-selection"
                  >
                    Clear selection
                  </Button>
                </div>
              </div>
            )}

            <div className="bg-white border border-border overflow-hidden overflow-x-auto">
              {(() => {
                // Precompute filtered list + summary lookups.
                const search = propertySearch.trim().toLowerCase();
                const locQ = propertyLocationFilter.trim().toLowerCase();
                const agentQ = propertyAgentFilter.trim().toLowerCase();
                const filteredProperties = properties.filter((p) => {
                  const summary = offerSummary[p.property_id];
                  if (propertyOfferFilter !== "all") {
                    const has = !!summary;
                    if (propertyOfferFilter === "with" && !has) return false;
                    if (propertyOfferFilter === "none" && has) return false;
                    if (
                      ["sent", "accepted", "rejected", "withdrawn"].includes(propertyOfferFilter)
                      && !(summary && summary[propertyOfferFilter] > 0)
                    ) return false;
                  }
                  if (locQ) {
                    const loc = [p.display_address, p.town, p.city, p.county]
                      .filter(Boolean).join(" ").toLowerCase();
                    if (!loc.includes(locQ)) return false;
                  }
                  if (agentQ) {
                    const a = [p.agent_name, p.agent_email].filter(Boolean).join(" ").toLowerCase();
                    if (!a.includes(agentQ)) return false;
                  }
                  if (search) {
                    const hay = [
                      p.title, p.display_address, p.postcode,
                      p.agent_name, p.agent_email,
                    ].filter(Boolean).join(" ").toLowerCase();
                    if (!hay.includes(search)) return false;
                  }
                  return true;
                });
                const filteredIds = new Set(filteredProperties.map((p) => p.property_id));
                return (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10">
                      <Checkbox
                        checked={filteredProperties.length > 0 && [...selectedPropertyIds].filter((id) => filteredIds.has(id)).length === filteredProperties.length}
                        onCheckedChange={(checked) => {
                          if (checked) setSelectedPropertyIds(new Set(filteredProperties.map(p => p.property_id)));
                          else setSelectedPropertyIds(new Set());
                        }}
                        aria-label="Select all properties"
                        data-testid="bulk-select-all"
                      />
                    </TableHead>
                    <TableHead className="w-16">Image</TableHead>
                    <TableHead>Title</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Price</TableHead>
                    <TableHead>Offers</TableHead>
                    <TableHead>Agent</TableHead>
                    <TableHead className="text-center">Video</TableHead>
                    <TableHead className="text-center">Featured</TableHead>
                    <TableHead>Admin</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredProperties.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={11} className="text-center text-sm text-muted-foreground py-8" data-testid="properties-filter-empty">
                        No properties match the current filters.
                      </TableCell>
                    </TableRow>
                  )}
                  {filteredProperties.map((property) => {
                    const today = new Date().toISOString().split('T')[0];
                    const isInactive = property.is_live === false || 
                      (property.live_from && property.live_from > today) ||
                      (property.live_to && property.live_to < today);
                    
                    return (
                    <TableRow 
                      key={property.property_id} 
                      data-testid={`property-row-${property.property_id}`}
                      className={`cursor-pointer hover:bg-muted/50 transition-colors ${isInactive ? 'opacity-50 bg-red-50/50' : ''}`}
                      onClick={() => openEditDialog(property)}
                    >
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selectedPropertyIds.has(property.property_id)}
                          onCheckedChange={(checked) => {
                            setSelectedPropertyIds((prev) => {
                              const next = new Set(prev);
                              if (checked) next.add(property.property_id);
                              else next.delete(property.property_id);
                              return next;
                            });
                          }}
                          aria-label={`Select ${property.title}`}
                          data-testid={`bulk-select-${property.property_id}`}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="relative">
                          <img 
                            src={resolveImageUrl(property.main_image)} 
                            alt={property.title}
                            className="w-12 h-12 object-cover"
                          />
                          {isInactive && (
                            <div className="absolute inset-0 flex items-center justify-center bg-red-500/20">
                              <span className="text-[8px] text-red-700 font-bold">OFF</span>
                            </div>
                          )}
                          {property.transaction_status && (() => {
                            const bgMap = {
                              "SSTC":        "bg-amber-500",
                              "SSTCM":       "bg-amber-600",
                              "Under Offer": "bg-purple-600",
                              "Reserved":    "bg-blue-600",
                              "Let Agreed":  "bg-teal-600",
                            };
                            const short = {
                              "SSTC":        "SSTC",
                              "SSTCM":       "SSTCM",
                              "Under Offer": "U/O",
                              "Reserved":    "RES",
                              "Let Agreed":  "LET",
                            };
                            const bg = bgMap[property.transaction_status];
                            if (!bg) return null;
                            return (
                              <div
                                className={`absolute bottom-0 inset-x-0 ${bg} text-white text-[7px] leading-tight text-center py-px font-semibold uppercase tracking-tight`}
                                title={property.transaction_status}
                                data-testid={`admin-row-status-${property.property_id}`}
                              >
                                {short[property.transaction_status]}
                              </div>
                            );
                          })()}
                        </div>
                      </TableCell>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          {property.title}
                          {isInactive && (
                            <span className="text-[10px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded">
                              Inactive
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">{property.display_address}</TableCell>
                      <TableCell className="font-medium text-primary">
                        {formatPrice(property.price, property.currency)}
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const s = offerSummary[property.property_id];
                          if (!s || !s.total) {
                            return <span className="text-xs text-muted-foreground">—</span>;
                          }
                          const primary = s.accepted > 0
                            ? { label: `Accepted (${s.total})`, cls: "bg-green-100 text-green-800 border-green-200" }
                            : (s.sent > 0
                              ? { label: `${s.sent} open`, cls: "bg-amber-100 text-amber-800 border-amber-200" }
                              : (s.rejected > 0
                                ? { label: `Rejected (${s.total})`, cls: "bg-red-50 text-red-700 border-red-200" }
                                : { label: `Withdrawn (${s.total})`, cls: "bg-slate-100 text-slate-600 border-slate-200" }));
                          return (
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); setActiveTab("trading"); }}
                              className={`text-[10px] font-medium border px-2 py-0.5 rounded-full cursor-pointer hover:brightness-95 ${primary.cls}`}
                              data-testid={`admin-property-offer-badge-${property.property_id}`}
                              title="View offers"
                            >
                              {primary.label}
                            </button>
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        {property.agent_logo ? (
                          <img 
                            src={resolveImageUrl(property.agent_logo)} 
                            alt={property.agent_name || 'Agent'}
                            className="h-8 w-auto max-w-[80px] object-contain"
                            title={property.agent_name || property.agent_email || ''}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'inline';
                            }}
                          />
                        ) : property.agent_name ? (
                          <span className="text-sm" title={property.agent_email || ''}>
                            {property.agent_name}
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground">-</span>
                        )}
                        {property.agent_logo && (
                          <span 
                            className="text-sm hidden" 
                            title={property.agent_email || ''}
                          >
                            {property.agent_name || 'Agent'}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {property.video_url ? (
                          <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded">
                            Has Video
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground">-</span>
                        )}
                        {property.rendy_video_status && (
                          <div className="mt-1 flex justify-center">
                            <RendyVideoStatusBadge status={property.rendy_video_status} progress={property.rendy_video_progress} />
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {property.featured ? (
                          <span className="text-xs bg-accent/20 text-accent px-2 py-1 rounded">
                            Featured
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        {property.admin_username ? (
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground" title={property.admin_username}>
                              {property.admin_username.split('@')[0]}
                            </span>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEmailCredentials(property);
                              }}
                              disabled={emailingCredentials === property.property_id}
                              className="h-6 px-2 text-xs rounded-none"
                              data-testid={`email-credentials-${property.property_id}`}
                              title="Send login credentials via email"
                            >
                              {emailingCredentials === property.property_id ? (
                                <Loader2 size={12} className="animate-spin" />
                              ) : (
                                <Mail size={12} />
                              )}
                            </Button>
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => e.stopPropagation()}
                                className="text-primary hover:text-primary"
                                title="Rightmove actions"
                                data-testid={`rightmove-menu-${property.property_id}`}
                              >
                                <Cloud size={16} />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                              align="end"
                              onClick={(e) => e.stopPropagation()}
                              className="w-56"
                            >
                              <DropdownMenuLabel className="text-xs">
                                Rightmove UAT
                              </DropdownMenuLabel>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onSelect={() => handleRightmoveAction(property, "send")}
                                data-testid={`rightmove-push-${property.property_id}`}
                              >
                                <Cloud size={14} className="mr-2 text-primary" />
                                Send / Update Property
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onSelect={() => openRightmoveOptions(property, "add_premium")}
                                data-testid={`rightmove-premium-${property.property_id}`}
                              >
                                <Crown size={14} className="mr-2 text-amber-600" />
                                Add Premium Listing…
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onSelect={() => openRightmoveOptions(property, "add_featured")}
                                data-testid={`rightmove-featured-${property.property_id}`}
                              >
                                <Star size={14} className="mr-2 text-yellow-500" />
                                Add Featured Property…
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onSelect={() => handleRightmoveAction(property, "remove")}
                                data-testid={`rightmove-remove-${property.property_id}`}
                              >
                                <CloudOff size={14} className="mr-2 text-orange-600" />
                                Remove from Rightmove
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePushToProd(property);
                            }}
                            disabled={!!pushingToProd[property.property_id]}
                            className="text-emerald-600 hover:text-emerald-700"
                            title="Push this property's content to LIVE production"
                            data-testid={`push-to-prod-${property.property_id}`}
                          >
                            {pushingToProd[property.property_id] ? (
                              <Loader2 size={16} className="animate-spin" />
                            ) : (
                              <Rocket size={16} />
                            )}
                          </Button>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => e.stopPropagation()}
                                disabled={Object.keys(zooplaForcePushing).some((k) => k.startsWith(`${property.property_id}:`))}
                                className="text-sky-600 hover:text-sky-700"
                                title="Zoopla actions (force-push / remove)"
                                data-testid={`zoopla-force-push-${property.property_id}`}
                              >
                                {Object.keys(zooplaForcePushing).some((k) => k.startsWith(`${property.property_id}:`)) ? (
                                  <Loader2 size={16} className="animate-spin" />
                                ) : (
                                  <Zap size={16} />
                                )}
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                              align="end"
                              onClick={(e) => e.stopPropagation()}
                              className="w-56"
                            >
                              <DropdownMenuLabel className="text-xs">
                                Force-push to Zoopla
                              </DropdownMenuLabel>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onSelect={() => handleZooplaForcePush(property, "sandbox")}
                                data-testid={`zoopla-force-push-sandbox-${property.property_id}`}
                              >
                                <Zap size={14} className="mr-2 text-sky-600" />
                                Sandbox
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onSelect={() => handleZooplaForcePush(property, "live")}
                                data-testid={`zoopla-force-push-live-${property.property_id}`}
                              >
                                <Zap size={14} className="mr-2 text-red-600" />
                                Live (production)
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuLabel className="text-xs">
                                Remove from Zoopla
                              </DropdownMenuLabel>
                              <DropdownMenuItem
                                onSelect={() => handleZooplaRemove(property, "sandbox")}
                                data-testid={`zoopla-remove-sandbox-${property.property_id}`}
                              >
                                <CloudOff size={14} className="mr-2 text-orange-600" />
                                Remove from Sandbox
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onSelect={() => handleZooplaRemove(property, "live")}
                                data-testid={`zoopla-remove-live-${property.property_id}`}
                              >
                                <CloudOff size={14} className="mr-2 text-red-600" />
                                Remove from Live
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteProperty(property.property_id);
                            }}
                            className="text-destructive hover:text-destructive"
                            data-testid={`delete-property-${property.property_id}`}
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                  })}
                </TableBody>
              </Table>
                );
              })()}
            </div>
          </div>
        )}

        {activeTab === "trading" && tradingSubTab === "enquiries" && (
          <div className="space-y-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <h2 className="font-serif text-2xl font-medium">Enquiries</h2>
              
              {/* Search */}
              <div className="relative w-full md:w-80">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name, email, phone..."
                  value={enquirySearch}
                  onChange={(e) => setEnquirySearch(e.target.value)}
                  className="pl-10 rounded-none"
                  data-testid="enquiry-search"
                />
              </div>
            </div>

            {/* Sort Options */}
            <div className="flex flex-wrap gap-2">
              <span className="text-sm text-muted-foreground self-center">Sort by:</span>
              {[
                { field: "timestamp", label: "Date" },
                { field: "name", label: "Name" },
                { field: "email", label: "Email" },
                { field: "property_title", label: "Property" }
              ].map(({ field, label }) => (
                <Button
                  key={field}
                  variant={enquirySort.field === field ? "default" : "outline"}
                  size="sm"
                  onClick={() => toggleEnquirySort(field)}
                  className="rounded-none gap-1"
                >
                  {label}
                  {enquirySort.field === field && (
                    enquirySort.direction === "asc" ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />
                  )}
                </Button>
              ))}
            </div>

            {getFilteredAndSortedEnquiries().length === 0 ? (
              <div className="bg-white border border-border p-12 text-center">
                <Mail className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">
                  {enquirySearch ? "No enquiries match your search" : "No enquiries yet"}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Showing {getFilteredAndSortedEnquiries().length} of {enquiries.length} enquiries
                </p>
                
                {getFilteredAndSortedEnquiries().map((enquiry) => (
                  <div 
                    key={enquiry.enquiry_id} 
                    className="bg-white border border-border p-6 space-y-4"
                    data-testid={`enquiry-${enquiry.enquiry_id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-medium">{enquiry.name}</h3>
                        <p className="text-sm text-muted-foreground">{enquiry.email}</p>
                        {enquiry.phone && (
                          <p className="text-sm text-muted-foreground">{enquiry.phone}</p>
                        )}
                      </div>
                      <div className="text-right flex flex-col items-end gap-2">
                        <p className="text-xs text-muted-foreground">
                          {formatDate(enquiry.timestamp)}
                        </p>
                        {enquiry.property_title && (
                          <p className="text-sm text-primary">
                            Re: {enquiry.property_title}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="bg-muted/30 p-4 border-l-4 border-primary">
                      <p className="text-sm">{enquiry.message}</p>
                    </div>
                    <div className="flex items-center justify-between">
                      <a 
                        href={`mailto:${enquiry.email}`}
                        className="text-sm text-primary hover:underline"
                      >
                        Reply via Email
                      </a>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setDeletingEnquiry(enquiry)}
                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        data-testid={`delete-enquiry-${enquiry.enquiry_id}`}
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        Delete
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Delete Confirmation Dialog */}
            <Dialog open={!!deletingEnquiry} onOpenChange={() => setDeletingEnquiry(null)}>
              <DialogContent className="rounded-none">
                <DialogHeader>
                  <DialogTitle>Delete Enquiry</DialogTitle>
                </DialogHeader>
                <p className="text-muted-foreground">
                  Are you sure you want to delete the enquiry from <strong>{deletingEnquiry?.name}</strong>? This action cannot be undone.
                </p>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setDeletingEnquiry(null)} className="rounded-none">
                    Cancel
                  </Button>
                  <Button 
                    variant="destructive" 
                    onClick={() => handleDeleteEnquiry(deletingEnquiry?.enquiry_id)}
                    className="rounded-none"
                  >
                    Delete
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        )}

        {activeTab === "content" && (
          <ContentTab pageContent={pageContent} setPageContent={setPageContent} />
        )}

        {activeTab === "trading" && tradingSubTab === "agents" && (
          <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-serif text-2xl font-medium">Account Management</h2>
                <p className="text-muted-foreground text-sm">Manage agent accounts and link them to properties</p>
              </div>
              <Button
                onClick={() => handleOpenAgentDialog()}
                className="bg-primary text-white hover:bg-primary/90 rounded-none"
                data-testid="add-agent-btn"
              >
                <Plus className="mr-2 h-4 w-4" />
                Add Agent
              </Button>
            </div>

            {/* Agents — row layout: Master agent first, then Agents, then non-agents, each alphabetical */}
            {agentsLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : agents.length === 0 ? (
              <div className="text-center py-12 bg-white border border-border">
                <UserCircle className="mx-auto h-12 w-12 text-muted-foreground/50 mb-4" />
                <p className="text-muted-foreground">No agents yet</p>
                <p className="text-sm text-muted-foreground mb-4">Create your first agent to manage properties</p>
                <Button onClick={() => handleOpenAgentDialog()} className="rounded-none">
                  <Plus className="mr-2 h-4 w-4" />
                  Add Agent
                </Button>
              </div>
            ) : (
              <div className="bg-white border border-border divide-y divide-border" data-testid="agents-row-list">
                {[...agents]
                  .sort((a, b) => {
                    const rank = (x) => (x.is_master_agent ? 0 : x.is_agent !== false ? 1 : 2);
                    const ra = rank(a), rb = rank(b);
                    if (ra !== rb) return ra - rb;
                    return (a.name || "").localeCompare(b.name || "");
                  })
                  .map((agent) => {
                  const today = new Date().toISOString().split('T')[0];
                  const isLive = agent.is_live !== false && 
                    (!agent.live_from || agent.live_from <= today) &&
                    (!agent.live_to || agent.live_to >= today);
                  const workers = agents.filter((a) => a.parent_agent_id === agent.agent_id && a.is_agent === false);
                  const workersOpen = expandedAgentWorkers.has(agent.agent_id);
                  const addableWorkers = agents.filter((a) => a.is_agent === false && a.agent_id !== agent.agent_id);

                  return (
                  <div key={agent.agent_id} data-testid={`agent-row-${agent.agent_id}`}>
                    <div className={`flex flex-wrap items-center gap-3 p-3 ${isLive ? '' : 'bg-red-50/50 opacity-75'}`}>
                      {agent.logo ? (
                        <img
                          src={resolveImageUrl(agent.logo)}
                          alt={agent.name}
                          className="w-10 h-10 object-contain bg-white p-0.5 border border-border flex-shrink-0"
                        />
                      ) : (
                        <div className="w-10 h-10 bg-white flex items-center justify-center border border-border flex-shrink-0">
                          <UserCircle className="h-5 w-5 text-muted-foreground" />
                        </div>
                      )}

                      <div className="min-w-0 w-48 flex-shrink-0">
                        <div className="flex items-center gap-1.5">
                          <h3 className="font-medium truncate text-sm">{agent.name}</h3>
                        </div>
                        {agent.contact_name && (
                          <p className="text-xs text-muted-foreground truncate">{agent.contact_name}</p>
                        )}
                      </div>

                      <div className="flex items-center gap-1.5 flex-wrap w-40 flex-shrink-0">
                        {agent.is_master_agent && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded font-medium bg-amber-100 text-amber-700" data-testid={`agent-master-badge-${agent.agent_id}`}>
                            Master
                          </span>
                        )}
                        <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${isLive ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}`}>
                          {isLive ? 'LIVE' : 'OFFLINE'}
                        </span>
                        <span
                          className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${agent.is_agent !== false ? 'bg-blue-100 text-blue-700' : 'bg-gray-200 text-gray-600'}`}
                          data-testid={`agent-type-badge-${agent.agent_id}`}
                        >
                          {agent.is_agent !== false ? 'Agent' : 'Not an agent'}
                        </span>
                      </div>

                      <div className="min-w-0 flex-1 text-xs text-muted-foreground space-y-0.5">
                        {agent.email && (
                          <div className="flex items-center gap-1.5 truncate">
                            <Mail className="h-3 w-3 flex-shrink-0" />
                            <span className="truncate">{agent.email}</span>
                          </div>
                        )}
                        {agent.phone && (
                          <div className="flex items-center gap-1.5">
                            <Phone className="h-3 w-3 flex-shrink-0" />
                            <span>{agent.phone}</span>
                          </div>
                        )}
                      </div>

                      <div className="w-20 flex-shrink-0 text-xs text-muted-foreground text-center">
                        {agent.properties_count || 0} props
                      </div>

                      {agent.is_agent === false ? (
                        <div className="w-40 flex-shrink-0">
                          <Select
                            value={agent.parent_agent_id || "none"}
                            onValueChange={(value) => handleWorkerUpdate(agent.agent_id, { parent_agent_id: value === "none" ? null : value })}
                          >
                            <SelectTrigger className="rounded-none h-8 text-xs" data-testid={`agent-parent-select-${agent.agent_id}`}>
                              <SelectValue placeholder="Parent agent" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">Unassigned</SelectItem>
                              {agents
                                .filter((a) => a.is_agent !== false)
                                .map((a) => (
                                  <SelectItem key={a.agent_id} value={a.agent_id}>
                                    {a.name}{a.is_master_agent ? " (Master)" : ""}
                                  </SelectItem>
                                ))}
                            </SelectContent>
                          </Select>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => toggleAgentWorkersPanel(agent.agent_id)}
                          className="w-28 flex-shrink-0 text-xs flex items-center justify-center gap-1 text-primary hover:underline"
                          data-testid={`agent-workers-toggle-${agent.agent_id}`}
                        >
                          {workersOpen ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                          Workers ({workers.length})
                        </button>
                      )}

                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleOpenAgentDialog(agent)}
                          className="rounded-none"
                          data-testid={`agent-edit-btn-${agent.agent_id}`}
                        >
                          <Pencil className="mr-1 h-3 w-3" />
                          Edit
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setDocumentsAgent(agent)}
                          className="rounded-none"
                          title="View this agent's document library"
                          data-testid={`agent-documents-btn-${agent.agent_id}`}
                        >
                          <FolderOpen className="h-3 w-3" />
                        </Button>
                        {!agent.is_master_agent && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setDeletingAgent(agent)}
                            className="text-destructive hover:text-destructive rounded-none"
                            data-testid={`agent-delete-btn-${agent.agent_id}`}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* Linked Properties (compact, inline) */}
                    {agent.linked_properties && agent.linked_properties.length > 0 && (
                      <div className="px-3 pb-2 flex items-center gap-2 flex-wrap text-xs text-muted-foreground">
                        <span className="font-medium">Properties:</span>
                        {agent.linked_properties.slice(0, 3).map((prop) => (
                          <span key={prop.property_id} className="flex items-center gap-1 bg-muted/50 px-1.5 py-0.5">
                            {prop.title}
                            <button
                              onClick={() => handleUnlinkAgentFromProperty(agent.agent_id, prop.property_id)}
                              className="text-muted-foreground hover:text-destructive"
                              title="Unlink property"
                            >
                              <Unlink className="h-2.5 w-2.5" />
                            </button>
                          </span>
                        ))}
                        {agent.linked_properties.length > 3 && (
                          <button
                            onClick={() => setShowAgentPropertiesDialog(agent)}
                            className="text-primary hover:underline"
                          >
                            +{agent.linked_properties.length - 3} more
                          </button>
                        )}
                      </div>
                    )}

                    {/* Workers panel — collapsed by default */}
                    {workersOpen && (
                      <div className="px-3 pb-3 bg-muted/20 border-t border-border pt-3" data-testid={`agent-workers-panel-${agent.agent_id}`}>
                        {workers.length === 0 ? (
                          <p className="text-xs text-muted-foreground mb-2" data-testid={`agent-workers-empty-${agent.agent_id}`}>
                            No workers assigned to this agent yet.
                          </p>
                        ) : (
                          <div className="space-y-1.5 mb-2">
                            {workers.map((worker) => (
                              <div
                                key={worker.agent_id}
                                className="flex items-center justify-between gap-2 bg-white border border-border px-2 py-1.5 text-xs"
                                data-testid={`agent-worker-item-${agent.agent_id}-${worker.agent_id}`}
                              >
                                <div className="min-w-0 truncate">
                                  <span className="font-medium">{worker.contact_name || worker.name}</span>
                                  <span className="text-muted-foreground ml-2">{worker.email}</span>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => handleRemoveWorker(worker.agent_id)}
                                  disabled={savingWorkerId === worker.agent_id}
                                  className="text-muted-foreground hover:text-destructive flex-shrink-0 disabled:opacity-40"
                                  title="Remove from this agent (returns to Master Agent)"
                                  data-testid={`agent-worker-remove-${agent.agent_id}-${worker.agent_id}`}
                                >
                                  <X className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                        <Select
                          value=""
                          onValueChange={(value) => handleWorkerUpdate(value, { parent_agent_id: agent.agent_id })}
                        >
                          <SelectTrigger className="rounded-none h-8 text-xs max-w-xs" data-testid={`agent-worker-add-select-${agent.agent_id}`}>
                            <SelectValue placeholder="+ Add worker..." />
                          </SelectTrigger>
                          <SelectContent>
                            {addableWorkers.length === 0 ? (
                              <div className="px-2 py-1.5 text-xs text-muted-foreground">No non-agent accounts available</div>
                            ) : (
                              addableWorkers.map((w) => (
                                <SelectItem key={w.agent_id} value={w.agent_id}>
                                  {w.contact_name || w.name} ({w.email})
                                </SelectItem>
                              ))
                            )}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                );
                })}
              </div>
            )}

            {/* Valuation Accounts — tenant accounts auto-created when
                someone submits the Capture Property Form (see
                `_upsert_tenant` in routes/list_property.py); each one
                carries its HM Land Registry + AI valuation PDF report in
                Documents. */}
            <div className="mt-8" data-testid="valuation-accounts-section">
              <h3 className="font-serif text-lg font-medium mb-1">Valuation Accounts ({tenants.length})</h3>
              <p className="text-xs text-muted-foreground mb-3">
                Accounts created automatically when someone submits the property valuation form, each with their HM Land Registry + AI valuation report stored in Documents.
              </p>
              <Input
                value={tenantSearch}
                onChange={(e) => setTenantSearch(e.target.value)}
                placeholder="Search by name, email or postcode..."
                className="rounded-none mb-3 max-w-sm"
                data-testid="valuation-accounts-search"
              />
              {tenantsLoading ? (
                <p className="text-sm text-muted-foreground">Loading...</p>
              ) : tenants.length === 0 ? (
                <p className="text-sm text-muted-foreground" data-testid="valuation-accounts-empty">No valuation accounts yet.</p>
              ) : (
                <div className="border border-border divide-y divide-border" data-testid="valuation-accounts-list">
                  {tenants.filter((t) => tenantMatchesSearch(t, tenantSearch)).length === 0 ? (
                    <p className="text-sm text-muted-foreground px-3 py-4" data-testid="valuation-accounts-no-results">No accounts match "{tenantSearch}".</p>
                  ) : (
                    tenants.filter((t) => tenantMatchesSearch(t, tenantSearch)).map((t) => (
                    <div
                      key={t.tenant_ref_id}
                      className="flex items-center justify-between gap-3 px-3 py-2.5 text-sm flex-wrap"
                      data-testid={`valuation-account-row-${t.tenant_ref_id}`}
                    >
                      <div className="min-w-0">
                        <div className="font-medium truncate" data-testid={`valuation-account-name-${t.tenant_ref_id}`}>{t.name || "(no name)"}</div>
                        <div className="text-xs text-muted-foreground truncate" data-testid={`valuation-account-email-${t.tenant_ref_id}`}>
                          {t.email}{t.phone ? ` · ${t.phone}` : ""}{t.postcodes?.length ? ` · ${t.postcodes.join(", ")}` : ""}
                        </div>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0 text-xs text-muted-foreground">
                        <span>{(t.property_ids || []).length} propert{(t.property_ids || []).length === 1 ? "y" : "ies"}</span>
                        <span>{t.created_at ? new Date(t.created_at).toLocaleDateString() : ""}</span>
                        <Button
                          variant="outline"
                          size="sm"
                          className="rounded-none h-7 text-xs"
                          disabled={!t.document_count}
                          onClick={() => setTenantDocsDialog(t)}
                          data-testid={`valuation-account-docs-btn-${t.tenant_ref_id}`}
                        >
                          <FolderOpen className="h-3 w-3 mr-1" /> Documents ({t.document_count || 0})
                        </Button>
                      </div>
                    </div>
                    ))
                  )}
                </div>
              )}
            </div>

            {/* Agent Dialog */}
            <Dialog open={showAgentDialog} onOpenChange={setShowAgentDialog}>
              <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto slh-dialog-fixed-90">
                <DialogHeader>
                  <DialogTitle>{editingAgent ? "Edit Account" : "Add New Agent"}</DialogTitle>
                  <DialogDescription>
                    {editingAgent ? "Update agent details" : "Create a new agent account"}
                  </DialogDescription>
                </DialogHeader>
                
                <div className="space-y-4 py-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2 space-y-2">
                      <Label>Agency/Company Name *</Label>
                      <Input
                        value={agentForm.name}
                        onChange={(e) => handleAgentFormChange('name', e.target.value)}
                        placeholder="ABC Realty"
                        className="rounded-none"
                        data-testid="agent-name-input"
                      />
                    </div>
                    
                    {/* Agent Logo Upload */}
                    <div className="col-span-2 space-y-2">
                      <Label>Agent Logo</Label>
                      <div className="flex items-start gap-4">
                        {agentForm.logo && (
                          <div className="relative">
                            <img 
                              src={resolveImageUrl(agentForm.logo)} 
                              alt="Agent logo preview"
                              className="h-20 w-auto max-w-[150px] object-contain border border-border p-1 bg-white"
                            />
                            <button
                              type="button"
                              onClick={() => { setAgentForm(prev => ({ ...prev, logo: '' })); if (editingAgent) setAgentHasChanges(true); }}
                              className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                            >
                              <X size={12} />
                            </button>
                          </div>
                        )}
                        <div className="flex-1">
                          <ImageUpload
                            onUpload={handleAgentLogoUpload}
                            multiple={false}
                            label="Upload Agent Logo"
                            skipWatermark={true}
                          />
                          <p className="text-xs text-muted-foreground mt-2">Or paste a URL:</p>
                          <Input
                            value={agentForm.logo}
                            onChange={(e) => { setAgentForm(prev => ({ ...prev, logo: e.target.value })); if (editingAgent) setAgentHasChanges(true); }}
                            placeholder="https://example.com/logo.png"
                            className="rounded-none mt-1"
                          />
                        </div>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Agent Contact Name</Label>
                      <Input
                        value={agentForm.contact_name}
                        onChange={(e) => handleAgentFormChange('contact_name', e.target.value)}
                        placeholder="John Smith"
                        className="rounded-none"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Agent Email</Label>
                      <Input
                        type="email"
                        value={agentForm.email}
                        onChange={(e) => handleAgentFormChange('email', e.target.value)}
                        placeholder="agent@example.com"
                        className="rounded-none"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Agent Telephone</Label>
                      <Input
                        value={agentForm.phone}
                        onChange={(e) => handleAgentFormChange('phone', e.target.value)}
                        placeholder="+44 7123 456789"
                        className="rounded-none"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Agent Website</Label>
                      <Input
                        value={agentForm.website}
                        onChange={(e) => handleAgentFormChange('website', e.target.value)}
                        placeholder="https://www.example.com"
                        className="rounded-none"
                      />
                    </div>
                    
                    <div className="col-span-2 space-y-2">
                      <Label>Address</Label>
                      <Input
                        value={agentForm.address}
                        onChange={(e) => handleAgentFormChange('address', e.target.value)}
                        placeholder="123 High Street, London"
                        className="rounded-none"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Commission</Label>
                      <Input
                        value={agentForm.commission}
                        onChange={(e) => handleAgentFormChange('commission', e.target.value)}
                        placeholder="2.5%"
                        className="rounded-none"
                      />
                    </div>
                    
                    <div className="col-span-2 space-y-2 flex items-center gap-2">
                      <Switch
                        checked={agentForm.email_notifications}
                        onCheckedChange={(checked) => handleAgentFormChange('email_notifications', checked)}
                      />
                      <Label>Email Notifications (receive enquiry alerts)</Label>
                    </div>

                    <div className="col-span-2 space-y-2 flex items-center gap-2">
                      <Switch
                        checked={agentForm.marketing_emails !== false}
                        onCheckedChange={(checked) => handleAgentFormChange('marketing_emails', checked)}
                        data-testid="agent-marketing-emails-switch"
                      />
                      <Label>Marketing Emails (receive campaign &amp; promo emails)</Label>
                    </div>

                    <div className="col-span-2 space-y-2 flex items-center gap-2">
                      <Switch
                        checked={agentForm.supplier_emails !== false}
                        onCheckedChange={(checked) => handleAgentFormChange('supplier_emails', checked)}
                        data-testid="agent-supplier-emails-switch"
                      />
                      <Label>Supplier Emails (receive preferred-supplier updates)</Label>
                    </div>

                    <div className="col-span-2 space-y-2 flex items-center gap-2">
                      <Switch
                        checked={agentForm.is_agent !== false}
                        onCheckedChange={(checked) => handleAgentFormChange('is_agent', checked)}
                        data-testid="agent-is-agent-switch"
                      />
                      <Label>This account is an Agent (uncheck for a non-agent company account)</Label>
                    </div>

                    {agentForm.is_agent === false && (
                      <div className="col-span-2 space-y-2">
                        <Label>Parent Agent</Label>
                        <Select
                          value={agentForm.parent_agent_id || "none"}
                          onValueChange={(value) => handleAgentFormChange('parent_agent_id', value === "none" ? null : value)}
                        >
                          <SelectTrigger className="rounded-none" data-testid="agent-parent-select-dialog">
                            <SelectValue placeholder="Select parent agent" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Unassigned</SelectItem>
                            {agents
                              .filter((a) => a.is_agent !== false && a.agent_id !== editingAgent?.agent_id)
                              .map((a) => (
                                <SelectItem key={a.agent_id} value={a.agent_id}>
                                  {a.name}{a.is_master_agent ? " (Master)" : ""}
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    <div className="col-span-2 space-y-2 flex items-center gap-2">
                      <Switch
                        checked={agentForm.is_master_agent === true}
                        onCheckedChange={(checked) => handleAgentFormChange('is_master_agent', checked)}
                        data-testid="agent-is-master-switch"
                      />
                      <Label>Master Agent (new SSO sign-ups default under this account — only one allowed)</Label>
                    </div>
                    
                    <div className="col-span-2 space-y-2">
                      <Label>Agent Notes (Internal)</Label>
                      <Textarea
                        value={agentForm.notes}
                        onChange={(e) => handleAgentFormChange('notes', e.target.value)}
                        placeholder="Internal notes about this agent (not visible to customers)..."
                        className="rounded-none min-h-20"
                      />
                    </div>
                    
                    {/* Live Status Section */}
                    <div className="col-span-2 bg-muted/30 border border-border rounded-lg p-4 space-y-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-3 h-3 rounded-full ${agentForm.is_live ? 'bg-green-500' : 'bg-red-500'}`} />
                          <div>
                            <h4 className="font-medium text-sm">Agent Status</h4>
                            <p className="text-xs text-muted-foreground">
                              {agentForm.is_live ? 'Agent properties visible on website' : 'Agent properties hidden from website'}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-muted-foreground">Live</span>
                          <button
                            type="button"
                            onClick={() => handleAgentFormChange('is_live', !agentForm.is_live)}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                              agentForm.is_live ? 'bg-green-500' : 'bg-gray-300'
                            }`}
                            data-testid="agent-live-toggle"
                          >
                            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              agentForm.is_live ? 'translate-x-6' : 'translate-x-1'
                            }`} />
                          </button>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1">
                          <Label className="text-xs">Live From</Label>
                          <Input
                            type="date"
                            value={agentForm.live_from || ''}
                            onChange={(e) => handleAgentFormChange('live_from', e.target.value)}
                            className="rounded-none h-9"
                            data-testid="agent-live-from"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Live Until</Label>
                          <Input
                            type="date"
                            value={agentForm.live_to || ''}
                            onChange={(e) => handleAgentFormChange('live_to', e.target.value)}
                            className="rounded-none h-9"
                            data-testid="agent-live-to"
                          />
                          <p className="text-xs text-muted-foreground">Default: 12 months</p>
                        </div>
                      </div>
                    </div>

                    {/* Account Access Section — disabling blocks login entirely
                        (password + Google SSO) and kicks out any already-open
                        session; the agent sees a "contact us" message. Separate
                        from "Live" above, which only hides their properties from
                        the public site but still lets them sign in and work. */}
                    {!agentForm.is_master_agent && (
                      <div className="col-span-2 bg-muted/30 border border-border rounded-lg p-4 space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`w-3 h-3 rounded-full ${agentForm.active ? 'bg-green-500' : 'bg-red-500'}`} />
                            <div>
                              <h4 className="font-medium text-sm">Account Access</h4>
                              <p className="text-xs text-muted-foreground">
                                {agentForm.active
                                  ? "Agent can sign in and manage their account"
                                  : "Disabled — the agent is blocked from signing in and shown a message to contact us"}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-muted-foreground">{agentForm.active ? "Enabled" : "Disabled"}</span>
                            <button
                              type="button"
                              onClick={() => handleAgentFormChange('active', !agentForm.active)}
                              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                                agentForm.active ? 'bg-green-500' : 'bg-gray-300'
                              }`}
                              data-testid="agent-active-toggle"
                            >
                              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                agentForm.active ? 'translate-x-6' : 'translate-x-1'
                              }`} />
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Assets Section */}
                    <div className="col-span-2 border border-border rounded-lg p-4 space-y-4">
                      <h4 className="font-medium text-sm border-b border-border pb-2">Assets</h4>
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <Label className="text-xs">Cloud Storage URL</Label>
                            {agentForm.cloud_storage_url && (
                              <a 
                                href={agentForm.cloud_storage_url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-xs text-primary hover:underline flex items-center gap-1"
                              >
                                Open <ExternalLink size={10} />
                              </a>
                            )}
                          </div>
                          <Input
                            value={agentForm.cloud_storage_url || ''}
                            onChange={(e) => handleAgentFormChange('cloud_storage_url', e.target.value)}
                            placeholder="https://drive.google.com/..."
                            className="rounded-none"
                            data-testid="agent-cloud-storage-url"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-xs">Cloud Storage Credentials</Label>
                          <Textarea
                            value={agentForm.cloud_storage_credentials || ''}
                            onChange={(e) => handleAgentFormChange('cloud_storage_credentials', e.target.value)}
                            placeholder="Username, password, or access keys..."
                            className="rounded-none min-h-16 font-mono text-sm"
                            data-testid="agent-cloud-credentials"
                          />
                          <p className="text-xs text-muted-foreground">Keep credentials secure. Only visible to admins.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Linked Properties */}
                  {editingAgent && editingAgent.linked_properties && editingAgent.linked_properties.length > 0 && (
                    <div className="border-t border-border pt-4 mt-4">
                      <Label className="text-sm font-medium mb-2 block">Linked Properties ({editingAgent.linked_properties.length})</Label>
                      <div className="space-y-2 max-h-40 overflow-y-auto">
                        {editingAgent.linked_properties.map((prop) => (
                          <div key={prop.property_id} className="flex items-center justify-between p-2 bg-muted/50 border border-border">
                            <div className="flex-1 min-w-0">
                              <a
                                href={`/property-admin?property_id=${prop.property_id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="font-medium text-sm text-primary hover:underline flex items-center gap-1"
                                title="Open property management page"
                              >
                                {prop.title}
                                <ExternalLink className="h-3 w-3" />
                              </a>
                              <p className="text-xs text-muted-foreground">{prop.display_address}</p>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleUnlinkAgentFromProperty(editingAgent.agent_id, prop.property_id)}
                              className="text-destructive hover:bg-destructive/10 p-2 rounded"
                              title="Unlink property"
                            >
                              <Unlink className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* SSO Connections — admin-side disconnect for Google/Facebook */}
                  {editingAgent && (
                    <div className="border-t border-border pt-4 mt-4" data-testid="agent-sso-section">
                      <Label className="text-sm font-medium mb-2 block">SSO Connections</Label>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between p-2 bg-muted/50 border border-border" data-testid="agent-sso-google-row">
                          <div className="min-w-0">
                            <p className="text-sm font-medium">Google</p>
                            <p className="text-xs text-muted-foreground truncate">
                              {editingAgent.google_user_id
                                ? (editingAgent.google_email || "Connected")
                                : "Not connected"}
                            </p>
                          </div>
                          {editingAgent.google_user_id && (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="rounded-none text-destructive hover:text-destructive"
                              disabled={disconnectingSso === "google"}
                              onClick={() => handleDisconnectAgentSso(editingAgent.agent_id, "google")}
                              data-testid="agent-sso-disconnect-google-btn"
                            >
                              {disconnectingSso === "google" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Disconnect"}
                            </Button>
                          )}
                        </div>
                        <div className="flex items-center justify-between p-2 bg-muted/50 border border-border" data-testid="agent-sso-facebook-row">
                          <div className="min-w-0">
                            <p className="text-sm font-medium">Facebook</p>
                            <p className="text-xs text-muted-foreground truncate">
                              {editingAgent.facebook_id
                                ? (editingAgent.facebook_name || "Connected")
                                : "Not connected"}
                            </p>
                          </div>
                          {editingAgent.facebook_id && (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="rounded-none text-destructive hover:text-destructive"
                              disabled={disconnectingSso === "facebook"}
                              onClick={() => handleDisconnectAgentSso(editingAgent.agent_id, "facebook")}
                              data-testid="agent-sso-disconnect-facebook-btn"
                            >
                              {disconnectingSso === "facebook" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Disconnect"}
                            </Button>
                          )}
                        </div>
                      </div>
                      {!editingAgent.google_user_id && !editingAgent.facebook_id && (
                        <p className="text-xs text-muted-foreground mt-2">
                          {editingAgent.has_password
                            ? "This agent has no SSO connections — password login only."
                            : "This agent has no SSO connections and no password set — they cannot sign in until one is linked."}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Verify ID — same ID-verification tool agents use on their own
                      dashboard, mounted on-behalf-of this agent so admins can see/run
                      it without needing the agent's own session. */}
                  {editingAgent && (
                    <div className="border-t border-border pt-4 mt-4" data-testid="agent-verify-id-section">
                      <Label className="text-sm font-medium mb-2 block">Verify ID</Label>
                      <VerifyIdSection agentId={editingAgent.agent_id} />
                    </div>
                  )}

                  {/* ID Documents — lands straight inside the agent's ID Documents
                      folder (every verify-ID attempt, pass or fail, is auto-filed
                      there) without needing to open the separate Documents dialog. */}
                  {editingAgent && (
                    <div className="border-t border-border pt-4 mt-4" data-testid="agent-id-documents-section">
                      <Label className="text-sm font-medium mb-2 block">ID Documents</Label>
                      <DocumentsSection agentId={editingAgent.agent_id} initialFolderName="ID Documents" />
                    </div>
                  )}

                  {/* Workers — non-agent registered users (SSO) assigned under this agent */}
                  {editingAgent && (() => {
                    const isWorkerOf = (a) => a.parent_agent_id === editingAgent.agent_id && a.is_agent === false;
                    const workers = agents.filter(isWorkerOf);
                    return (
                    <div className="border-t border-border pt-4 mt-4" data-testid="agent-workers-section">
                      <Label className="text-sm font-medium mb-2 block">
                        Workers ({workers.length})
                      </Label>
                      <p className="text-xs text-muted-foreground mb-3">
                        Workers are SSO-registered users (not Agent accounts) assigned under this agent. New
                        sign-ups default under the Master Agent — reassign them here.
                      </p>
                      {workers.length === 0 ? (
                        <p className="text-sm text-muted-foreground" data-testid="agent-workers-empty">
                          No workers assigned to this agent yet.
                        </p>
                      ) : (
                        <div className="space-y-3">
                          {workers
                            .map((worker) => (
                              <div
                                key={worker.agent_id}
                                className="border border-border p-3 space-y-2"
                                data-testid={`worker-row-${worker.agent_id}`}
                              >
                                <div className="flex items-center justify-between gap-2">
                                  <div>
                                    <p className="font-medium text-sm">{worker.contact_name || worker.name}</p>
                                    <p className="text-xs text-muted-foreground">{worker.email}</p>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    {savingWorkerId === worker.agent_id && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
                                    <Switch
                                      checked={worker.active !== false}
                                      onCheckedChange={(checked) => handleWorkerUpdate(worker.agent_id, { active: checked })}
                                      data-testid={`worker-active-switch-${worker.agent_id}`}
                                    />
                                    <span className="text-xs text-muted-foreground">{worker.active !== false ? "Active" : "Inactive"}</span>
                                  </div>
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                  <Input
                                    defaultValue={worker.phone || ""}
                                    placeholder="Telephone"
                                    className="rounded-none h-8 text-sm"
                                    onBlur={(e) => e.target.value !== (worker.phone || "") && handleWorkerUpdate(worker.agent_id, { phone: e.target.value })}
                                    data-testid={`worker-phone-${worker.agent_id}`}
                                  />
                                  <Input
                                    defaultValue={worker.position || ""}
                                    placeholder="Position (e.g. Sales Negotiator)"
                                    className="rounded-none h-8 text-sm"
                                    onBlur={(e) => e.target.value !== (worker.position || "") && handleWorkerUpdate(worker.agent_id, { position: e.target.value })}
                                    data-testid={`worker-position-${worker.agent_id}`}
                                  />
                                  <Input
                                    defaultValue={worker.address || ""}
                                    placeholder="Address"
                                    className="rounded-none h-8 text-sm"
                                    onBlur={(e) => e.target.value !== (worker.address || "") && handleWorkerUpdate(worker.agent_id, { address: e.target.value })}
                                    data-testid={`worker-address-${worker.agent_id}`}
                                  />
                                  <Input
                                    defaultValue={worker.commission || "0"}
                                    placeholder="Commission/fee"
                                    className="rounded-none h-8 text-sm"
                                    onBlur={(e) => e.target.value !== (worker.commission || "0") && handleWorkerUpdate(worker.agent_id, { commission: e.target.value })}
                                    data-testid={`worker-commission-${worker.agent_id}`}
                                  />
                                </div>
                                <div className="space-y-1">
                                  <Label className="text-xs text-muted-foreground">Parent Agent</Label>
                                  <Select
                                    value={worker.parent_agent_id || ""}
                                    onValueChange={(value) => handleWorkerUpdate(worker.agent_id, { parent_agent_id: value })}
                                  >
                                    <SelectTrigger className="rounded-none h-8 text-sm" data-testid={`worker-parent-select-${worker.agent_id}`}>
                                      <SelectValue placeholder="Select parent agent" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {agents
                                        .filter((a) => a.agent_id !== worker.agent_id && a.is_agent !== false)
                                        .map((a) => (
                                          <SelectItem key={a.agent_id} value={a.agent_id}>
                                            {a.name}{a.is_master_agent ? " (Master)" : ""}
                                          </SelectItem>
                                        ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                              </div>
                            ))}
                        </div>
                      )}
                    </div>
                    );
                  })()}
                </div>
                
                <DialogFooter className="flex items-center justify-between border-t border-border pt-4 sticky bottom-0 bg-background z-10 -mx-6 px-6 pb-0">
                  <div className="flex items-center gap-2 text-sm">
                    {editingAgent && (
                      <>
                        {agentAutoSaving ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                            <span className="text-muted-foreground">Saving...</span>
                          </>
                        ) : agentHasChanges ? (
                          <span className="text-amber-600">Unsaved changes</span>
                        ) : agentLastSaved ? (
                          <span className="text-green-600">All changes saved</span>
                        ) : (
                          <span className="text-muted-foreground">Auto-save enabled</span>
                        )}
                      </>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setShowAgentDialog(false)} className="rounded-none">
                      {editingAgent ? 'Close' : 'Cancel'}
                    </Button>
                    {!editingAgent && (
                      <Button
                        onClick={handleSaveAgent}
                        disabled={agentFormLoading || !agentForm.name}
                        data-testid="agent-create-save-btn"
                        className="bg-primary text-white hover:bg-primary/90 rounded-none"
                      >
                        {agentFormLoading ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Creating...
                          </>
                        ) : (
                          "Create Agent"
                        )}
                      </Button>
                    )}
                  </div>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Delete Agent Confirmation */}
            <Dialog open={!!deletingAgent} onOpenChange={() => setDeletingAgent(null)}>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>Delete Agent</DialogTitle>
                </DialogHeader>
                <p className="text-muted-foreground">
                  Are you sure you want to delete <strong>{deletingAgent?.name}</strong>? 
                  This will unlink the agent from {deletingAgent?.properties_count || 0} properties.
                </p>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setDeletingAgent(null)} className="rounded-none">
                    Cancel
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={() => handleDeleteAgent(deletingAgent?.agent_id)}
                    className="rounded-none"
                    data-testid="agent-delete-confirm-btn"
                  >
                    Delete
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Agent Documents Dialog — master admin deep-link into any
                agent's library with full CRUD rights (backend authorises
                via `agent_id` query param). */}
            <Dialog open={!!documentsAgent} onOpenChange={(v) => !v && setDocumentsAgent(null)}>
              <DialogContent className="sm:max-w-5xl max-h-[90vh] overflow-y-auto slh-dialog-fixed-90">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <FolderOpen className="h-5 w-5" />
                    Documents — {documentsAgent?.name}
                  </DialogTitle>
                </DialogHeader>
                {documentsAgent && (
                  <DocumentsSection agentId={documentsAgent.agent_id} />
                )}
                <DialogFooter>
                  <Button variant="outline" onClick={() => setDocumentsAgent(null)} className="rounded-none">
                    Close
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Valuation Account Documents Dialog — reuses the same
                DocumentsSection as Agent Documents above, scoped to the
                tenant's synthetic "owner:<email>" id (see
                `_resolve_agent_scope` in routes/agent_documents.py). */}
            <Dialog open={!!tenantDocsDialog} onOpenChange={(v) => !v && setTenantDocsDialog(null)}>
              <DialogContent className="sm:max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="valuation-account-docs-dialog">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <FolderOpen className="h-5 w-5" />
                    Documents — {tenantDocsDialog?.name || tenantDocsDialog?.email}
                  </DialogTitle>
                </DialogHeader>
                {tenantDocsDialog && (
                  <DocumentsSection agentId={`owner:${tenantDocsDialog.email}`} />
                )}
                <DialogFooter>
                  <Button variant="outline" onClick={() => setTenantDocsDialog(null)} className="rounded-none" data-testid="valuation-account-docs-close-btn">
                    Close
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Agent Properties Dialog */}
            <Dialog open={!!showAgentPropertiesDialog} onOpenChange={() => setShowAgentPropertiesDialog(null)}>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>Linked Properties - {showAgentPropertiesDialog?.name}</DialogTitle>
                </DialogHeader>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {showAgentPropertiesDialog?.linked_properties?.map((prop) => (
                    <div key={prop.property_id} className="flex items-center justify-between p-2 border border-border">
                      <div>
                        <p className="font-medium text-sm">{prop.title}</p>
                        <p className="text-xs text-muted-foreground">{prop.display_address}</p>
                      </div>
                      <button
                        onClick={() => {
                          handleUnlinkAgentFromProperty(showAgentPropertiesDialog.agent_id, prop.property_id);
                          setShowAgentPropertiesDialog(null);
                        }}
                        className="text-destructive hover:bg-destructive/10 p-2 rounded"
                        title="Unlink property"
                      >
                        <Unlink className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowAgentPropertiesDialog(null)} className="rounded-none">
                    Close
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        )}

        {activeTab === "seo" && (
          <SeoTab />
        )}

        {activeTab === "styles" && (
          <div className="space-y-8">
            {/* Auto-save Status Bar */}
            <div className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm border-b border-border -mx-6 px-6 py-4 flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="font-serif text-2xl font-medium">Site Styles</h2>
                  <p className="text-muted-foreground text-sm">Customize colors and appearance across your entire site</p>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2 text-sm" data-testid="styles-autosave-status">
                    {stylesAutoSaving ? (
                      <span className="flex items-center gap-1.5 text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span>Saving...</span>
                      </span>
                    ) : stylesHasChanges ? (
                      <span className="flex items-center gap-1.5 text-amber-600">
                        <CloudOff className="h-4 w-4" />
                        <span>Unsaved</span>
                      </span>
                    ) : stylesLastSaved ? (
                      <span className="flex items-center gap-1.5 text-green-600">
                        <Cloud className="h-4 w-4" />
                        <span>Saved</span>
                      </span>
                    ) : (
                      <span className="flex items-center gap-1.5 text-muted-foreground">
                        <Cloud className="h-4 w-4" />
                        <span>Auto-save on</span>
                      </span>
                    )}
                  </div>
                  <Button
                    onClick={handleSaveStyles}
                    disabled={stylesAutoSaving || !stylesHasChanges}
                    className="rounded-none"
                    data-testid="save-styles-btn"
                  >
                    {stylesAutoSaving ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Save Styles
                      </>
                    )}
                  </Button>
                </div>
              </div>
              
              {/* Sticky Mini Navigation */}
              <div className="flex flex-wrap gap-2">
                {[
                  { id: 'hero-section', label: 'Hero Section' },
                  { id: 'global-colors', label: 'Global Colors' },
                  { id: 'navigation', label: 'Navigation' },
                  { id: 'property-cards', label: 'Property Cards' },
                  { id: 'header-footer', label: 'Header & Footer' },
                  { id: 'buttons-ctas', label: 'Buttons & CTAs' }
                ].map((section) => (
                  <button
                    key={section.id}
                    onClick={() => {
                      const element = document.getElementById(`styles-${section.id}`);
                      if (element) {
                        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }
                    }}
                    className="px-3 py-1.5 text-sm font-medium rounded-full border border-border hover:bg-primary hover:text-white hover:border-primary transition-colors"
                    data-testid={`styles-nav-${section.id}`}
                  >
                    {section.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Hero Section Settings */}
            <div id="styles-hero-section" className="bg-white border border-border overflow-hidden scroll-mt-36">
              <div className="bg-white border-b border-border px-6 py-3 flex items-center justify-between">
                <h3 className="font-serif text-lg font-medium">Hero Section</h3>
              </div>
              
              <div className="p-6 space-y-6">
                {/* Hero Text Content */}
                <div className="space-y-4">
                  <Label className="text-sm font-medium">Hero Text Content</Label>
                  <div className="space-y-3">
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Title</Label>
                      <Input
                        value={siteStyles.heroTitle || "Find Your Dream Home"}
                        onChange={(e) => updateStyle('heroTitle', e.target.value)}
                        placeholder="Main headline"
                        className="rounded-none"
                        data-testid="hero-title-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Subtitle</Label>
                      <Input
                        value={siteStyles.heroSubtitle || "Welcome to SellLocalHomes"}
                        onChange={(e) => updateStyle('heroSubtitle', e.target.value)}
                        placeholder="Tagline or subtitle"
                        className="rounded-none"
                        data-testid="hero-subtitle-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Description</Label>
                      <Textarea
                        value={siteStyles.heroDescription || "Discover exceptional properties in prime locations. Your perfect home is waiting."}
                        onChange={(e) => updateStyle('heroDescription', e.target.value)}
                        placeholder="Supporting description text"
                        className="rounded-none min-h-20"
                        data-testid="hero-description-input"
                      />
                    </div>
                  </div>
                </div>

                {/* Hero Text Style */}
                <div className="space-y-4 border-t border-border pt-4">
                  <Label className="text-sm font-medium">Text Style</Label>
                  
                  {/* Text Alignment */}
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Text Alignment</Label>
                    <div className="flex gap-2">
                      {['left', 'center', 'right'].map((align) => (
                        <button
                          key={align}
                          type="button"
                          onClick={() => updateStyle('heroTextAlign', align)}
                          className={`flex-1 py-2 px-4 border text-sm font-medium capitalize transition-colors ${
                            (siteStyles.heroTextAlign || 'left') === align
                              ? 'bg-primary text-white border-primary'
                              : 'bg-white text-foreground border-border hover:bg-muted'
                          }`}
                          data-testid={`hero-align-${align}`}
                        >
                          {align}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Font Sizes */}
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label className="text-xs text-muted-foreground">Title Size</Label>
                        <span className="text-xs font-mono bg-muted px-1.5 py-0.5 rounded">
                          {siteStyles.heroTitleSize || 48}px
                        </span>
                      </div>
                      <input
                        type="range"
                        min="32"
                        max="72"
                        value={siteStyles.heroTitleSize || 48}
                        onChange={(e) => updateStyle('heroTitleSize', parseInt(e.target.value))}
                        className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
                        data-testid="hero-title-size-slider"
                      />
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label className="text-xs text-muted-foreground">Subtitle Size</Label>
                        <span className="text-xs font-mono bg-muted px-1.5 py-0.5 rounded">
                          {siteStyles.heroSubtitleSize || 14}px
                        </span>
                      </div>
                      <input
                        type="range"
                        min="10"
                        max="24"
                        value={siteStyles.heroSubtitleSize || 14}
                        onChange={(e) => updateStyle('heroSubtitleSize', parseInt(e.target.value))}
                        className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
                        data-testid="hero-subtitle-size-slider"
                      />
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label className="text-xs text-muted-foreground">Description Size</Label>
                        <span className="text-xs font-mono bg-muted px-1.5 py-0.5 rounded">
                          {siteStyles.heroDescriptionSize || 18}px
                        </span>
                      </div>
                      <input
                        type="range"
                        min="14"
                        max="28"
                        value={siteStyles.heroDescriptionSize || 18}
                        onChange={(e) => updateStyle('heroDescriptionSize', parseInt(e.target.value))}
                        className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
                        data-testid="hero-description-size-slider"
                      />
                    </div>
                  </div>

                  {/* Font Colour */}
                  <ColorPicker
                    label="Text Colour"
                    value={siteStyles.heroFontHex || "#ffffff"}
                    onChange={(color) => updateStyle('heroFontHex', color)}
                    description="Colour for all hero text"
                    testId="style-hero-font"
                  />
                </div>

                {/* Hero Image Settings */}
                <div className="space-y-4 border-t border-border pt-4">
                  <Label className="text-sm font-medium">Image & Overlay</Label>

                  {/* Hero Image Upload */}
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Hero Image</Label>
                    {siteStyles.heroImage && (
                      <div className="relative h-24 rounded overflow-hidden border border-border mb-2">
                        <img src={resolveImageUrl(siteStyles.heroImage)} alt="Hero" className="w-full h-full object-cover" />
                        <button
                          onClick={() => updateStyle('heroImage', '')}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-600"
                        >×</button>
                      </div>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      className="text-xs w-full"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        const formData = new FormData();
                        formData.append('file', file);
                        try {
                          const res = await axios.post(`${API}/admin/upload`, formData, {
                            withCredentials: true,
                            headers: { 'Content-Type': 'multipart/form-data' }
                          });
                          if (res.data?.url) {
                            updateStyle('heroImage', res.data.url);
                            toast.success('Hero image uploaded');
                          }
                        } catch (err) {
                          toast.error('Failed to upload hero image');
                        }
                      }}
                      data-testid="hero-image-upload"
                    />
                    <p className="text-xs text-muted-foreground">Upload a custom hero image or leave empty for default</p>
                  </div>
                  
                  {/* Hero Height */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs text-muted-foreground">Hero Height</Label>
                      <span className="text-sm font-mono bg-muted px-2 py-1 rounded">
                        {siteStyles.heroHeight || 80}vh
                      </span>
                    </div>
                    <input
                      type="range"
                      min="5"
                      max="100"
                      value={siteStyles.heroHeight || 80}
                      onChange={(e) => updateStyle('heroHeight', parseInt(e.target.value))}
                      className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
                      data-testid="hero-height-slider"
                    />
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>5vh (Minimal)</span>
                      <span>100vh (Full screen)</span>
                    </div>
                  </div>

                  {/* Hero Image Transparency */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs text-muted-foreground">Image Transparency</Label>
                      <span className="text-sm font-mono bg-muted px-2 py-1 rounded">
                        {siteStyles.heroImageOpacity || 100}%
                      </span>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={siteStyles.heroImageOpacity || 100}
                      onChange={(e) => updateStyle('heroImageOpacity', parseInt(e.target.value))}
                      className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
                      data-testid="hero-image-opacity-slider"
                    />
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>0% (Invisible)</span>
                      <span>100% (Full visible)</span>
                    </div>
                  </div>

                  {/* Hero Text Overlay */}
                  <div className="grid grid-cols-2 gap-4">
                    <ColorPicker
                      label="Overlay Colour"
                      value={siteStyles.heroTextOverlayColor || "#000000"}
                      onChange={(color) => updateStyle('heroTextOverlayColor', color)}
                      description="Background overlay"
                      testId="style-hero-overlay-color"
                    />
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label className="text-xs text-muted-foreground">Overlay Opacity</Label>
                        <span className="text-xs font-mono bg-muted px-1.5 py-0.5 rounded">
                          {siteStyles.heroTextOverlayOpacity || 0}%
                        </span>
                      </div>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={siteStyles.heroTextOverlayOpacity || 0}
                        onChange={(e) => updateStyle('heroTextOverlayOpacity', parseInt(e.target.value))}
                        className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
                        data-testid="hero-text-overlay-opacity-slider"
                      />
                    </div>
                  </div>
                </div>
                
                {/* Preview */}
                <div className="border-t border-border pt-4">
                  <Label className="text-xs text-muted-foreground mb-3 block">Preview</Label>
                  <div 
                    className="relative h-48 rounded overflow-hidden bg-gray-900"
                  >
                    <img
                      src={siteStyles.heroImage ? resolveImageUrl(siteStyles.heroImage) : "https://images.unsplash.com/photo-1757264119016-7e6b568b810d?crop=entropy&cs=srgb&fm=jpg&q=85&w=400"}
                      alt="Hero preview"
                      className="w-full h-full object-cover"
                      style={{ opacity: (siteStyles.heroImageOpacity || 100) / 100 }}
                    />
                    <div 
                      className="absolute inset-0"
                      style={{ 
                        backgroundColor: siteStyles.heroTextOverlayColor || "#000000",
                        opacity: (siteStyles.heroTextOverlayOpacity || 0) / 100 
                      }}
                    />
                    <div 
                      className="absolute inset-0 flex flex-col justify-center p-6"
                      style={{ textAlign: siteStyles.heroTextAlign || 'left' }}
                    >
                      <h2 
                        className="font-serif font-medium drop-shadow-lg mb-2"
                        style={{ 
                          color: siteStyles.heroFontHex || "#ffffff",
                          fontSize: `${Math.min((siteStyles.heroTitleSize || 48) * 0.5, 28)}px`
                        }}
                      >
                        {siteStyles.heroTitle || "Find Your Dream Home"}
                      </h2>
                      <span 
                        className="uppercase tracking-widest font-semibold drop-shadow mb-2"
                        style={{ 
                          color: siteStyles.heroFontHex || "#ffffff",
                          fontSize: `${Math.min((siteStyles.heroSubtitleSize || 14) * 0.8, 12)}px`,
                          opacity: 0.8
                        }}
                      >
                        {siteStyles.heroSubtitle || "Welcome to SellLocalHomes"}
                      </span>
                      <p 
                        className="drop-shadow max-w-md"
                        style={{ 
                          color: siteStyles.heroFontHex || "#ffffff",
                          fontSize: `${Math.min((siteStyles.heroDescriptionSize || 18) * 0.7, 14)}px`,
                          opacity: 0.8,
                          margin: siteStyles.heroTextAlign === 'center' ? '0 auto' : undefined
                        }}
                      >
                        {siteStyles.heroDescription || "Discover exceptional properties..."}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Global Colors */}
            <div id="styles-global-colors" className="bg-white border border-border overflow-hidden scroll-mt-36">
              <div className="bg-white border-b border-border px-6 py-3 flex items-center justify-between">
                <h3 className="font-serif text-lg font-medium">Global Colors</h3>
              </div>
              
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <ColorPicker
                  label="Primary Color"
                  value={siteStyles.primaryHex || "#2f4f4f"}
                  onChange={(color) => updateStyle('primaryHex', color)}
                  description="Buttons, links, accents"
                  testId="style-primary"
                />

                <ColorPicker
                  label="Site Background"
                  value={siteStyles.backgroundHex || "#f5f2ed"}
                  onChange={(color) => updateStyle('backgroundHex', color)}
                  description="Page background color"
                  testId="style-background"
                />

                <ColorPicker
                  label="Accent Color"
                  value={siteStyles.accentHex || "#e07b5d"}
                  onChange={(color) => updateStyle('accentHex', color)}
                  description="Highlights, badges"
                  testId="style-accent"
                />

                <ColorPicker
                  label="Text Color"
                  value={siteStyles.textHex || "#1a1a1a"}
                  onChange={(color) => updateStyle('textHex', color)}
                  description="Main text color"
                  testId="style-text"
                />

                <ColorPicker
                  label="Muted Text Color"
                  value={siteStyles.mutedTextHex || "#666666"}
                  onChange={(color) => updateStyle('mutedTextHex', color)}
                  description="Secondary text, descriptions"
                  testId="style-muted-text"
                />

                <ColorPicker
                  label="Border Color"
                  value={siteStyles.borderHex || "#e5e0d8"}
                  onChange={(color) => updateStyle('borderHex', color)}
                  description="Borders, dividers"
                  testId="style-border"
                />
              </div>
            </div>
          </div>

            {/* Navigation Settings */}
            <div id="styles-navigation" className="bg-white border border-border overflow-hidden scroll-mt-36">
              <div className="bg-white border-b border-border px-6 py-3 flex items-center justify-between">
                <h3 className="font-serif text-lg font-medium">Navigation</h3>
              </div>
              
              <div className="p-6 space-y-6">
                {/* Navigation Content */}
                <div className="space-y-4">
                  <Label className="text-sm font-medium">Navigation Content</Label>
                  
                  {/* Logo Text */}
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Logo Text</Label>
                    <Input
                      value={siteStyles.navLogoText || "SellLocalHomes"}
                      onChange={(e) => updateStyle('navLogoText', e.target.value)}
                      placeholder="Site name"
                      className="rounded-none"
                      data-testid="nav-logo-text"
                    />
                  </div>

                  {/* Navigation Links */}
                  <div className="space-y-3">
                    <Label className="text-xs text-muted-foreground">Menu Links</Label>
                    <div className="space-y-2">
                      {(siteStyles.navLinks || [
                        { name: "Home", path: "/", enabled: true },
                        { name: "Properties", path: "/properties", enabled: true },
                        { name: "Join Us", path: "/join-us", enabled: true },
                        { name: "Contact", path: "/contact", enabled: true }
                      ]).map((link, index) => (
                        <div key={index} className="flex items-center gap-3 p-2 bg-muted/30 border border-border">
                          <input
                            type="checkbox"
                            checked={link.enabled}
                            onChange={(e) => {
                              const newLinks = [...(siteStyles.navLinks || [])];
                              newLinks[index] = { ...newLinks[index], enabled: e.target.checked };
                              updateStyle('navLinks', newLinks);
                            }}
                            className="h-4 w-4 rounded border-gray-300"
                            data-testid={`nav-link-toggle-${index}`}
                          />
                          <Input
                            value={link.name}
                            onChange={(e) => {
                              const newLinks = [...(siteStyles.navLinks || [])];
                              newLinks[index] = { ...newLinks[index], name: e.target.value };
                              updateStyle('navLinks', newLinks);
                            }}
                            className="rounded-none flex-1 h-8 text-sm"
                            placeholder="Link name"
                            data-testid={`nav-link-name-${index}`}
                          />
                          <Input
                            value={link.path}
                            onChange={(e) => {
                              const newLinks = [...(siteStyles.navLinks || [])];
                              newLinks[index] = { ...newLinks[index], path: e.target.value };
                              updateStyle('navLinks', newLinks);
                            }}
                            className="rounded-none w-32 h-8 text-sm font-mono"
                            placeholder="/path"
                            data-testid={`nav-link-path-${index}`}
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* CTA Button */}
                  <div className="space-y-2 border-t border-border pt-4">
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={siteStyles.navCtaEnabled !== false}
                        onChange={(e) => updateStyle('navCtaEnabled', e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300"
                        data-testid="nav-cta-toggle"
                      />
                      <Label className="text-xs text-muted-foreground">Show CTA Button</Label>
                    </div>
                    <Input
                      value={siteStyles.navCtaText || "Request Viewing"}
                      onChange={(e) => updateStyle('navCtaText', e.target.value)}
                      placeholder="Button text"
                      className="rounded-none"
                      disabled={siteStyles.navCtaEnabled === false}
                      data-testid="nav-cta-text"
                    />
                  </div>
                </div>

                {/* Navigation Style */}
                <div className="space-y-4 border-t border-border pt-4">
                  <Label className="text-sm font-medium">Navigation Style</Label>
                  
                  {/* Font Size Controls */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <Label className="text-xs text-muted-foreground">Logo Font Size</Label>
                        <span className="text-sm font-mono bg-muted px-2 py-1 rounded">
                          {siteStyles.navLogoFontSize || 20}px
                        </span>
                      </div>
                      <input
                        type="range"
                        min="14"
                        max="32"
                        value={siteStyles.navLogoFontSize || 20}
                        onChange={(e) => updateStyle('navLogoFontSize', parseInt(e.target.value))}
                        className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
                        data-testid="nav-logo-font-size-slider"
                      />
                      <div className="flex justify-between text-xs text-muted-foreground">
                        <span>14px</span>
                        <span>32px</span>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <Label className="text-xs text-muted-foreground">Menu Font Size</Label>
                        <span className="text-sm font-mono bg-muted px-2 py-1 rounded">
                          {siteStyles.navFontSize || 14}px
                        </span>
                      </div>
                      <input
                        type="range"
                        min="12"
                        max="20"
                        value={siteStyles.navFontSize || 14}
                        onChange={(e) => updateStyle('navFontSize', parseInt(e.target.value))}
                        className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
                        data-testid="nav-font-size-slider"
                      />
                      <div className="flex justify-between text-xs text-muted-foreground">
                        <span>12px</span>
                        <span>20px</span>
                      </div>
                    </div>
                  </div>

                  {/* Colour Controls */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pt-2">
                    <ColorPicker
                      label="Background"
                      value={siteStyles.navBgHex || "#ffffff"}
                      onChange={(color) => updateStyle('navBgHex', color)}
                      description="Nav bar background"
                      testId="style-nav-bg"
                    />
                    <ColorPicker
                      label="Text Colour"
                      value={siteStyles.navTextHex || "#1a1a1a"}
                      onChange={(color) => updateStyle('navTextHex', color)}
                      description="Link text colour"
                      testId="style-nav-text"
                    />
                    <ColorPicker
                      label="Hover Colour"
                      value={siteStyles.navHoverHex || "#2f4f4f"}
                      onChange={(color) => updateStyle('navHoverHex', color)}
                      description="Link hover colour"
                      testId="style-nav-hover"
                    />
                    <ColorPicker
                      label="Button Background"
                      value={siteStyles.navButtonBgHex || "#2f4f4f"}
                      onChange={(color) => updateStyle('navButtonBgHex', color)}
                      description="CTA button background"
                      testId="style-nav-btn-bg"
                    />
                    <ColorPicker
                      label="Button Text"
                      value={siteStyles.navButtonTextHex || "#ffffff"}
                      onChange={(color) => updateStyle('navButtonTextHex', color)}
                      description="CTA button text"
                      testId="style-nav-btn-text"
                    />
                  </div>
                </div>

                {/* Navigation Preview */}
                <div className="border-t border-border pt-4">
                  <Label className="text-xs text-muted-foreground mb-3 block">Preview</Label>
                  <div 
                    className="p-4 rounded border border-border"
                    style={{ backgroundColor: siteStyles.navBgHex || "#ffffff" }}
                  >
                    {/* Top Row: Logo */}
                    <div className="flex items-center gap-3 pb-3 border-b border-border/30">
                      <div className="w-10 h-10 bg-gray-300 rounded"></div>
                      <span 
                        className="font-serif font-medium"
                        style={{ 
                          color: siteStyles.navTextHex || "#1a1a1a",
                          fontSize: `${siteStyles.navLogoFontSize || 20}px`
                        }}
                      >
                        {siteStyles.navLogoText || "SellLocalHomes"}
                      </span>
                    </div>
                    {/* Bottom Row: Nav Links */}
                    <div className="flex items-center justify-center gap-6 pt-3">
                      {(siteStyles.navLinks || []).filter(l => l.enabled).map((link, i) => (
                        <span 
                          key={i}
                          className="font-medium cursor-pointer transition-colors"
                          style={{ 
                            color: siteStyles.navTextHex || "#1a1a1a",
                            fontSize: `${siteStyles.navFontSize || 14}px`
                          }}
                          onMouseEnter={(e) => e.target.style.color = siteStyles.navHoverHex || "#2f4f4f"}
                          onMouseLeave={(e) => e.target.style.color = siteStyles.navTextHex || "#1a1a1a"}
                        >
                          {link.name}
                        </span>
                      ))}
                      {siteStyles.navCtaEnabled !== false && (
                        <button
                          className="px-4 py-2 font-medium rounded-none"
                          style={{ 
                            backgroundColor: siteStyles.navButtonBgHex || "#2f4f4f",
                            color: siteStyles.navButtonTextHex || "#ffffff",
                            fontSize: `${siteStyles.navFontSize || 14}px`
                          }}
                        >
                          {siteStyles.navCtaText || "Request Viewing"}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Card Styles */}
            <div id="styles-property-cards" className="bg-white border border-border overflow-hidden scroll-mt-36">
              <div className="bg-white border-b border-border px-6 py-3 flex items-center justify-between">
                <h3 className="font-serif text-lg font-medium">Property Card Styles</h3>
              </div>
              
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <ColorPicker
                    label="Card Background Color"
                    value={siteStyles.cardBackground || "#e8f0e8"}
                    onChange={(color) => updateStyle('cardBackground', color)}
                    description="Background color for property cards"
                    testId="style-card-bg"
                  />

                  <ColorPicker
                    label="Card Border Color"
                    value={siteStyles.cardBorder || "#c5d9c5"}
                    onChange={(color) => updateStyle('cardBorder', color)}
                    description="Border color for property cards"
                    testId="style-card-border"
                  />
              </div>
              </div>

              {/* Card Preview */}
              <div className="mt-4 p-4 border border-border rounded bg-muted/30">
                <p className="text-sm text-muted-foreground mb-3">Card Preview:</p>
                <div 
                  className="p-4 border rounded-sm max-w-xs"
                  style={{ 
                    backgroundColor: siteStyles.cardBackground, 
                    borderColor: siteStyles.cardBorder 
                  }}
                >
                  <div className="h-20 bg-gray-300 rounded mb-2"></div>
                  <p className="font-medium">Sample Property</p>
                  <p className="text-sm text-muted-foreground">$500,000 • 3 beds • 2 baths</p>
                </div>
              </div>
            </div>

            {/* Header & Footer */}
            <div id="styles-header-footer" className="bg-white border border-border overflow-hidden scroll-mt-36">
              <div className="bg-white border-b border-border px-6 py-3 flex items-center justify-between">
                <h3 className="font-serif text-lg font-medium">Header & Footer</h3>
              </div>
              
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <ColorPicker
                    label="Header Background"
                    value={siteStyles.headerBgHex || "#ffffff"}
                    onChange={(color) => updateStyle('headerBgHex', color)}
                    description="Navigation bar background"
                    testId="style-header-bg"
                  />

                  <ColorPicker
                    label="Footer Background"
                    value={siteStyles.footerBgHex || "#2f4f4f"}
                    onChange={(color) => updateStyle('footerBgHex', color)}
                    description="Footer section background"
                    testId="style-footer-bg"
                  />
                </div>
              </div>
            </div>

            {/* Buttons */}
            <div id="styles-buttons-ctas" className="bg-white border border-border overflow-hidden scroll-mt-36">
              <div className="bg-white border-b border-border px-6 py-3 flex items-center justify-between">
                <h3 className="font-serif text-lg font-medium">Buttons & CTAs</h3>
              </div>
              
              <div className="p-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <ColorPicker
                    label="Button Background"
                    value={siteStyles.buttonBgHex || "#2f4f4f"}
                    onChange={(color) => updateStyle('buttonBgHex', color)}
                    description="Primary button color"
                    testId="style-button-bg"
                  />

                  <ColorPicker
                    label="Button Text Color"
                    value={siteStyles.buttonTextHex || "#ffffff"}
                    onChange={(color) => updateStyle('buttonTextHex', color)}
                    description="Text on buttons"
                    testId="style-button-text"
                  />
                </div>

                {/* Button Preview */}
                <div className="p-4 border border-border rounded bg-muted/30">
                  <p className="text-sm text-muted-foreground mb-3">Button Preview:</p>
                  <button 
                    className="px-6 py-2 rounded-sm font-medium"
                    style={{ 
                      backgroundColor: siteStyles.buttonBgHex || "#2f4f4f", 
                      color: siteStyles.buttonTextHex || "#ffffff"
                    }}
                  >
                    Sample Button
                  </button>
                </div>
              </div>
            </div>

            {/* Full Site Preview */}
            <div className="bg-white border border-border p-6 space-y-6">
              <h3 className="font-serif text-xl font-medium border-b border-border pb-3">
                Full Site Preview
              </h3>
              
              <div 
                className="border rounded overflow-hidden"
                style={{ backgroundColor: siteStyles.backgroundHex || "#f5f2ed" }}
              >
                {/* Mini Header */}
                <div 
                  className="px-4 py-3 border-b flex items-center justify-between"
                  style={{ 
                    backgroundColor: siteStyles.headerBgHex || "#ffffff",
                    borderColor: siteStyles.borderHex || "#e5e0d8"
                  }}
                >
                  <span className="font-medium" style={{ color: siteStyles.textHex || "#1a1a1a" }}>SellLocalHomes</span>
                  <div className="flex gap-2">
                    <span className="text-sm" style={{ color: siteStyles.mutedTextHex || "#666666" }}>Properties</span>
                    <span className="text-sm" style={{ color: siteStyles.mutedTextHex || "#666666" }}>Contact</span>
                  </div>
                </div>
                
                {/* Mini Content */}
                <div className="p-4">
                  <div 
                    className="p-3 border rounded-sm mb-3"
                    style={{ 
                      backgroundColor: siteStyles.cardBackground || "#e8f0e8", 
                      borderColor: siteStyles.cardBorder || "#c5d9c5"
                    }}
                  >
                    <div className="h-12 bg-gray-300 rounded mb-2"></div>
                    <p className="text-sm font-medium" style={{ color: siteStyles.textHex || "#1a1a1a" }}>Property Title</p>
                    <p className="text-xs" style={{ color: siteStyles.mutedTextHex || "#666666" }}>$500,000</p>
                  </div>
                  <button 
                    className="px-4 py-1 text-sm rounded-sm"
                    style={{ 
                      backgroundColor: siteStyles.buttonBgHex || "#2f4f4f", 
                      color: siteStyles.buttonTextHex || "#ffffff"
                    }}
                  >
                    View Details
                  </button>
                </div>
                
                {/* Mini Footer */}
                <div 
                  className="px-4 py-3"
                  style={{ backgroundColor: siteStyles.footerBgHex || "#2f4f4f" }}
                >
                  <span className="text-xs text-white/70">© 2026 SellLocalHomes</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Applications Tab */}
        {activeTab === "trading" && tradingSubTab === "applications" && (
          <div className="space-y-6" data-testid="applications-tab">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="font-serif text-2xl font-medium">Partner Applications</h2>
                <p className="text-muted-foreground mt-1">Review and manage agent partner applications</p>
              </div>
              <div className="flex gap-4">
                <div className="text-center px-4 py-2 bg-yellow-50 border border-yellow-200 rounded">
                  <p className="text-2xl font-bold text-yellow-600">{applicationStats.pending}</p>
                  <p className="text-xs text-yellow-700">Pending</p>
                </div>
                <div className="text-center px-4 py-2 bg-green-50 border border-green-200 rounded">
                  <p className="text-2xl font-bold text-green-600">{applicationStats.approved}</p>
                  <p className="text-xs text-green-700">Approved</p>
                </div>
                <div className="text-center px-4 py-2 bg-red-50 border border-red-200 rounded">
                  <p className="text-2xl font-bold text-red-600">{applicationStats.rejected}</p>
                  <p className="text-xs text-red-700">Rejected</p>
                </div>
              </div>
            </div>

            {applicationsLoading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : applications.length === 0 ? (
              <div className="text-center py-12 bg-muted/30 rounded-lg border border-border">
                <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="font-medium text-lg mb-2">No Applications Yet</h3>
                <p className="text-muted-foreground">When agents apply through the &quot;Become a Partner&quot; page, they&apos;ll appear here.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {applications.map((app) => (
                  <div 
                    key={app.application_id} 
                    className={`bg-white border rounded-lg p-6 ${
                      app.status === 'pending' ? 'border-yellow-300' : 
                      app.status === 'approved' ? 'border-green-300' : 'border-red-300'
                    }`}
                    data-testid={`application-${app.application_id}`}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-semibold text-lg">{app.agency_name}</h3>
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            app.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                            app.status === 'approved' ? 'bg-green-100 text-green-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {app.status.charAt(0).toUpperCase() + app.status.slice(1)}
                          </span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                          <div>
                            <p className="text-muted-foreground">Contact Name</p>
                            <p className="font-medium">{app.contact_name}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Email</p>
                            <p className="font-medium">{app.email}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Phone</p>
                            <p className="font-medium">{app.phone}</p>
                          </div>
                          {app.website && (
                            <div>
                              <p className="text-muted-foreground">Website</p>
                              <a href={app.website} target="_blank" rel="noopener noreferrer" className="font-medium text-primary hover:underline">
                                {app.website}
                              </a>
                            </div>
                          )}
                        </div>
                        {app.message && (
                          <div className="mt-4 p-3 bg-muted/30 rounded">
                            <p className="text-sm text-muted-foreground mb-1">Message</p>
                            <p className="text-sm">{app.message}</p>
                          </div>
                        )}
                        <p className="text-xs text-muted-foreground mt-4">
                          Applied: {new Date(app.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                      
                      <div className="flex flex-wrap gap-2 ml-4">
                        {app.status === 'pending' && (
                          <>
                            <Button
                              size="sm"
                              onClick={() => handleApproveApplication(app.application_id)}
                              disabled={processingApplication === app.application_id}
                              className="bg-green-600 hover:bg-green-700 text-white"
                              data-testid={`approve-${app.application_id}`}
                            >
                              {processingApplication === app.application_id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                "Approve"
                              )}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleRejectApplication(app.application_id)}
                              disabled={processingApplication === app.application_id}
                              className="border-red-300 text-red-600 hover:bg-red-50"
                              data-testid={`reject-${app.application_id}`}
                            >
                              Reject
                            </Button>
                          </>
                        )}
                        <Button size="sm" variant="outline" onClick={() => handleEditApplication(app)} className="rounded-none" data-testid={`edit-app-${app.application_id}`}>
                          Edit
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => handleDeleteApplication(app.application_id)} className="rounded-none border-red-200 text-red-500 hover:bg-red-50" data-testid={`delete-app-${app.application_id}`}>
                          Delete
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Property Dialog */}
      <MasterPropertyEditDialog
        showPropertyDialog={showPropertyDialog}
        setShowPropertyDialog={setShowPropertyDialog}
        editingProperty={editingProperty}
        cmsFieldSettings={cmsFieldSettings}
        sectionOrderIdx={sectionOrderIdx}
        propertyForm={propertyForm}
        setPropertyForm={setPropertyForm}
        propertyHasChanges={propertyHasChanges}
        setPropertyHasChanges={setPropertyHasChanges}
        handleFormChange={handleFormChange}
        scrapeUrl={scrapeUrl}
        setScrapeUrl={setScrapeUrl}
        scraping={scraping}
        handleScrape={handleScrape}
        scrapeAutoApply={scrapeAutoApply}
        setScrapeAutoApply={setScrapeAutoApply}
        scrapeResult={scrapeResult}
        setScrapeResult={setScrapeResult}
        applyScrapedData={applyScrapedData}
        handleGalleryUpload={handleGalleryUpload}
        generatingSeo={generatingSeo}
        handleGenerateSeoKeywords={handleGenerateSeoKeywords}
        agents={agents}
        handleSelectAgent={handleSelectAgent}
        showDbNames={showDbNames}
        aiSummarising={aiSummarising}
        handleAISummary={handleAISummary}
        aiRewriting={aiRewriting}
        handleAIRewrite={handleAIRewrite}
        handleAddressGeocode={handleAddressGeocode}
        handlePostcodeGeocode={handlePostcodeGeocode}
        formLoading={formLoading}
        handleSubmitProperty={handleSubmitProperty}
        handleDeleteProperty={handleDeleteProperty}
        propertyAutoSaving={propertyAutoSaving}
        propertyLastSaved={propertyLastSaved}
        propertyInitialLoad={propertyInitialLoad}
      />

      {/* Edit Application Dialog */}
      <Dialog open={showEditAppDialog} onOpenChange={setShowEditAppDialog}>
        <DialogContent className="sm:max-w-md rounded-none">
          <DialogHeader>
            <DialogTitle className="font-serif">Edit Application</DialogTitle>
            <DialogDescription>Update application details</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1">
              <Label className="text-xs">Agency Name</Label>
              <Input value={editAppForm.agency_name} onChange={(e) => setEditAppForm(p => ({ ...p, agency_name: e.target.value }))} className="rounded-none" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Contact Name</Label>
              <Input value={editAppForm.contact_name} onChange={(e) => setEditAppForm(p => ({ ...p, contact_name: e.target.value }))} className="rounded-none" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Email</Label>
              <Input value={editAppForm.email} onChange={(e) => setEditAppForm(p => ({ ...p, email: e.target.value }))} className="rounded-none" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Phone</Label>
              <Input value={editAppForm.phone} onChange={(e) => setEditAppForm(p => ({ ...p, phone: e.target.value }))} className="rounded-none" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Website</Label>
              <Input value={editAppForm.website} onChange={(e) => setEditAppForm(p => ({ ...p, website: e.target.value }))} className="rounded-none" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Message</Label>
              <Textarea value={editAppForm.message} onChange={(e) => setEditAppForm(p => ({ ...p, message: e.target.value }))} className="rounded-none" rows={3} />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setShowEditAppDialog(false)} className="rounded-none">Cancel</Button>
            <Button onClick={handleSaveApplication} className="rounded-none">Save Changes</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Rightmove per-property action modal (Push / Remove) */}
      <Dialog
        open={!!rightmoveAction}
        onOpenChange={(open) => { if (!open) setRightmoveAction(null); }}
      >
        <DialogContent className="sm:max-w-lg rounded-none" data-testid="rightmove-action-modal">
          <DialogHeader>
            <DialogTitle className="font-serif flex items-center gap-2">
              {rightmoveAction?.action === "remove" ? (
                <><CloudOff className="h-5 w-5 text-orange-600" /> Remove from Rightmove UAT</>
              ) : rightmoveAction?.action === "add_premium" ? (
                <><Crown className="h-5 w-5 text-amber-600" /> Add Premium Listing (Rightmove UAT)</>
              ) : rightmoveAction?.action === "add_featured" ? (
                <><Star className="h-5 w-5 text-yellow-500" /> Add Featured Property (Rightmove UAT)</>
              ) : (
                <><Cloud className="h-5 w-5 text-primary" /> Push to Rightmove UAT (SendProperty)</>
              )}
            </DialogTitle>
            <DialogDescription>
              {rightmoveAction?.property?.title || "Property"}
            </DialogDescription>
          </DialogHeader>

          {/* Pre-call option controls for Premium / Featured flows */}
          {rightmoveAction?.needsConfirm && rightmoveAction?.action === "add_premium" && (
            <div className="py-3 space-y-3 border border-amber-200 bg-amber-50 rounded p-3" data-testid="rightmove-premium-options">
              <p className="text-sm text-amber-900">
                Choose the Premium Listing display type and which platforms it should appear on.
              </p>
              <label className="flex items-center gap-2 text-sm cursor-pointer border-t border-amber-200 pt-2">
                <input
                  type="checkbox"
                  checked={rightmoveAction.options?.auto_send_first !== false}
                  onChange={(e) => setRightmoveAction((prev) => ({
                    ...prev,
                    options: { ...(prev.options || {}), auto_send_first: e.target.checked },
                  }))}
                  data-testid="premium-auto-send-checkbox"
                />
                <span>Also send property first <span className="text-amber-700">(recommended — avoids VAL_00289)</span></span>
              </label>
              <div className="space-y-1">
                <label className="text-xs font-medium text-amber-900 block">Display type</label>
                <select
                  value={rightmoveAction.options?.premium_display_type ?? 1}
                  onChange={(e) => setRightmoveAction((prev) => ({
                    ...prev,
                    options: { ...(prev.options || {}), premium_display_type: Number(e.target.value) },
                  }))}
                  className="w-full h-8 rounded border border-amber-300 bg-white px-2 text-sm"
                  data-testid="premium-display-type-select"
                >
                  <option value={1}>1 — Standard Premium Listing</option>
                  <option value={2}>2 — Premium Listing Plus</option>
                  <option value={3}>3 — Premium Display</option>
                </select>
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={rightmoveAction.options?.premium_web !== false}
                  onChange={(e) => setRightmoveAction((prev) => ({
                    ...prev,
                    options: { ...(prev.options || {}), premium_web: e.target.checked },
                  }))}
                  data-testid="premium-web-checkbox"
                />
                <span>Web (rightmove.co.uk)</span>
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={rightmoveAction.options?.premium_mobile !== false}
                  onChange={(e) => setRightmoveAction((prev) => ({
                    ...prev,
                    options: { ...(prev.options || {}), premium_mobile: e.target.checked },
                  }))}
                  data-testid="premium-mobile-checkbox"
                />
                <span>Mobile app</span>
              </label>
              <div className="space-y-1">
                <label className="text-xs font-medium text-amber-900 block">Resale stamp overlay</label>
                <select
                  value={rightmoveAction.options?.premium_resale_stamp_text ?? "1"}
                  onChange={(e) => setRightmoveAction((prev) => ({
                    ...prev,
                    options: { ...(prev.options || {}), premium_resale_stamp_text: e.target.value },
                  }))}
                  className="w-full h-8 rounded border border-amber-300 bg-white px-2 text-sm"
                  data-testid="premium-resale-stamp-select"
                >
                  <option value="1">1 — New Build</option>
                  <option value="4">4 — New Listing</option>
                  <option value="8">8 — Available</option>
                  <option value="9">9 — Reduced</option>
                </select>
                <p className="text-[10px] text-amber-800">
                  Mandatory per Rightmove. Empirically-verified codes shown.
                </p>
              </div>
            </div>
          )}

          {rightmoveAction?.needsConfirm && rightmoveAction?.action === "add_featured" && (
            <div className="py-3 space-y-3 border border-yellow-200 bg-yellow-50 rounded p-3" data-testid="rightmove-featured-options">
              <p className="text-sm text-yellow-900">
                Choose the featured slot. Requires a subscription with manual rotation enabled.
              </p>
              <label className="flex items-center gap-2 text-sm cursor-pointer border-b border-yellow-200 pb-2">
                <input
                  type="checkbox"
                  checked={rightmoveAction.options?.auto_send_first !== false}
                  onChange={(e) => setRightmoveAction((prev) => ({
                    ...prev,
                    options: { ...(prev.options || {}), auto_send_first: e.target.checked },
                  }))}
                  data-testid="featured-auto-send-checkbox"
                />
                <span>Also send property first <span className="text-yellow-800">(recommended — avoids VAL_00289)</span></span>
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="radio"
                  name="featured-type"
                  checked={rightmoveAction.options?.featured_type !== 2}
                  onChange={() => setRightmoveAction((prev) => ({
                    ...prev,
                    options: { ...(prev.options || {}), featured_type: 1 },
                  }))}
                  data-testid="featured-type-1"
                />
                <span>Featured Property</span>
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="radio"
                  name="featured-type"
                  checked={rightmoveAction.options?.featured_type === 2}
                  onChange={() => setRightmoveAction((prev) => ({
                    ...prev,
                    options: { ...(prev.options || {}), featured_type: 2 },
                  }))}
                  data-testid="featured-type-2"
                />
                <span>Featured Property of the Week</span>
              </label>
            </div>
          )}

          {rightmoveAction?.needsConfirm && (
            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => setRightmoveAction(null)}
                data-testid="rightmove-options-cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  const opts = rightmoveAction.options || {};
                  handleRightmoveAction(rightmoveAction.property, rightmoveAction.action, opts);
                }}
                className="bg-primary text-primary-foreground"
                data-testid="rightmove-options-confirm"
              >
                Confirm
              </Button>
            </div>
          )}

          {!rightmoveAction?.needsConfirm && rightmoveAction?.loading ? (
            <div className="flex items-center gap-3 py-6 text-sm text-muted-foreground" data-testid="rightmove-modal-loading">
              <Loader2 className="h-4 w-4 animate-spin" />
              {rightmoveAction.action === "remove"
                ? "Removing from Rightmove UAT…"
                : rightmoveAction.action === "add_premium"
                ? "Adding Premium Listing…"
                : rightmoveAction.action === "add_featured"
                ? "Adding Featured Property…"
                : "Pushing to Rightmove UAT…"}
            </div>
          ) : !rightmoveAction?.needsConfirm && rightmoveAction?.result && (() => {
            const r = rightmoveAction?.result || {};
            const rmJson = r.response_json || {};
            const rmSuccess = rmJson.success === true;
            const rmId = rmJson.property?.cms_id;
            const rmUrl = rmId ? `https://www.adftest.rightmove.com/properties/${rmId}` : null;
            const errors = rmJson.errors || [];
            const warnings = rmJson.warnings || [];
            const prefix = r.prefix || null;
            const prefixJson = prefix?.response_json || {};
            const prefixSuccess = prefixJson.success === true;

            return (
              <div className="space-y-3 py-2 text-sm" data-testid="rightmove-modal-result">
                {prefix && (
                  <div
                    className={`p-2 rounded border text-xs ${prefixSuccess ? "border-green-300 bg-green-50 text-green-900" : "border-red-300 bg-red-50 text-red-900"}`}
                    data-testid="rightmove-prefix-result"
                  >
                    <div className="font-medium">
                      Step 1 · SendProperty {prefixSuccess ? "✓" : "✗"} (HTTP {prefix.http_status})
                    </div>
                    {prefixJson.message && <div className="mt-1">{prefixJson.message}</div>}
                  </div>
                )}
                {prefix && <div className="text-xs font-medium text-muted-foreground">Step 2 · {rightmoveAction.action === "add_premium" ? "AddPremiumListing" : "AddFeaturedProperty"}</div>}
                <div className="flex items-center gap-2">
                  {rmSuccess ? (
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded">
                      <Check size={12} /> Success
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-800 text-xs font-medium rounded">
                      <X size={12} /> Failed
                    </span>
                  )}
                  {r.http_status && (
                    <span className="text-xs text-muted-foreground">HTTP {r.http_status}</span>
                  )}
                  {r.tls_version && (
                    <span className="text-xs text-muted-foreground">· {r.tls_version}</span>
                  )}
                </div>

                {rmJson.message && (
                  <div className="text-sm text-foreground">{rmJson.message}</div>
                )}
                {!rmJson.message && r.detail && (
                  <div className="text-sm text-red-800" data-testid="rightmove-modal-detail">{r.detail}</div>
                )}

                {rmSuccess && rmUrl && (
                  <div className="p-3 border border-blue-300 bg-blue-50 rounded space-y-2">
                    <div className="text-xs uppercase tracking-wider text-blue-800 font-medium">
                      Rightmove UAT preview
                    </div>
                    <a
                      href={rmUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-blue-800 hover:text-blue-900 underline font-mono text-sm break-all"
                      data-testid="rightmove-modal-preview-link"
                    >
                      <ExternalLink size={14} />
                      {rmUrl}
                    </a>
                    <div className="text-xs text-blue-900 pt-1 border-t border-blue-200 space-y-1.5">
                      <div className="p-2 bg-amber-100 border border-amber-300 rounded text-amber-900">
                        <span className="font-semibold">⏱ Please wait 15–40 minutes.</span>{" "}
                        Per Rightmove support: <em>&quot;After a property upload, some properties may
                        appear after 15–20 minutes whilst others may take longer such as 30–40
                        minutes, this is due to the processes the properties go through when
                        uploaded to our site.&quot;</em> Until then, the preview URL will show a
                        &quot;we couldn&apos;t find the property&quot; page — this is expected.
                      </div>
                      <div>
                        <span className="font-semibold">Then, when you visit the link:</span>{" "}
                        your browser will show a login prompt. Enter:
                      </div>
                      <div className="font-mono">
                        Username: <span className="font-semibold">rightmove</span>
                        <span className="mx-2 text-blue-400">·</span>
                        Password: <span className="font-semibold">rightpass</span>
                      </div>
                      <div className="text-blue-800 text-[11px] pt-1 italic">
                        Note: Rightmove refreshes their UAT every 1–2 months, wiping test data —
                        you may need to re-push properties after a refresh.
                      </div>
                    </div>
                  </div>
                )}

                {rmSuccess && warnings.length > 0 && (
                  <div
                    className="p-3 border border-amber-300 bg-amber-50 rounded text-xs text-amber-900"
                    data-testid="rightmove-warnings-note"
                  >
                    <div className="font-medium mb-1">
                      Property was uploaded successfully — the warnings below are non-blocking.
                    </div>
                    Common cause: Rightmove couldn&apos;t fetch one or more images. Fix by hosting
                    the images at a public URL ending in{" "}
                    <span className="font-mono">.jpg</span>,{" "}
                    <span className="font-mono">.jpeg</span>,{" "}
                    <span className="font-mono">.png</span> or{" "}
                    <span className="font-mono">.gif</span> — Rightmove refuses to re-ingest
                    URLs from its own CDN (<span className="font-mono">media.rightmove.co.uk</span>).
                  </div>
                )}

                {errors.length > 0 && (
                  <div className="space-y-1">
                    <div className="text-xs font-medium text-red-800">Errors ({errors.length})</div>
                    <ul className="text-xs space-y-1 max-h-40 overflow-auto">
                      {errors.map((e, i) => (
                        <li key={i} className="p-2 bg-red-50 border-l-2 border-red-400">
                          <span className="font-mono text-red-800">{e.error_code}</span>
                          {e.error_value && <span className="ml-2 text-red-600">[{String(e.error_value).slice(0, 60)}]</span>}
                          <div className="text-red-700 mt-0.5">{e.error_description}</div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {warnings.length > 0 && (
                  <div className="space-y-1">
                    <div className="text-xs font-medium text-amber-800">Warnings ({warnings.length})</div>
                    <ul className="text-xs space-y-1 max-h-32 overflow-auto">
                      {warnings.map((w, i) => (
                        <li key={i} className="p-2 bg-amber-50 border-l-2 border-amber-400 text-amber-800">
                          <span className="font-mono">{w.warning_code}</span>: {w.warning_description}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {r.detail && !rmJson.message && (
                  <div className="text-xs text-red-700 p-2 bg-red-50">{r.detail}</div>
                )}

                {r.endpoint && (
                  <div className="text-[10px] text-muted-foreground font-mono break-all pt-2 border-t">
                    {r.endpoint}
                  </div>
                )}
              </div>
            );
          })()}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRightmoveAction(null)}
              className="rounded-none"
              data-testid="rightmove-modal-close"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* --- Push error dialog (Zoopla / future Rightmove) --- */}
      <Dialog open={!!pushError} onOpenChange={(open) => { if (!open) setPushError(null); }}>
        <DialogContent
          className="max-w-2xl max-h-[85vh] overflow-y-auto"
          data-testid="push-error-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <X size={18} className="text-red-600" />
              {pushError?.title || "Push failed"}
            </DialogTitle>
            <DialogDescription>
              {pushError?.provider} · {pushError?.environment?.toUpperCase()}
              {pushError?.property?.title ? ` · ${pushError.property.title}` : ""}
              {pushError?.httpStatus ? ` · HTTP ${pushError.httpStatus}` : ""}
            </DialogDescription>
          </DialogHeader>

          {pushError && (
            <div className="space-y-3 text-sm">
              {/* Missing-fields callout — the actionable part */}
              {pushError.missing?.length > 0 && (
                <div className="border-l-4 border-amber-500 bg-amber-50 p-3 rounded space-y-2">
                  <div className="font-semibold text-amber-900">
                    Missing required field{pushError.missing.length > 1 ? "s" : ""}
                  </div>
                  <ul className="list-disc list-inside space-y-1 text-amber-900 text-sm">
                    {pushError.missing.map((f, i) => (
                      <li key={i} data-testid={`push-error-missing-${i}`}>{f}</li>
                    ))}
                  </ul>
                  <div className="text-xs text-amber-800 pt-1">
                    Fill these in on the property record and try again.
                  </div>
                </div>
              )}

              {/* Human-readable summary from the backend */}
              <div className="border-l-4 border-red-400 bg-red-50 p-3 rounded">
                <div className="text-xs font-semibold text-red-900 uppercase tracking-wide mb-1">
                  Error details
                </div>
                <div className="text-sm text-red-900 whitespace-pre-wrap break-words">
                  {pushError.summary}
                </div>
              </div>

              {/* Endpoint (helps devs correlate with server logs) */}
              {pushError.url && (
                <div className="text-[11px] text-muted-foreground break-all">
                  <span className="uppercase tracking-wide font-medium mr-1">Endpoint:</span>
                  <code>{pushError.url}</code>
                </div>
              )}

              {/* Collapsible request/response for advanced debugging */}
              {pushError.request_body && (
                <details className="text-xs">
                  <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                    Request body sent to {pushError.provider}
                  </summary>
                  <pre className="text-[10px] mt-2 max-h-64 overflow-auto bg-muted p-2 rounded">
                    {JSON.stringify(pushError.request_body, null, 2)}
                  </pre>
                </details>
              )}
              {pushError.response_text && (
                <details className="text-xs" open={!pushError.missing?.length}>
                  <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                    Raw response from {pushError.provider}
                  </summary>
                  <pre className="text-[10px] mt-2 max-h-64 overflow-auto bg-muted p-2 rounded whitespace-pre-wrap">
                    {pushError.response_text}
                  </pre>
                </details>
              )}
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => {
                const details = [
                  `Provider: ${pushError?.provider}`,
                  `Environment: ${pushError?.environment}`,
                  `Property: ${pushError?.property?.title || pushError?.property?.property_id || ""}`,
                  `HTTP: ${pushError?.httpStatus || "n/a"}`,
                  `Summary: ${pushError?.summary || ""}`,
                  pushError?.missing?.length ? `Missing: ${pushError.missing.join("; ")}` : "",
                  pushError?.url ? `Endpoint: ${pushError.url}` : "",
                  pushError?.response_text ? `Response: ${pushError.response_text}` : "",
                ].filter(Boolean).join("\n");
                navigator.clipboard?.writeText(details);
                toast.success("Error details copied to clipboard");
              }}
              data-testid="push-error-copy"
            >
              Copy details
            </Button>
            {pushError?.property && (
              <Button
                onClick={() => {
                  openEditDialog(pushError.property);
                  setPushError(null);
                }}
                data-testid="push-error-edit-property"
              >
                Edit property
              </Button>
            )}
            <Button
              variant="secondary"
              onClick={() => setPushError(null)}
              data-testid="push-error-close"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
